/**
 * seoLandingPagesTierI.js — Iter 195 Tier-I JA-primary expansion (10 net-new).
 * Cluster 4 — Regional toll road guides (route-level intent).
 *
 * Editorial guardrails (never bend):
 *  - JTRはETC利用照会サービス・NEXCO・各高速道路会社の公式ではない
 *  - 料金・距離・割引率・適用条件は 道路・車種・IC・距離・時間帯・登録状況・将来の制度変更で変動 と添える
 *  - 「PDF + CSV」を統一表現、Excel/.xlsxは使わない
 *  - JTRは「確認を支援」「整理に役立つ」「地域別の通行記録整理を補助」
 *  - 絶対表現禁止: 完全, 一切, 必ず, 確実に割引される, 自動的に全て判定, 不正検出
 *
 * Note: All entries are direct object literals with inline ja:{} and en:{}
 * blocks so the JA grammar-review export script picks up every Japanese
 * string by field path.
 */

const REF_ETC_MEISAI = { label: 'ETC利用照会サービス（公式）', url: 'https://www.etc-meisai.jp/', note: '通行明細・利用証明書PDF・利用明細CSVの公式出力先' };
const REF_GOETC = { label: 'go-etc.jp — ETC総合情報', url: 'https://www.go-etc.jp/', note: 'ETCカード・割引制度・利用案内の公式情報' };
const REF_ETC_MILEAGE = { label: 'ETCマイレージサービス（公式）', url: 'https://www.smile-etc.jp/', note: '平日朝夕割引・ポイント還元の登録窓口' };
const REF_NEXCO_E = { label: 'NEXCO東日本（公式）', url: 'https://www.e-nexco.co.jp/', note: '東日本管内の高速道路情報' };
const REF_NEXCO_C = { label: 'NEXCO中日本（公式）', url: 'https://www.c-nexco.co.jp/', note: '中日本管内の高速道路情報' };
const REF_NEXCO_W = { label: 'NEXCO西日本（公式）', url: 'https://www.w-nexco.co.jp/', note: '西日本管内の高速道路情報' };
const REF_HANSHIN = { label: '阪神高速道路（公式）', url: 'https://www.hanshin-exp.co.jp/', note: '阪神高速の通行料金・割引体系' };
const REF_JBHC = { label: 'JB本四高速（公式）', url: 'https://www.jb-honshi.co.jp/', note: '本州四国連絡橋（瀬戸大橋・しまなみ海道・神戸淡路鳴門）公式' };

const RELATED_FEATURES_CORE = [
  { title: '無料週次レポート', desc: '週1回のメール配信。無期限・無料・カード登録不要。', path: '/free-weekly' },
  { title: 'プレミアム毎日レポート', desc: '前日のETC通行明細をPDF + CSVで毎朝自動配信。', path: '/personal/features' },
  { title: 'ビジネススイート', desc: '法人向けの車両別・部署別配信・経費按分支援。', path: '/business/features' },
  { title: 'PDF + CSV エクスポート', desc: '会計ソフト・スプレッドシートとの互換性。Excel不要。', path: '/guides/etc-pdf-csv-download-guide' },
];

// Each regional guide uses the same JTR-solution narrative inline so the JA
// export workbook captures all strings by field path.
function _jtrSolutionBlock() {
  return {
    intro: 'JTRはETC利用照会サービスから取得した利用明細を毎朝メール配信します。地域別の通行記録整理を補助し、複数路線・複数地域をまたぐ通行も1通のメールで一元管理する整理に役立ちます。',
    bullets: [
      '対象路線含む全国のETC通行明細を1通のメールに集約',
      'PDF（書類）+ CSV（データ）を毎朝同梱配信',
      '車両ニックネーム別の経費按分（法人プラン）',
      '深夜・休日・平日朝夕等の適用済み割引をCSVで確認',
      'バイリンガル（日本語・英語）対応',
      '取得後すぐに配信する方式、生データの長期保存なし',
    ],
  };
}

// Generic regional guide factory — returns a fully-populated entry whose
// ja:{} block is built from explicit Japanese strings (literal in this file)
// so the JA export captures every grammar-review row.
function regionalEntry(cfg) {
  const { slug, ja, enFallback, references, primaryKeyword, enPrimaryKeyword, keywords } = cfg;
  return {
    slug,
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: { ja: primaryKeyword, en: enPrimaryKeyword },
    keywords,
    ja: {
      title: ja.title,
      metaDesc: ja.metaDesc,
      h1: ja.h1,
      answer: ja.answer,
      whyMatters: ja.whyMatters,
      officialBackground: ja.officialBackground,
      commonProblems: ja.commonProblems,
      jtrSolution: _jtrSolutionBlock(),
      whoFor: ja.whoFor,
      steps: ja.steps,
      pdfCsv: '当該路線の通行記録もETC利用照会サービスのPDF + CSVで取得可能。JTRが両方をメール同梱で配信します。',
      email: '毎朝のメール配信で当該路線の通行料を継続的に記録、月次・年次の経費整理に役立ちます。',
      examples: ja.examples,
      relatedFeatures: RELATED_FEATURES_CORE,
      faqs: ja.faqs,
      references,
    },
    en: enFallback,
  };
}

// Build full JA payloads for each regional guide. JA strings are all
// literal so they appear in the JA review workbook.
const _REGIONS = [
  {
    slug: 'hanshin-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '阪神高速 ETC 明細',
      en: 'Hanshin Expressway ETC statement',
    },
    ja: {
      title: '阪神高速ETC明細の確認方法｜利用照会・すぐログ活用 | JTR',
      metaDesc: '阪神高速のETC利用明細・履歴を確認する方法を解説。ETC利用照会サービス（過去15か月）と阪神高速「すぐログ」の使い分け、PDF・CSV保存、首都高速・NEXCOとの違いまで実務ベースで紹介。',
      h1: '阪神高速ETC明細ガイド',
      primaryKeyword: '阪神高速 ETC 明細',
      answer: '阪神高速ETC明細ガイドは、都市高速の距離別料金とETC・現金の異なる扱いを理解し、正確な利用記録を確認・保管するための案内です。短距離移動や頻繁な業務利用が多い都市路線では、入口と出口の組み合わせを記録と照合することが重要です。公式のETC利用照会サービスやカード明細を活用し、カード・車両・経路・利用目的・公式証拠の保管先を記録することで、後日の説明や精算がスムーズになります。',
      whyMatters: '都市高速は短距離ランプ、距離別課金、現金扱いが長距離高速道路と異なり、記録管理に注意が必要です。業務・政府・レンタカー・家族利用では、公式の利用明細、PDF出力、CSV出力、カード明細、社内メモを一緒に保管することで、精算確認・運転者への質問・部門配分・不正利用の疑いへの対応が円滑になります。記憶や画面キャプチャに頼らず、公式ETC利用記録と業務上の説明を紐付けて保管することが最も安全です。',
      officialBackground: '阪神高速は都市高速として距離別料金体系を採用し、車種区分やETC・現金での扱いが異なります。料金・割引・車種区分・ETC専用レーンなどの公式情報は阪神高速の公式サイトで確認できます。ETC利用照会サービスでは、ETCカードの利用明細や利用証明書をPDFまたはCSV形式でダウンロードでき、通常のETCカードは過去15か月分の記録が閲覧可能です。カード発行会社も月次明細を提供する場合があります。JTRはこれら公式記録の受信・整理・保管・確認を支援する独立サービスであり、公式の通行料金データを作成する主体ではありません。',
      whoFor: [
        '阪神高速を利用する個人ドライバー',
        'ETC明細を管理する企業の経理・総務担当者',
        '配送や営業で頻繁に都市高速を使う事業者',
        'レンタカーや家族車両のETC記録を整理したい利用者',
      ],
      jtrSolution: {
        intro: 'JTRは公式ETC利用記録を実務的な確認フローに変換する独立サービスです。手動検索・印刷・ファイル名変更・転送の手間を減らし、記録の配信・整理・保管・確認支援に注力します。',
        bullets: [
          'PDF・CSV記録を保管し、設定に応じてETCカードや車両ごとに整理',
          '確認が必要な記録を強調表示し、早期レビューを支援',
          '複数車両・複数カードの利用を社内方針と照合しやすく',
          '不審な記録を早期に発見し、公式照会や運転者確認を促進',
          '税務・精算・監査では記録整理を支援。最終判断は会計士・雇用主・公式指針に確認',
        ],
      },
      pdfCsv: 'JTRはETC利用記録をPDFとCSV形式で提供します。PDFは確認・共有に便利で、CSVは並べ替え・絞り込み・会計システムへの取り込みに適しています。両方を保管することで、後日の確認や精算業務が効率化されます。',
      email: 'JTRは設定に応じてETC利用記録を定期的にメール配信します。記録が届いたら速やかに確認し、不明な利用や金額の相違があれば公式の道路運営者またはカード発行会社に問い合わせてください。記録と業務説明を紐付けて保管することで、後日の説明負担が軽減されます。',
      steps: [
        {
          t: '阪神高速ETC明細の対象範囲を確認',
          d: '阪神高速は都市型の対距離制料金体系を採用しており、短距離利用や複数ランプ利用が多いため、ETC記録で正確な入口・出口ICを確認することが重要です。',
        },
        {
          t: '公式の料金事業者情報を確認',
          d: '経路、料金額、車種区分、割引適用条件、ETC専用レーンなど道路固有のルールについては、まず阪神高速公式サイトで確認します。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細を確認',
          d: '走行後のETC利用明細、利用証明書、PDF記録、CSV記録の取得には、ETC利用照会サービスまたは各カード発行会社の明細を確認します。',
        },
        {
          t: '走行日・IC・カード・車両・目的を一貫して記録',
          d: '利用日、入口IC、出口IC、ETCカード、車両、車種区分、ドライバーまたは部門、利用目的を社内統一フォーマットで記録します。',
        },
        {
          t: '公式データと社内メモを分離管理',
          d: '公式の通行記録は利用事実を示し、社内メモは精算目的や承認情報を説明するため、両者を明確に区別して保管します。',
        },
        {
          t: '不明・遅延・不一致時は公式窓口へ問合せ',
          d: '記録が不詳細、遅延、予想外、または不一致の場合は推測せず、該当する公式事業者またはカード発行会社に問合せ、回答を記録に添付します。',
        },
      ],
      commonProblems: [
        {
          q: '短距離利用の記録が多く管理が煩雑',
          a: '阪神高速は都市型で短距離利用が多いため、PDF+CSV出力を活用し、日付・カード・車両ごとに整理すると確認作業が効率化されます。',
        },
        {
          q: '複数車両・複数カードの利用履歴が混在',
          a: 'ETC利用照会サービスでカード別に記録を分け、社内でも車両・部門別に整理することで、精算時の突き合わせがスムーズになります。',
        },
        {
          q: '料金額が予想と異なる',
          a: '阪神高速の対距離制、車種区分、割引適用の有無を公式サイトで確認し、それでも不明な場合は公式窓口に問合せて記録を保管します。',
        },
        {
          q: '記憶だけで精算申請して後で不備が発覚',
          a: '記憶やスクリーンショットではなく、公式ETC利用記録と社内メモを一緒に保管することで、後日の質問や監査にも対応できます。',
        },
      ],
      examples: [
        {
          who: 'ドライバー',
          use: '阪神高速ETC明細ガイドを参照し、精算申請前にどの公式情報源を確認すべきか把握する。',
        },
        {
          who: '経理担当者',
          use: '月次でPDF+CSV記録を出力し、社内の車両・部門割当と照合して精算処理を行う。',
        },
        {
          who: '家族利用者',
          use: '複数車両のETC記録を確認し、個人利用と業務利用を分けて会計士へ情報提供する。',
        },
        {
          who: '車両管理者',
          use: '見覚えのない通行記録を発見し、公式ETC利用記録を確認後、ドライバーへ経緯を確認する。',
        },
      ],
      faqs: [
        {
          q: 'JTRは阪神高速の公式情報源ですか？',
          a: 'いいえ。JTRは独立したサービスです。公式の経路・料金・割引・設定・安全ルールは該当する料金事業者または公式ETCサービスで確認してください。JTRは利用記録の受信・整理・保管・確認支援を行います。',
        },
        {
          q: 'ETC利用照会サービスで料金事業者サイトを代替できますか？',
          a: 'いいえ。ETC利用照会サービスはETCカード利用明細と利用証明書に有用ですが、最新の経路・料金・割引・レーン・車種ルールは料金事業者サイトで確認が必要です。',
        },
        {
          q: 'PDFとCSVのどちらを保存すべきですか？',
          a: '両方が有用です。PDF記録は確認・共有が容易で、CSV記録は並替え・絞込み・経理システムへの取込みに適しています。',
        },
        {
          q: 'これらの記録を税務や精算に使えますか？',
          a: '税務・精算確認の補助資料として活用できますが、最終的な取扱いは雇用主の方針、会計士の助言、公式ガイダンスに従ってください。本ガイドは税務助言ではありません。',
        },
        {
          q: '料金や経路が誤っているように見える場合は？',
          a: '公式記録を確認し、カード・車両・利用日・IC情報を照合した上で、問題が解決しない場合は該当する料金事業者またはカード発行会社へ問合せてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'ビジネス向けETC記録管理',
          desc: '複数車両・複数カードのETC利用記録を部門別・プロジェクト別に整理し、精算・監査支援を効率化',
          path: '/business',
        },
        {
          title: 'PDF+CSV出力機能',
          desc: 'ETC利用記録をPDFとCSV形式で出力し、確認・共有・経理システム取込みをスムーズに',
          path: '/features',
        },
        {
          title: '無料トライアル',
          desc: 'JTRのETC記録整理・保管・確認支援機能を無料で試して、業務効率化を体験',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用照会、利用証明書発行、PDF/CSV出力に対応。通常のETCカード記録は過去15か月分が確認可能',
        },
        {
          label: 'ETC総合情報ポータル：ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別、利用証明書発行、通常ETCカード記録期間、法人ETCカード記録期間、無線・非無線ETC利用、PDF/CSVダウンロードについて解説',
        },
        {
          label: '阪神高速道路株式会社：料金案内',
          url: 'https://www.hanshin-exp.co.jp/drivers/ryoukin/',
          note: '阪神高速の対距離制料金体系、車種区分ルールなど公式料金情報',
        },
        {
          label: 'ETC総合情報ポータル：お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する公式料金事業者カスタマーセンター一覧',
        },
        {
          label: 'Japan Toll Receipts プライバシーポリシー',
          url: 'https://japantollreceipts.jp/privacy',
          note: 'JTRにおけるETC記録の取扱い、独立性、公式データ作成の有無について確認可能',
        },
      ],
      longBody: '阪神高速道路のETC明細を正確に管理するためには、都市高速特有の距離別料金制度と短距離走行の特性を理解しておく必要があります。阪神高速は市街地を結ぶ路線網のため、入口と出口の組み合わせが多く、料金所間の距離が短い区間も少なくありません。このため、目的地だけから料金を推測するのではなく、実際に利用した入口インターチェンジと出口インターチェンジの組み合わせをETC利用記録で確認することが重要です。ETC利用照会サービスでは過去15か月分の通行履歴を照会でき、利用証明書の発行やPDF・CSV形式での記録ダウンロードが可能です。ただし、ETC利用照会サービスはカード単位の利用明細を提供するものであり、阪神高速道路の最新料金体系や車種区分、割引制度、ETC専用レーンの配置といった運用ルールについては、阪神高速道路公式サイトで確認する必要があります。業務利用や複数車両を管理する場合、どのETCカードがどの車両に割り当てられ、どの部署または運転者が利用したのかを記録しておかなければ、後日の経費精算や利用状況の確認が困難になります。特に都市高速では同じカードで複数回短距離通行するケースも多く、利用日時と入出場ICの組み合わせだけでは判別しにくい場合があります。そのため、公式のETC利用明細に加えて、社内管理用のメモや走行目的、車両番号、運転者名を併記した記録を保持しておくことが推奨されます。JTRは独立したサービスであり、NEXCO、ETC利用照会サービス、カード発行会社、政府機関のいずれでもありません。JTRは公式の通行料金データを作成するのではなく、ユーザーが受け取ったETC利用記録を整理し、アーカイブし、確認作業を支援する役割を担います。たとえば、PDF形式とCSV形式の両方で記録を保管することで、目視確認と表計算ソフトへの取り込みの両方に対応できます。予期しない通行記録が見つかった場合、JTRはその記録を目立たせることで早期の確認を促しますが、不正利用の断定や法的判断を行うものではありません。最終的な税務処理や経費承認の可否については、雇用者の方針、会計士の助言、または公式ガイダンスに従って判断してください。阪神高速を含む都市高速の明細管理では、記憶や部分的なスクリーンショットに頼るのではなく、公式記録と業務上の文脈を一体として保管することが、後日の問い合わせや確認対応を円滑にする鍵となります。',
    },
    en: {
      title: 'Hanshin Expressway ETC Statement Guide | Japan Toll Receipts',
      metaDesc: "How to check, organize, and store your Hanshin Expressway ETC usage records. Official inquiry sources, PDF & CSV records, JTR's role, FAQs, and key tips.",
      h1: 'Hanshin Expressway ETC Statement Guide',
      primaryKeyword: 'Hanshin Expressway ETC statement',
      answer: 'The Hanshin Expressway ETC Statement Guide helps you understand distance-based urban expressway pricing and the different treatment of ETC versus cash, so you can verify and store accurate usage records. For urban routes with frequent short trips and business use, matching entry-exit ramp pairs against your records is essential. Use the official ETC inquiry service and card statements, then record card, vehicle, route, purpose, and where official evidence is stored—this makes later explanations and settlement much smoother.',
      whyMatters: 'Urban expressways differ from long-distance highways: short ramps, distance-based charges, and cash-equivalent treatment require careful record management. For business, government, rental-car, and family use, storing official statements alongside PDF exports, CSV exports, card statements, and internal notes enables smooth settlement checks, driver follow-up, department allocation, and responses to suspected misuse. Relying on memory or screenshots is risky; linking official ETC records to business explanations and storing both together is the safest approach.',
      officialBackground: 'Hanshin Expressway operates a distance-based fare system as an urban expressway, with vehicle classes and different treatment for ETC versus cash. Official information on fares, discounts, vehicle classes, and ETC-only lanes is available on the Hanshin Expressway official website. The ETC Toll Inquiry Service lets you download ETC card usage statements and proof-of-use documents in PDF or CSV format; standard ETC cards offer up to 15 months of past records. Card issuers may also provide monthly statements. JTR is an independent service that helps you receive, organize, store, and review these official records—JTR does not create official toll data.',
      whoFor: [
        'Individual drivers using Hanshin Expressway',
        'Corporate accounting and administration staff managing ETC statements',
        'Businesses with frequent urban expressway use for delivery or sales',
        'Users organizing ETC records for rental cars or family vehicles',
      ],
      jtrSolution: {
        intro: 'JTR is an independent service that turns official ETC usage records into a practical review workflow. It reduces manual search, printing, file-renaming, and forwarding tasks, focusing instead on delivery, organization, storage, and review support.',
        bullets: [
          'Store PDF and CSV records, organized by ETC card or vehicle as configured',
          'Highlight records needing review to support early checking',
          'Make multi-vehicle, multi-card usage easier to cross-check against internal policies',
          'Help spot unusual records early, prompting official inquiry or driver confirmation',
          'Support record organization for tax, settlement, and audit—final decisions rest with accountants, employers, and official guidance',
        ],
      },
      pdfCsv: 'JTR provides ETC usage records in both PDF and CSV formats. PDFs are convenient for review and sharing; CSVs suit sorting, filtering, and import into accounting systems. Storing both formats streamlines later verification and settlement work.',
      email: 'JTR delivers ETC usage records by email on the schedule you configure. Review records promptly when they arrive; if you find unfamiliar trips or amount discrepancies, contact the official road operator or card issuer. Linking records to business explanations and storing them together reduces the burden of later inquiries.',
      steps: [
        {
          t: 'Confirm scope of Hanshin Expressway ETC details',
          d: 'Hanshin Expressway uses urban distance-based tolling with frequent short trips and multiple ramp usage, making it important to verify exact entry/exit ICs in ETC records.',
        },
        {
          t: 'Check official operator information',
          d: 'For routes, toll amounts, vehicle classifications, discount conditions, ETC-only lanes, and road-specific rules, first consult the Hanshin Expressway official site.',
        },
        {
          t: 'Check ETC Inquiry Service or card statements',
          d: 'To obtain post-travel ETC usage details, usage certificates, PDF records, and CSV records, check the ETC Inquiry Service or statements from each card issuer.',
        },
        {
          t: 'Record travel date, ICs, card, vehicle, and purpose consistently',
          d: 'Record usage date, entry IC, exit IC, ETC card, vehicle, vehicle class, driver or department, and purpose using a standardized internal format.',
        },
        {
          t: 'Separate official data from internal notes',
          d: 'Official travel records show usage facts, while internal notes explain reimbursement purposes and approval information; keep both clearly distinct in storage.',
        },
        {
          t: 'Contact official channels for missing, delayed, or mismatched records',
          d: 'When records are incomplete, delayed, unexpected, or inconsistent, do not guess—contact the relevant official operator or card issuer and attach responses to records.',
        },
      ],
      commonProblems: [
        {
          q: 'Many short-distance records make management cumbersome',
          a: 'Hanshin Expressway is urban with frequent short trips; using PDF + CSV exports organized by date, card, and vehicle streamlines verification work.',
        },
        {
          q: 'Usage history from multiple vehicles and cards is mixed',
          a: 'Separate records by card in the ETC Inquiry Service, then organize internally by vehicle and department to simplify reimbursement reconciliation.',
        },
        {
          q: 'Toll amount differs from expectation',
          a: 'Check Hanshin Expressway distance-based tolling, vehicle class, and discount application on the official site; if still unclear, contact the official help desk and retain records.',
        },
        {
          q: 'Submitted reimbursement from memory, then issues emerged later',
          a: 'Store official ETC usage records together with internal notes rather than relying on memory or screenshots, enabling response to later questions or audits.',
        },
      ],
      examples: [
        {
          who: 'Driver',
          use: 'Refer to the Hanshin Expressway ETC details guide to understand which official sources to check before submitting reimbursement requests.',
        },
        {
          who: 'Accounting staff',
          use: 'Export monthly PDF + CSV records, reconcile against internal vehicle and department assignments, and process reimbursement.',
        },
        {
          who: 'Personal user',
          use: 'Review ETC records for multiple vehicles, separate personal from business usage, and provide information to accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'Discover unfamiliar travel records, verify official ETC usage records, then confirm circumstances with driver.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official Hanshin Expressway information source?',
          a: 'No. JTR is an independent service. For official routes, tolls, discounts, settings, and safety rules, consult the relevant toll operator or official ETC service. JTR assists with receiving, organizing, storing, and verifying usage records.',
        },
        {
          q: 'Can the ETC Inquiry Service replace the operator site?',
          a: 'No. The ETC Inquiry Service is useful for ETC card usage details and certificates, but current routes, tolls, discounts, lanes, and vehicle class rules must be verified on the toll operator site.',
        },
        {
          q: 'Should I save PDF or CSV?',
          a: 'Both are useful. PDF records are easy to review and share, while CSV records suit sorting, filtering, and import into accounting systems.',
        },
        {
          q: 'Can I use these records for tax or reimbursement?',
          a: 'They can serve as supporting materials for tax and reimbursement verification, but final handling should follow employer policy, accountant advice, and official guidance. This guide is not tax advice.',
        },
        {
          q: 'What if tolls or routes appear incorrect?',
          a: 'Verify official records, reconcile card, vehicle, usage date, and IC information, then if the issue persists, contact the relevant toll operator or card issuer.',
        },
      ],
      relatedFeatures: [
        {
          title: 'Business ETC record management',
          desc: 'Organize multi-vehicle and multi-card ETC usage records by department and project to streamline reimbursement and usage review support',
          path: '/business',
        },
        {
          title: 'PDF + CSV export',
          desc: 'Export ETC usage records in PDF and CSV formats for smooth review, sharing, and accounting system import',
          path: '/features',
        },
        {
          title: 'Free trial',
          desc: "Try JTR's ETC record organization, storage, and verification support features at no cost to experience workflow efficiency",
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Supports ETC card usage inquiry, usage certificate issuance, PDF/CSV export. Standard ETC card records available for past 15 months',
        },
        {
          label: 'ETC General Information Portal: ETC Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, usage certificate issuance, standard ETC card record period, corporate ETC card record period, wireless/non-wireless ETC usage, PDF/CSV download',
        },
        {
          label: 'Hanshin Expressway Company: Toll Information',
          url: 'https://www.hanshin-exp.co.jp/drivers/ryoukin/',
          note: 'Official toll information for Hanshin Expressway distance-based tolling, vehicle classification rules',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'Directory of official toll operator customer centers for toll, discount, and ETC usage inquiries',
        },
        {
          label: 'Japan Toll Receipts Privacy Policy',
          url: 'https://japantollreceipts.jp/privacy',
          note: 'Verify JTR handling of ETC records, independence, and absence of official data creation',
        },
      ],
      longBody: "Managing ETC transaction records for the Hanshin Expressway accurately requires understanding the distance-based toll structure and short-haul characteristics unique to urban expressways. Because the Hanshin network connects city areas, there are numerous entrance and exit combinations, many involving short segments between toll points. For this reason, it is important not to estimate charges based solely on the destination, but rather to verify the actual combination of entrance and exit interchanges used by consulting your ETC usage records. The ETC Toll Inquiry Service allows you to query up to fifteen months of travel history and supports issuance of usage certificates as well as record downloads in PDF and CSV formats. However, the ETC Toll Inquiry Service provides statement data on a per-card basis; the latest toll schedules, vehicle classifications, discount programs, and ETC-only lane locations for the Hanshin Expressway must be confirmed on the official Hanshin Expressway website. When managing business travel or multiple vehicles, failing to record which ETC card is assigned to which vehicle and which department or driver made each trip will make later expense reconciliation and usage verification difficult. Urban expressways in particular often see the same card used for multiple short trips, and date-time stamps combined with interchange pairs alone may not be enough to distinguish them. For this reason, it is recommended that you maintain supplemental notes alongside the official ETC statement—trip purpose, vehicle number, and driver name—for internal tracking. JTR is an independent service and is not affiliated with NEXCO, the ETC Toll Inquiry Service, card issuers, or any government agency. JTR does not generate official toll data; instead, it organizes, archives, and assists users in reviewing the ETC usage records they have received. For example, retaining records in both PDF and CSV formats supports visual review as well as import into spreadsheet software. When an unexpected trip record appears, JTR highlights it to prompt early review, but does not determine fraud or provide legal conclusions. Final decisions on tax treatment and expense approval should follow your employer's policies, your accountant's advice, or official guidance. For urban expressway statements including the Hanshin network, the key to smooth follow-up inquiries and audit response is to store official records together with business context, rather than relying on memory or partial screenshots.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '阪神高速ETC利用記録の流れ',
            intro: '都市高速の通行から記録保管までの4段階',
            nodes: [
              {
                title: 'ETC通行',
                desc: '入口・出口ランプを通過、距離別料金で自動課金',
              },
              {
                title: '公式記録生成',
                desc: 'ETC利用照会サービスに15か月分のデータが蓄積',
              },
              {
                title: 'PDF・CSV取得',
                desc: '公式サイトから利用明細・証明書をダウンロード',
              },
              {
                title: 'JTR保管・整理',
                desc: 'カード・車両別に分類、確認必要箇所を強調表示',
              },
            ],
          },
          en: {
            title: 'Hanshin Expressway ETC Record Flow',
            intro: 'Four stages from urban toll use to record storage',
            nodes: [
              {
                title: 'ETC passage',
                desc: 'Pass entry/exit ramps, distance-based auto charge',
              },
              {
                title: 'Official record',
                desc: '15-month data stored in ETC inquiry service',
              },
              {
                title: 'PDF/CSV download',
                desc: 'Retrieve statements & certificates from official site',
              },
              {
                title: 'JTR organize',
                desc: 'Sort by card/vehicle, highlight review items',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式照会先マップ',
            intro: '阪神高速ETC記録の取得・確認に必要な情報源',
            sources: [
              {
                name: '阪神高速公式サイト',
                role: '料金・割引・車種区分の公式案内',
                url: 'hanshin-exp.co.jp',
                scope: '距離別料金体系、ETC専用レーン情報',
              },
              {
                name: 'ETC利用照会サービス',
                role: '過去15か月分の利用明細・証明書',
                url: 'etc-meisai.jp',
                scope: 'PDF・CSV形式でダウンロード可能',
              },
              {
                name: 'カード発行会社',
                role: '月次明細と請求額の確認',
                url: '各社サイト',
                scope: 'カード利用全体の記録・支払履歴',
              },
              {
                name: 'JTR',
                role: '記録の受信・整理・保管支援',
                url: 'japantollreceipts.jp',
                scope: '独立サービス、公式データ作成主体ではない',
              },
            ],
          },
          en: {
            title: 'Official Source Map',
            intro: 'Key information sources for Hanshin ETC records',
            sources: [
              {
                name: 'Hanshin Expressway',
                role: 'Official toll, discount & vehicle class info',
                url: 'hanshin-exp.co.jp',
                scope: 'Distance-based rates, ETC-only lane data',
              },
              {
                name: 'ETC Inquiry Service',
                role: '15-month usage statements & certificates',
                url: 'etc-meisai.jp',
                scope: 'Download in PDF/CSV format',
              },
              {
                name: 'Card issuer',
                role: 'Monthly statements & billing confirmation',
                url: 'Issuer sites',
                scope: 'Full card usage & payment history',
              },
              {
                name: 'JTR',
                role: 'Record delivery, organization & storage aid',
                url: 'japantollreceipts.jp',
                scope: 'Independent service, not official data creator',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF vs CSV 記録形式の比較',
            intro: '阪神高速ETC明細の2つの保管形式の特徴',
            columns: [
              'PDF形式',
              'CSV形式',
            ],
            rows: [
              {
                feature: '確認・共有の容易さ',
                a: 'そのまま閲覧・印刷可能',
                b: '表計算ソフトで開く必要',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '並べ替え・絞り込み',
                a: '検索のみ',
                b: '自由に条件設定可能',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '会計システム連携',
                a: '手入力が必要',
                b: '取り込み・自動化が容易',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '公式証明書として',
                a: '利用証明書PDF推奨',
                b: '補助記録として活用',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '保管推奨',
                a: '両方とも保管',
                b: '両方とも保管',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'PDF vs CSV Record Formats',
            intro: 'Comparison of two storage formats for Hanshin ETC records',
            columns: [
              'PDF format',
              'CSV format',
            ],
            rows: [
              {
                feature: 'Easy view/share',
                a: 'Direct view & print',
                b: 'Requires spreadsheet app',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Sort & filter',
                a: 'Search only',
                b: 'Full conditional filtering',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Accounting integration',
                a: 'Manual entry needed',
                b: 'Import & automation easy',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Official proof',
                a: 'Certificate PDF recommended',
                b: 'Supplementary record',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Storage recommendation',
                a: 'Keep both',
                b: 'Keep both',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '阪神高速ETC記録確認チェックリスト',
            intro: '配信された明細を確認する際の必須項目',
            items: [
              {
                text: '入口・出口の組み合わせは正しいか',
                hint: '短距離ランプが多い都市高速では経路確認が重要',
              },
              {
                text: '利用日時と業務予定は一致するか',
                hint: '運転者・車両・目的を紐付けて記録',
              },
              {
                text: '料金は距離別体系と合っているか',
                hint: '車種区分・ETC割引の適用を確認',
              },
              {
                text: 'ETCカードと車両の組み合わせは妥当か',
                hint: '社用・個人・レンタカーでの利用を区別',
              },
              {
                text: '不明な記録や重複がないか',
                hint: '疑問があれば公式照会サービスで再確認',
              },
              {
                text: 'PDF・CSV両方を保管したか',
                hint: '後日の精算・監査に備えて両形式を保管',
              },
            ],
          },
          en: {
            title: 'Hanshin ETC Record Review Checklist',
            intro: 'Essential items when reviewing delivered statements',
            items: [
              {
                text: 'Entry/exit pair correct?',
                hint: 'Route verification crucial for urban short ramps',
              },
              {
                text: 'Date/time match business schedule?',
                hint: 'Link driver, vehicle & purpose to record',
              },
              {
                text: 'Toll fits distance-based rate?',
                hint: 'Check vehicle class & ETC discount applied',
              },
              {
                text: 'ETC card & vehicle pairing valid?',
                hint: 'Distinguish business, personal, rental use',
              },
              {
                text: 'Any unknown or duplicate records?',
                hint: 'Re-check via official inquiry if uncertain',
              },
              {
                text: 'Both PDF & CSV saved?',
                hint: 'Store both formats for future reconciliation & audit',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'honshu-shikoku-bridge-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '本四高速ETC明細',
      en: 'Honshu–Shikoku Bridge ETC records',
    },
    ja: {
      title: '本四高速ETC明細ガイド | Japan Toll Receipts',
      metaDesc: '本州四国連絡高速道路（神戸淡路鳴門・瀬戸中央・西瀬戸の3ルート）のETC通行記録の確認・整理・保管方法を解説。本四高速の公式情報、ETC利用照会サービスでのPDF利用証明書・CSV明細取得、長距離通行料金の経費精算、独立サービスJTRの位置づけ、注意点とよくある質問まで網羅した実務ガイド。',
      h1: '本四高速ETC明細ガイド',
      primaryKeyword: '本四高速ETC明細',
      answer: '本四高速ETC明細ガイドは、明石海峡大橋・瀬戸中央道・しまなみ海道などの本州四国連絡高速道路を利用したドライバーや企業が、ETC利用記録を正しく確認・整理・保管するための解説です。JB本四高速とNEXCO路線が接続する区間では、複数事業者の料金・割引が組み合わさるため、通行記録を慎重に確認し、公式データと社内説明を一緒に保管することが重要です。',
      whyMatters: '本州四国連絡高速道路は専門の道路事業者JB本四高速が管理し、橋梁部分の前後でNEXCO路線と接続します。一つの走行で複数事業者の料金・割引が適用されるため、記録の確認を怠ると経費精算や監査時に説明が困難になります。ETC利用照会サービスやカード明細は公式利用記録を提供しますが、車両・ドライバー・目的などの社内情報は別途記録が必要です。公式データと内部説明を一体管理することで、後日の確認作業が安全かつ効率的になります。',
      officialBackground: '本州四国連絡高速道路のETC記録管理には複数の公式システムが関わります。道路事業者は経路・料金・割引・車種区分・安全規則を公開し、ETC利用照会サービスはETCカード利用明細や利用証明書をPDF・CSV形式で提供します。カード発行会社は月次明細を発行する場合があります。JTRは独立したサービスであり、NEXCO・MEISAI・ETC利用照会サービス・カード会社・政府機関ではありません。JTRは公式通行データを作成せず、利用者が受け取った記録の整理・保管・確認支援を行います。実務上有効な記録には、どのカードが課金され、どの車両が走行し、どの区間を利用し、なぜ通行したか、公式証拠がどこにあるかの5点が必要です。公式データと社内説明を分けて保管すれば、後日の監査や質問対応が容易になります。',
      whoFor: [
        '明石海峡大橋・瀬戸中央道・しまなみ海道などを業務で利用する企業ドライバー',
        '複数車両・複数ETCカードを管理し経費精算や部門別配賦を行う経理担当者',
        'レンタカーや個人車両で本四高速を通行し利用記録を保管したい個人利用者',
        'ETC通行記録の確認・保管方法を社内ルール化したい管理部門',
      ],
      jtrSolution: {
        intro: 'JTRはETC利用記録を実務的な確認フローに変換します。手作業での検索・印刷・名称変更・転送を減らし、配信・整理・保管・確認支援に注力します。JTRは独立サービスであり、公式通行データを作成しません。記録を整理し理解しやすく提示します。',
        bullets: [
          'PDF・CSV記録を保管し、設定に応じてETCカードや車両ごとに分類',
          '確認が必要な記録を強調表示し、早期確認を支援',
          '社内ポリシーとの照合が必要な管理者を支援',
          '異常な記録を検出候補として提示（確定的な不正判定はしません）',
          '税務・請求書・経費精算では記録整理を支援、最終処理は会計士・雇用主・公式指針で確認が必要',
        ],
      },
      pdfCsv: 'PDF記録は閲覧・共有が容易で、CSV記録は並べ替え・絞り込み・会計システムへの取り込みに適しています。両方を保管すれば、目視確認と自動処理の両面で対応できます。JTRはPDF・CSV形式で出力します（Excel・XLSXではありません）。',
      email: 'Eメール配信設定により、ETC利用記録を定期的に受け取ることができます。記録が届いたら速やかに確認し、公式データと社内説明を一緒に保管してください。記録が不詳細・遅延・予想外の場合は、推測せず公式事業者やカード発行会社に問い合わせ、回答を記録と一緒に保管してください。',
      steps: [
        {
          t: '本州四国連絡道路の通行記録確認範囲を確認',
          d: '本州四国連絡高速道路株式会社(JB本四)が管理する橋梁区間と、前後のNEXCO区間が含まれる可能性があるため、利用した経路全体を把握します。',
        },
        {
          t: '料金所・料金・割引・車両区分は公式道路会社サイトで確認',
          d: '経路、料金額、車両区分、割引適用条件、ETC専用レーンの有無など、料金に関わる公式情報はJB本四またはNEXCOの公式サイトで確認します。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細で通行履歴を取得',
          d: '通行後のETC利用明細、利用証明書、PDF記録、CSV記録は、ETC利用照会サービスまたはカード発行会社の明細サービスから取得します。',
        },
        {
          t: '通行日・IC・カード・車両・利用者・目的を記録',
          d: '通行日時、入口IC、出口IC、利用したETCカード、車両、車種区分、運転者または部署、利用目的を一つの形式で記録し、後日確認できるようにします。',
        },
        {
          t: '公式記録と社内説明を分けて保管',
          d: '公式な通行記録(PDF・CSV)と、社内の経費精算理由や承認情報は分けて管理します。公式記録は改変せず、説明は別ファイルまたは欄に記載します。',
        },
        {
          t: '不明な記録は推測せず公式窓口へ問い合わせ',
          d: '記録が不詳細、遅延、予想外の金額、車両不一致の場合は、推測せず該当する道路会社またはカード発行会社に問い合わせ、回答も記録と共に保管します。',
        },
      ],
      commonProblems: [
        {
          q: '本州四国連絡道路の通行記録が複数の区間に分かれて表示される',
          a: '橋梁区間(JB本四)と接続する高速道路(NEXCO)は別の事業者のため、記録が分かれる場合があります。入口IC・出口IC・日時・カードで対応関係を確認してください。',
        },
        {
          q: 'ETC利用照会サービスに記録が表示されない',
          a: '通常ETCカードは通行後数日以内に反映されますが、カード種類や通行状況により遅延する場合があります。15ヶ月の照会期間内であることを確認し、不明な場合は公式窓口へ問い合わせてください。',
        },
        {
          q: '割引が適用されているか確認したい',
          a: '割引適用はカード種類、時間帯、車種区分、登録状況により異なります。JB本四またはNEXCOの公式サイトで割引条件を確認し、適用されない場合は道路会社窓口へ問い合わせてください。',
        },
        {
          q: 'PDF記録とCSV記録のどちらを保管すべきか',
          a: 'PDF記録は閲覧・共有に便利で、CSV記録は並べ替え・抽出・会計システムへの取り込みに便利です。両方を保管しておくと後日の確認や処理が容易になります。',
        },
      ],
      examples: [
        {
          who: '本州四国連絡道路を利用する営業担当者',
          use: '経費精算前に、どの公式情報源を確認すべきかを本ガイドで把握し、ETC利用照会サービスからPDF・CSV記録を取得して提出します。',
        },
        {
          who: '複数車両を管理する企業の経理担当者',
          use: '月次でETC利用記録をPDF・CSV形式で一括取得し、社内の車両台帳・部署配分と照合して、不明な通行記録を早期に発見します。',
        },
        {
          who: '個人事業主または家族で車両を共有する利用者',
          use: 'ETCカード明細を確認し、業務利用と私用を分類してから、会計士へ提出する記録を整理します。',
        },
        {
          who: '車両管理責任者',
          use: '見覚えのない通行記録を発見した際、公式ETC利用記録で日時・IC・車両区分を確認してから、運転者へ確認依頼を行います。',
        },
      ],
      faqs: [
        {
          q: 'JTRは本州四国連絡道路の公式情報源ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式の経路、料金、割引、設定、安全規則はJB本四またはNEXCOの公式サイトで確認してください。JTRはETC利用記録の受信・整理・保管・確認を支援します。',
        },
        {
          q: 'ETC利用照会サービスは道路会社の公式サイトの代わりになりますか?',
          a: 'いいえ。ETC利用照会サービスはETCカードの利用明細と利用証明書の発行に有用ですが、最新の経路、料金、割引、車線、車種区分の確認には道路会社の公式サイトが必要です。',
        },
        {
          q: 'PDF記録とCSV記録はどちらを保存すべきですか?',
          a: '両方の保存が推奨されます。PDF記録は確認・共有が容易で、CSV記録は並べ替え・抽出・会計ソフトへの取り込みに適しています。',
        },
        {
          q: 'これらの記録を税務申告や経費精算に使用できますか?',
          a: '記録は税務や経費精算の資料として活用できますが、最終的な取扱いは勤務先の規定、会計士の助言、公式ガイダンスに従ってください。本ガイドは税務助言ではありません。',
        },
        {
          q: '料金や経路が間違っていると思われる場合はどうすればよいですか?',
          a: '公式記録を確認し、カード・車両・通行日時・IC情報を照合してください。問題が解決しない場合は、該当する道路会社またはカード発行会社の窓口へ問い合わせてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録の受信、整理、保管、確認フローを理解し、業務または家計管理に活用する方法を確認できます。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数ETCカードの通行記録を一元管理し、部署別・車両別の集計や確認作業を効率化できます。',
          path: '/business',
        },
        {
          title: '無料トライアル',
          desc: 'JTRの記録整理・PDF・CSV出力機能を実際に試し、自社または自分の管理フローに適合するか確認できます。',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用照会、利用証明書発行、PDF・CSV形式での利用明細ダウンロードが可能な公式サービス。通常ETCカードは過去15ヶ月分が照会可能。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別、利用証明書発行、通常ETCカード・ETCコーポレートカードの照会期間、無線・非無線通行、PDF・CSV出力について説明。',
        },
        {
          label: 'JB本四 ETC情報',
          url: 'https://www.jb-honshi.co.jp/customer_index/etc/',
          note: '本州四国連絡高速道路のETC利用照会サービス、ETC履歴プリンタなど、公式ETC関連サービスページ。',
        },
        {
          label: 'NEXCO西日本 料金・経路検索',
          url: 'https://search.w-nexco.co.jp/',
          note: 'NEXCO西日本管内の経路・料金検索が可能。本州四国連絡道路と接続する区間の確認に有用。',
        },
        {
          label: 'ETC総合情報ポータル: お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金、割引、ETC利用に関する公式道路会社カスタマーセンター一覧。不明な記録や問題がある場合の問い合わせ先。',
        },
      ],
      longBody: '本州四国連絡高速道路株式会社(JB本四高速)が管理する本州四国連絡橋ルートは、明石海峡大橋・瀬戸中央自動車道・西瀬戸自動車道(しまなみ海道)など複数の海峡横断路を含み、前後の接続区間でNEXCO西日本や他の高速道路会社と連続する特性があります。そのため一つの走行でも複数の事業者が関与し、料金・割引・車種区分の判定が複雑に組み合わさる場合が少なくありません。ETC利用照会サービスで取得できる利用明細には入口IC・出口IC・利用日時・利用金額・車載器管理番号などが記録されますが、橋梁部と接続区間の内訳や、どの事業者がどの部分を管理しているかは明細の1行だけでは判然としないことがあります。このため利用後に料金を確認する際は、JB本四高速の公式サイトやNEXCO西日本の料金検索で経路と内訳を照らし合わせ、どの区間にどの割引が適用されたかを整理する必要があります。法人や公用の利用では、ETCカードの利用明細だけでなく車両・運転者・業務目的・承認記録を同時に保管し、後日の経理確認や確認対応に備える体制が求められます。JTRはこうした公式記録の受信・整理・保管・閲覧支援を提供する独立サービスであり、NEXCO・JB本四高速・ETC利用照会サービス・カード発行会社・政府機関ではありません。JTRは公式な通行記録を作成する立場にはなく、利用者が公式サービスから取得した明細をPDFおよびCSV形式で受け取り、車両・カード・部門ごとに整理して閲覧・検索しやすくする役割を担います。利用明細に不明な走行や想定外の金額が含まれる場合、JTRは早期の確認を促す仕組みを提供しますが、不正利用の確定判断や法的結論を下す機能ではありません。実務上よくある誤解として、カード明細の1行だけを根拠に経費精算を進める、スクリーンショットや手書きメモで代用する、利用証明書の発行可能期間を過ぎてから記録を求める、車両と運転者の対応記録を残さないまま複数の車両で同一カードを使い回す、といった運用が挙げられます。これらは後日の確認時に説明が困難になるだけでなく、税務・監査・保険の場面で証跡不足と指摘されるリスクを高めます。橋梁区間を含む長距離走行では、SA・PAでの休憩時刻や給油記録も併せて保管しておくと、経路と時刻の整合性を示す補助資料として有用です。最終的な税務処理・請求書対応・補助金申請の可否は、所属組織の経理規程・顧問税理士の判断・公的ガイドラインに従って確認してください。',
    },
    en: {
      title: 'Honshu–Shikoku Bridge ETC Record Guide | Japan Toll Receipts',
      metaDesc: 'How to verify, organize, and store ETC usage records for Honshu–Shikoku Expressway. Official inquiry services, PDF/CSV records, JTR role, FAQs, and key points.',
      h1: 'Honshu–Shikoku Bridge ETC Record Guide',
      primaryKeyword: 'Honshu–Shikoku Bridge ETC records',
      answer: "The Honshu–Shikoku Bridge ETC Record Guide explains how drivers and companies using the Akashi Kaikyo Bridge, Seto-Chuo Expressway, Shimanami Kaido, and other Honshu–Shikoku routes can properly verify, organize, and store ETC usage records. Because JB Honshu–Shikoku and NEXCO routes connect, multiple operators' tolls and discounts may combine in a single trip. Careful verification of travel records and storing official data alongside internal explanations is essential.",
      whyMatters: "Honshu–Shikoku Expressway is managed by the specialized operator JB Honshu–Shikoku and connects with NEXCO routes before and after bridge sections. A single journey may apply multiple operators' tolls and discounts, so incomplete record verification can make expense reimbursement and audits difficult to explain. The ETC inquiry service and card statements provide official usage records, but vehicle, driver, and purpose details require separate internal documentation. Managing official data and internal explanations together makes later verification safe and efficient.",
      officialBackground: 'ETC record management for Honshu–Shikoku Expressway involves multiple official systems. Road operators publish route, toll, discount, vehicle class, and safety rules; the ETC inquiry service provides ETC card statements and usage certificates in PDF and CSV formats. Card issuers may provide monthly statements. JTR is an independent service—NOT NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC inquiry service, a card company, or a government agency. JTR does not create official travel data; it helps users organize, store, and verify records they receive. Practically useful records require five elements: which card was charged, which vehicle traveled, which section was used, why the trip occurred, and where official evidence resides. Storing official data and internal explanations separately makes future audits and inquiries easier to handle.',
      whoFor: [
        'Corporate drivers using Akashi Kaikyo, Seto-Chuo, Shimanami Kaido, and other routes for business',
        'Accounting staff managing multiple vehicles and ETC cards for expense reimbursement and department allocation',
        'Individual users traveling Honshu–Shikoku routes by rental or personal car who wish to retain usage records',
        'Management teams who want to formalize ETC record verification and retention procedures internally',
      ],
      jtrSolution: {
        intro: 'JTR converts ETC usage records into practical verification workflows. It reduces manual searching, printing, renaming, and forwarding, focusing on delivery, organization, storage, and verification support. JTR is independent and does not create official travel data; it organizes records and presents them clearly.',
        bullets: [
          'Stores PDF and CSV records, classified by ETC card or vehicle as configured',
          'Highlights records requiring review to support early verification',
          'Assists administrators in cross-checking records against internal policies',
          'Presents anomaly detection candidates (does not make definitive fraud determinations)',
          'Supports record organization for tax, invoicing, and expense workflows—final processing requires accountant, employer, or official guidance',
        ],
      },
      pdfCsv: 'PDF records are easy to view and share; CSV records suit sorting, filtering, and import into accounting systems. Retaining both allows visual inspection and automated processing. JTR outputs PDF and CSV formats (not Excel or XLSX).',
      email: 'Email delivery settings let you receive ETC usage records regularly. Verify records promptly upon arrival and store official data with internal explanations. If records are incomplete, delayed, or unexpected, do not guess—contact the official operator or card issuer, and retain the response with your records.',
      steps: [
        {
          t: 'Check the coverage of Honshu-Shikoku Bridge toll records',
          d: 'Understand the entire route used, as it may include bridge sections managed by JB Honshu-Shikoku and adjacent NEXCO sections before and after.',
        },
        {
          t: 'Verify toll gates, fees, discounts, and vehicle class on official road company sites',
          d: 'Check official information on routes, toll amounts, vehicle classification, discount eligibility, and ETC-only lanes on JB Honshu-Shikoku or NEXCO official sites.',
        },
        {
          t: 'Obtain travel history via ETC Inquiry Service or card statement',
          d: "Retrieve ETC usage details, usage certificates, PDF records, and CSV records after travel from the ETC Inquiry Service or your card issuer's statement service.",
        },
        {
          t: 'Record travel date, IC, card, vehicle, user, and purpose',
          d: 'Record date and time, entry IC, exit IC, ETC card used, vehicle, vehicle class, driver or department, and purpose in one format for later review.',
        },
        {
          t: 'Store official records separately from internal explanations',
          d: 'Keep official travel records (PDF/CSV) separate from internal expense reasons and approval details. Do not modify official records; add explanations in separate files or fields.',
        },
        {
          t: 'Contact official support for unclear records instead of guessing',
          d: 'For incomplete, delayed, unexpected amounts, or vehicle mismatches, contact the relevant road company or card issuer without guessing, and store responses with records.',
        },
      ],
      commonProblems: [
        {
          q: 'Honshu-Shikoku Bridge travel records appear split across multiple sections',
          a: 'Bridge sections (JB Honshu-Shikoku) and connecting expressways (NEXCO) are managed by different operators, so records may be split. Match them by entry IC, exit IC, date/time, and card.',
        },
        {
          q: 'Records do not appear in ETC Inquiry Service',
          a: "Standard ETC cards usually reflect within a few days after travel, but delays may occur depending on card type or travel conditions. Confirm you're within the 15-month inquiry period; contact official support if unclear.",
        },
        {
          q: 'Want to confirm whether discounts were applied',
          a: 'Discount eligibility depends on card type, time of day, vehicle class, and registration status. Check discount conditions on JB Honshu-Shikoku or NEXCO official sites; contact the road company if discounts are missing.',
        },
        {
          q: 'Should I keep PDF records or CSV records?',
          a: 'PDF records are convenient for viewing and sharing, while CSV records are useful for sorting, filtering, and importing into accounting systems. Keeping both makes later review and processing easier.',
        },
      ],
      examples: [
        {
          who: 'Sales staff using Honshu-Shikoku Bridge routes',
          use: 'Before expense reporting, check which official sources to consult in this guide, then retrieve and submit PDF and CSV records from the ETC Inquiry Service.',
        },
        {
          who: 'Corporate accounting staff managing multiple vehicles',
          use: 'Download monthly ETC usage records in PDF and CSV formats, cross-check with internal vehicle ledgers and department allocations, and identify unclear travel records early.',
        },
        {
          who: 'Sole proprietor or family sharing a vehicle',
          use: 'Review ETC card statements, separate business and personal use, then organize records for submission to the accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'When finding unfamiliar travel records, confirm date, time, IC, and vehicle class in official ETC usage records before requesting clarification from drivers.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official information source for Honshu-Shikoku Bridge routes?',
          a: 'No. JTR is an independent service. For official routes, tolls, discounts, settings, and safety rules, check JB Honshu-Shikoku or NEXCO official sites. JTR assists with receiving, organizing, storing, and reviewing ETC usage records.',
        },
        {
          q: 'Can the ETC Inquiry Service replace official road company sites?',
          a: 'No. The ETC Inquiry Service is useful for ETC card usage details and certificates, but official road company sites are needed for the latest routes, tolls, discounts, lanes, and vehicle classifications.',
        },
        {
          q: 'Should I save PDF records or CSV records?',
          a: 'Saving both is recommended. PDF records are easy to review and share, while CSV records are suited for sorting, filtering, and importing into accounting software.',
        },
        {
          q: 'Can these records be used for tax filing or expense reporting?',
          a: "Records can be used as supporting materials for tax or expense purposes, but final handling should follow your employer's policies, accountant's advice, and official guidance. This guide is not tax advice.",
        },
        {
          q: 'What should I do if I think the toll or route is incorrect?',
          a: 'Check official records and cross-check card, vehicle, date/time, and IC information. If the issue persists, contact the relevant road company or card issuer support.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: 'Understand the flow of receiving, organizing, storing, and reviewing ETC usage records, and how to apply it to business or household management.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Centralize travel records for multiple vehicles and ETC cards, streamlining department-level and vehicle-level aggregation and review tasks.',
          path: '/business',
        },
        {
          title: 'Free Trial',
          desc: "Try JTR's record organization, PDF, and CSV export features to see if they fit your company or personal management workflow.",
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for ETC card usage inquiry, usage certificate issuance, and downloading usage details in PDF and CSV formats. Standard ETC cards can query up to 15 months of past records.',
        },
        {
          label: 'ETC General Information Portal: ETC Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, usage certificate issuance, inquiry periods for standard ETC and ETC Corporate cards, wireless and non-wireless travel, and PDF/CSV output.',
        },
        {
          label: 'JB Honshu-Shikoku ETC Information',
          url: 'https://www.jb-honshi.co.jp/customer_index/etc/',
          note: 'Official ETC-related service page for Honshu-Shikoku Bridge expressways, including ETC Inquiry Service and ETC history printers.',
        },
        {
          label: 'NEXCO West Japan Route & Toll Search',
          url: 'https://search.w-nexco.co.jp/',
          note: 'Route and toll search for NEXCO West Japan network. Useful for confirming sections connecting to Honshu-Shikoku Bridge routes.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'List of official road company customer centers for tolls, discounts, and ETC usage. Contact points for unclear records or issues.',
        },
      ],
      longBody: "The Honshu-Shikoku Bridge Expressway Company Limited (JB Honshu-Shikoku) manages multiple strait-crossing routes including the Akashi Kaikyo Bridge, Seto-Chuo Expressway, and Nishi-Seto Expressway (Shimanami Kaido), which connect continuously with sections operated by NEXCO West and other expressway companies before and after the bridge portions. A single journey often involves multiple operators, and the determination of tolls, discounts, and vehicle classifications can be highly complex. ETC usage statements obtained through the ETC Toll Inquiry Service record entry and exit interchanges, usage date and time, toll amount, and on-board unit management number, but a single line in the statement may not clearly show the breakdown between bridge sections and connecting segments or which operator manages which portion. When reviewing charges after travel, it is necessary to cross-reference routes and breakdowns using JB Honshu-Shikoku's official website and NEXCO West's toll search tools to understand which discounts were applied to which segments. For corporate or official use, organizations are expected to maintain not only ETC card statements but also simultaneous records of vehicles, drivers, business purposes, and approval documentation to prepare for subsequent accounting reviews and audit responses. JTR is an independent service that supports the receipt, organization, storage, and viewing of such official records; it is not NEXCO, JB Honshu-Shikoku, the ETC Toll Inquiry Service, a card issuer, or a government agency. JTR does not create official travel records but receives statements obtained by users from official services in PDF and CSV formats, organizing them by vehicle, card, and department for easier viewing and searching. When statements include unexplained trips or unexpected amounts, JTR provides mechanisms to prompt early verification, but it does not make definitive determinations of unauthorized use or draw legal conclusions. Common misunderstandings in practice include processing expense claims based solely on a single line in a card statement, substituting with screenshots or handwritten notes, requesting records after the issuance period for usage certificates has expired, and rotating a single card across multiple vehicles without maintaining vehicle-driver correspondence records. These practices not only make later verification difficult but also increase the risk of being cited for insufficient documentation in tax, audit, or insurance contexts. For long-distance travel including bridge sections, storing rest times at service and parking areas along with refueling records can serve as useful supplementary material to demonstrate route and time consistency. Final tax treatment, invoice handling, and subsidy application eligibility should be confirmed in accordance with your organization's accounting regulations, guidance from your tax advisor, and applicable public guidelines.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '本四高速ETC記録の流れ',
            intro: '通行から記録保管までの4ステップ',
            nodes: [
              {
                title: '通行',
                desc: '明石海峡大橋・瀬戸中央道等を通行',
              },
              {
                title: '課金',
                desc: 'ETCカードに複数事業者の料金が記録',
              },
              {
                title: '照会',
                desc: 'ETC利用照会サービスでPDF・CSV取得',
              },
              {
                title: '保管',
                desc: '公式データと社内説明を一体管理',
              },
            ],
          },
          en: {
            title: 'Honshu-Shikoku ETC Record Flow',
            intro: '4 steps from passage to archive',
            nodes: [
              {
                title: 'Passage',
                desc: 'Drive Akashi-Kaikyo, Seto-Chuo, etc.',
              },
              {
                title: 'Charge',
                desc: "Multiple operators' tolls on ETC card",
              },
              {
                title: 'Query',
                desc: 'Download PDF/CSV from ETC service',
              },
              {
                title: 'Archive',
                desc: 'Store official data + internal notes',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式情報源マップ',
            intro: '本四高速ETC記録に関わる公式システム',
            sources: [
              {
                name: 'JB本四高速',
                role: '道路事業者',
                url: 'jb-honshi.co.jp',
                scope: '経路・料金・橋梁管理',
              },
              {
                name: 'ETC利用照会サービス',
                role: '通行記録提供',
                url: 'etc-meisai.jp',
                scope: 'PDF・CSV明細発行',
              },
              {
                name: 'ETCカード発行会社',
                role: '決済・明細',
                url: 'カード会社サイト',
                scope: '月次請求書・利用明細',
              },
              {
                name: 'JTR',
                role: '独立整理支援',
                url: 'japantollreceipts.jp',
                scope: '記録の分類・保管・確認補助',
              },
            ],
          },
          en: {
            title: 'Official Source Map',
            intro: 'Systems involved in Honshu-Shikoku ETC records',
            sources: [
              {
                name: 'JB Honshi',
                role: 'Road operator',
                url: 'jb-honshi.co.jp',
                scope: 'Routes, tolls, bridge mgmt',
              },
              {
                name: 'ETC Query Service',
                role: 'Record provider',
                url: 'etc-meisai.jp',
                scope: 'PDF/CSV statements',
              },
              {
                name: 'Card Issuer',
                role: 'Payment, billing',
                url: 'Card co. site',
                scope: 'Monthly statements',
              },
              {
                name: 'JTR',
                role: 'Independent organizer',
                url: 'japantollreceipts.jp',
                scope: 'Sort, store, review aid',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF vs CSV 保管比較',
            intro: '両形式の保管が実務上有効',
            columns: [
              'PDF記録',
              'CSV記録',
            ],
            rows: [
              {
                feature: '閲覧の容易さ',
                a: '高',
                b: '中',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '並べ替え・絞り込み',
                a: '不可',
                b: '可能',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '会計システム取り込み',
                a: '困難',
                b: '容易',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '監査時の証拠性',
                a: '高',
                b: '高',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: '長期保管',
                a: '推奨',
                b: '推奨',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'PDF vs CSV Storage',
            intro: 'Both formats recommended in practice',
            columns: [
              'PDF records',
              'CSV records',
            ],
            rows: [
              {
                feature: 'Ease of viewing',
                a: 'High',
                b: 'Medium',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Sort & filter',
                a: 'No',
                b: 'Yes',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Import to accounting',
                a: 'Hard',
                b: 'Easy',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Audit evidence',
                a: 'Strong',
                b: 'Strong',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: 'Long-term archive',
                a: 'Yes',
                b: 'Yes',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '実務上有効な記録の5要素',
            intro: '監査・精算・説明に耐える記録が必要',
            items: [
              {
                text: 'どのETCカードに課金されたか',
                hint: 'カード番号・名義を記録',
              },
              {
                text: 'どの車両が走行したか',
                hint: '車両番号・ナンバープレート',
              },
              {
                text: 'どの区間を利用したか',
                hint: '入口・出口・経路',
              },
              {
                text: 'なぜ通行したか',
                hint: '業務目的・訪問先・承認者',
              },
              {
                text: '公式証拠はどこにあるか',
                hint: 'PDF・CSV保管場所・参照番号',
              },
            ],
          },
          en: {
            title: '5 Elements of Actionable Records',
            intro: 'Records must support audit, reimbursement, explanation',
            items: [
              {
                text: 'Which ETC card was charged',
                hint: 'Card number, cardholder name',
              },
              {
                text: 'Which vehicle drove',
                hint: 'Vehicle ID, license plate',
              },
              {
                text: 'Which section was used',
                hint: 'Entry, exit, route',
              },
              {
                text: 'Why the trip occurred',
                hint: 'Business purpose, destination, approver',
              },
              {
                text: 'Where official proof is',
                hint: 'PDF/CSV location, reference number',
              },
            ],
          },
        },
      },
    ],
  },
];

// The remaining 8 NEXCO regional guides share a structural framing.
// Their JA fields are built explicitly below so all strings appear in
// the JA review export workbook.
const _NEXCO_ROUTES = [
  {
    slug: 'tomei-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '東名高速 ETC 明細',
      en: 'Tomei Expressway ETC statement',
    },
    ja: {
      title: '東名高速ETC明細の確認・整理方法 | JTR',
      metaDesc: '東名高速道路（E1・NEXCO中日本管轄）のETC利用明細を確認・整理・保存する実務ガイド。ETC利用照会サービスでPDF利用証明書・CSV明細を取得する手順、IC・JCT別の整理、東京〜名古屋〜静岡出張の経費精算、料金検索ツールでの事前見積もり、独立サービスJTRの日次配信を併用した記録運用までまとめました。',
      h1: '東名高速ETC明細の確認と整理',
      primaryKeyword: '東名高速 ETC 明細',
      answer: '東名高速道路のETC明細は、走行後にETC利用照会サービスで確認できます。通常のETCカードなら15カ月分、法人ETCカードなら62日分の利用記録をPDF・CSVで取得可能です。事前の料金検索はNEXCO中日本ドライブコンパスで、走行後の記録整理にはJTRが役立ちます。',
      whyMatters: '東京-名古屋間を結ぶ東名高速道路(E1)は、日本を代表する大動脈です。業務利用や定期的な出張で走行する場合、料金の事前確認、割引適用の把握、走行後の明細整理が経費精算や月次集計に直結します。公式ツールで正確な情報を得て、記録を適切に保存・整理することが、スムーズな業務運営につながります。',
      officialBackground: '東名高速道路はNEXCO中日本が管理する主要路線で、料金検索・ルート確認はドライブコンパスで行えます。工事・規制情報も公式サイトで提供されています。走行後の利用明細は、ETC利用照会サービスが公式の確認先です。同サービスでは利用証明書の発行、PDF・CSVダウンロードに対応しており、通常のETCカード・ETCパーソナルカードは15カ月分、法人ETCカードは62日分の照会が可能です。割引プログラムやポイント制度についてはETCマイレージサービスやNEXCO各社の公式ページで最新ルールを確認してください。料金は区間・車種・日時・ルート・割引登録状況によって変動するため、走行前に公式ツールで見積もり、走行後に実際の記録を取得する流れが基本です。',
      whoFor: [
        '東京-名古屋間を定期的に運転する営業担当者',
        '東名・新東名を使い分ける物流事業者',
        '関東-中部エリアで出張が多いビジネス利用者',
        '複数台・複数カードの明細を月次で集計する経理担当者',
      ],
      jtrSolution: {
        intro: 'JTRは独立したETC記録配信・整理サービスです。公式システムが作成した利用データを受け取り、PDF・CSVを自動配信してアーカイブ化します。東名高速の利用頻度が高い方や、複数カード・複数車両を管理する事業者に適しています。',
        bullets: [
          '走行後のPDF・CSV記録をメール自動配信',
          'カード別・車両別・部門別の記録整理に対応',
          '手動ダウンロードの手間を削減し記録漏れを防止',
          '日英対応で外国人ドライバーや国際チームでも利用可',
          '月次集計や経費精算のワークフローを効率化',
        ],
      },
      pdfCsv: 'PDFは人が読みやすく、提出書類として扱いやすい形式です。CSVは並べ替え・絞り込み・月次集計に向いています。本格的な記録管理では両方を保存し、PDFで個別確認、CSVで全体把握を行う使い分けが有効です。',
      email: 'JTRはPDF・CSVをメール配信するため、走行後に手動ダウンロードする手間が省けます。複数カードや複数ドライバーの記録を一元管理したい事業者、定期的に東名を利用し記録の取り忘れを防ぎたい方に適しています。',
      steps: [
        {
          t: 'NEXCO中日本ドライブコンパスで経路と料金目安を確認',
          d: '東名高速または新東名高速のルート選択と、IC間・車種別の料金目安を公式ツールで事前確認します。',
        },
        {
          t: '工事・車線規制・渋滞情報を確認',
          d: 'NEXCO中日本の工事・規制情報ページで、走行予定日の通行規制やう回路の有無を確認します。',
        },
        {
          t: '有効なETCカードとセットアップ済み車載器で走行',
          d: '正常に動作する車載器と、有効期限内のETCカードで通行し、ゲート通過時の音声・ランプを確認します。',
        },
        {
          t: 'ETC利用照会サービスで利用明細を確認',
          d: '走行後、公式のETC利用照会サービスで最終的な利用履歴・料金・割引適用を確認します。',
        },
        {
          t: '利用証明書(PDF)と利用明細(CSV)をダウンロード',
          d: '経理提出用のPDF証明書と、集計・フィルタ用のCSVファイルの両方を保存します。',
        },
        {
          t: 'JTRで東名高速の利用記録をカード別・車両別に整理',
          d: '複数カード・複数車両で繰り返し利用する場合、JTRでPDF・CSVを自動受信・整理し、月次レビューを効率化します。',
        },
      ],
      commonProblems: [
        {
          q: '料金検索ツールで表示された金額と実際の請求額が違う',
          a: '検索ツールの金額は目安です。最終的な料金は走行ルート・時間帯・割引適用状況により変動するため、走行後にETC利用照会サービスで実績を確認してください。',
        },
        {
          q: '割引が適用されているか分からない',
          a: '割引適用には事前登録や走行条件が必要な場合があります。公式のETCマイレージサービスやNEXCO割引ページで条件を確認し、利用照会サービスで実績を確認してください。',
        },
        {
          q: 'スクリーンショットだけ保存していて正式な証明書がない',
          a: '経理や税務で利用する場合、公式のPDF利用証明書またはCSV明細が必要です。ETC利用照会サービスまたはJTRで正式な記録を取得・保管してください。',
        },
        {
          q: '法人ETCカードの明細が15か月前まで遡れない',
          a: '法人ETCカードの照会期間は62日間です。一般ETCカード・ETCパーソナルカードは15か月ですが、カード種別により期間が異なるため早めに記録を取得してください。',
        },
      ],
      examples: [
        {
          who: '物流配車担当者',
          use: '東名高速と新東名高速の経路選択を比較し、JTRのCSVファイルでトラック別の実績料金を月次レビューしています。',
        },
        {
          who: '営業担当ドライバー',
          use: '東京・静岡間の定期訪問で、JTRからPDF証明書を経費精算に、CSV明細を月次集計に活用しています。',
        },
        {
          who: '車両管理責任者',
          use: '工事期間中の予定外ルート変更を、期待される東名利用と実際の明細記録を比較して把握しています。',
        },
        {
          who: '外国籍居住者',
          use: '長距離走行前に公式経路検索で料金を確認し、走行後はJTRのメール配信でPDF・CSV記録を受け取っています。',
        },
      ],
      faqs: [
        {
          q: '東名高速のETC料金と明細について最も重要な点は何ですか?',
          a: '料金・割引・利用履歴は公式のNEXCOやETC利用照会サービスが管理しており、JTRはその記録を受信・整理する独立したサービスです。公式情報を確認し、JTRで記録管理を効率化してください。',
        },
        {
          q: 'JTRは公式のETCまたはNEXCOサービスですか?',
          a: 'いいえ。JTRは独立した記録配信・整理サービスです。料金・割引・ポイント・適格性の決定は公式のNEXCO・ETC利用照会サービス・ETCマイレージサービスが行います。',
        },
        {
          q: 'PDFとCSVのどちらを保存すべきですか?',
          a: '可能であれば両方を保存してください。PDFは人間の確認や証明書提出に適しており、CSVは並べ替え・フィルタ・月次集計に適しています。',
        },
        {
          q: '料金や割引は日付や条件で変わりますか?',
          a: 'はい。料金・割引適用・適格性は道路・日時・時間帯・車種・ルート・事前登録・公式キャンペーン規則により変動します。公式ページで最新条件を確認してください。',
        },
        {
          q: '企業利用で個人利用と異なる点は何ですか?',
          a: '企業はETCカードをドライバー・車両・部門・精算規則に紐付け、PDF・CSV記録を共有アーカイブで管理する繰り返し可能なワークフローを構築してください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録のPDF・CSV自動配信と整理の流れを確認',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数カード・車両・部門での東名高速記録管理',
          path: '/business',
        },
        {
          title: '無料トライアル',
          desc: '東名高速の記録配信を実際に試して効果を確認',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'NEXCO中日本公式サイト',
          url: 'https://www.c-nexco.co.jp/',
          note: '料金・経路検索、交通情報、SA情報、通行規制、ETC割引の公式情報',
        },
        {
          label: 'ドライブコンパス',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: '東名高速を含むNEXCO中日本管内の公式料金・経路検索ツール',
        },
        {
          label: 'NEXCO中日本工事・規制情報',
          url: 'https://www.c-nexco.co.jp/construction/useful/',
          note: '東名高速の工事予定・通行規制・う回路の公式情報',
        },
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETC利用履歴確認、利用証明書発行、PDF・CSVダウンロードの公式サービス',
        },
        {
          label: 'ETC総合情報ポータル(利用照会サービス説明)',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '一般ETCカード15か月・法人ETCカード62日間の照会期間とPDF・CSV提供の公式説明',
        },
      ],
      longBody: '東名高速道路のETC料金と明細を調べる際、多くのドライバーが最初に直面する混乱は、日本のETC環境が複数の独立したシステムで構成されている点です。料金を支払うETCカード、料金所と通信する車載器、NEXCO中日本が提供する料金検索ツール、走行後の利用履歴を表示するETC利用照会サービス、そして利用明細の受け取りと整理を支援する任意のサービスがそれぞれ異なる役割を担っています。東名高速道路はE1として知られ、NEXCO中日本が管理する主要な東京・名古屋間回廊です。走行前の料金見積もりはドライブコンパスなどの公式ツールで確認でき、工事情報や通行規制も同じくNEXCO中日本の公式サイトで事前に把握できます。新東名を含む複数ルートの選択肢がある区間では、走行予定のルートを正確に指定しないと見積もり額と実際の請求額が一致しない場合があります。走行後の記録取得には、ETC利用照会サービスが中心的な役割を果たします。公式案内によれば、通常のETCクレジットカードおよびETCパーソナルカードは15か月分の履歴照会が可能ですが、ETCコーポレートカードは62日間と短いため、法人利用者は特に注意が必要です。利用証明書の発行とPDFまたはCSV形式でのダウンロードに対応しており、領収書レーンで紙の利用証明書を受け取らなかった場合でも事後確認が可能です。PDF記録は人間が確認しやすく提出書類として適している一方、CSV記録は複数カード・車両・運転者・部門にわたる集計や並べ替えに適しています。経理担当者や車両管理者は可能な限り両方の形式を保管することで、個別の証跡確認と月次集計の両方に対応できます。Japan Toll Receiptsは公式システムではなく、独立した記録配信・整理サービスとして位置づけられます。JTRはNEXCO、ETC利用照会サービス、ETCマイレージサービス、政府機関のいずれとも提携しておらず、料金データの作成者でも割引適用の判定者でもありません。その役割は、公式システムが生成した利用データをメール配信し、PDFとCSVを継続的にアーカイブし、複数カードや車両ごとの記録整理を支援する点にあります。バイリンガル対応や月次ワークフローの一貫性も利点ですが、料金額や割引適用の最終判断は常に公式システムと道路運営者に委ねられます。東名高速を頻繁に利用する物流事業者や営業担当者は、走行前にルートと料金を公式ツールで確認し、走行後にCSVファイルで実績を集計することで、予算管理と経費精算の精度を高めることができます。工事期間中のルート変更や迂回による料金差異も、予定ルートと実際の明細記録を照合することで把握しやすくなります。記録を経理処理や税務準備に使用する前には、記録元、日付、車種区分、カード種別、ルート、最終金額を通常は確認し、社内規定や会計担当者の要件に従うことが重要です。割引プログラムは事前登録が必要な場合や、特定の道路・日時・時間帯にのみ適用される場合があり、自動適用を前提とすることは避けるべきです。また、見積もり料金と利用記録を混同したり、スクリーンショットのみを保存して公式PDF・CSV記録を取得しないといった誤りも散見されます。照会期間が短いカードでは記録取得の遅れが致命的になるため、走行後は速やかに利用照会サービスで確認し、必要に応じてJTRのような自動配信サービスを活用することで記録漏れを防ぐことが推奨されます。',
    },
    en: {
      title: 'Tomei Expressway ETC Statement Guide | JTR',
      metaDesc: 'How to check and organize Tomei Expressway ETC statements. Covers NEXCO Central fare lookup, construction info, ETC inquiry service records, PDF/CSV export, and JTR delivery.',
      h1: 'Tomei Expressway ETC Statement Check & Organization',
      primaryKeyword: 'Tomei Expressway ETC statement',
      answer: "Tomei Expressway ETC statements are available after travel via the ETC Inquiry Service. Standard ETC cards provide 15 months of records; corporate ETC cards provide 62 days. Records can be downloaded in PDF and CSV formats. For advance fare estimates, use NEXCO Central's Drive Compass; for post-trip record management, JTR streamlines the process.",
      whyMatters: "The Tomei Expressway (E1) linking Tokyo and Nagoya is one of Japan's most vital arteries. For business users and frequent travelers, advance fare checks, discount awareness, and post-trip statement organization directly impact expense reporting and monthly reconciliation. Accessing accurate information through official tools and storing records properly supports smooth business operations.",
      officialBackground: 'The Tomei Expressway is managed by NEXCO Central Japan. Fare searches and route planning are available via Drive Compass, and construction or regulation updates are posted on the official site. Post-trip statements are accessed through the ETC Inquiry Service, which issues usage certificates and supports PDF and CSV downloads. Standard ETC and ETC Personal cards retain 15 months of history; corporate ETC cards retain 62 days. Discount programs and point schemes are detailed on the ETC Mileage Service and NEXCO official pages—check for the latest rules. Fares vary by segment, vehicle class, date, time, route, and discount enrollment, so the standard workflow is to estimate via official tools before travel and retrieve actual records afterward.',
      whoFor: [
        'Sales staff who regularly drive the Tokyo–Nagoya corridor',
        'Logistics operators who switch between Tomei and Shin-Tomei routes',
        'Business travelers with frequent trips across Kanto–Chubu regions',
        'Accounting teams reconciling multiple cards and vehicles monthly',
      ],
      jtrSolution: {
        intro: 'JTR is an independent ETC record delivery and organization service. It receives usage data generated by official systems and auto-delivers PDF and CSV files for archival. Ideal for frequent Tomei users and businesses managing multiple cards or vehicles.',
        bullets: [
          'Auto-delivery of post-trip PDF and CSV records via email',
          'Organize records by card, vehicle, or department',
          'Eliminate manual downloads and prevent missing records',
          'Bilingual support (Japanese/English) for international drivers and teams',
          'Streamline monthly reconciliation and expense workflows',
        ],
      },
      pdfCsv: 'PDF is human-readable and suitable for submission documents. CSV enables sorting, filtering, and monthly aggregation. For robust record management, save both formats: use PDF for individual checks and CSV for holistic analysis.',
      email: 'JTR delivers PDF and CSV records by email, removing the need for manual downloads after each trip. Well-suited for businesses consolidating multiple cards or drivers, and for regular Tomei users who want to avoid missing records.',
      steps: [
        {
          t: 'Check route and fare estimates on NEXCO Central Drive Compass',
          d: 'Use the official tool to compare Tomei and Shin-Tomei routes and check IC-to-IC fare estimates by vehicle class before your trip.',
        },
        {
          t: 'Check construction, lane regulations, and congestion information',
          d: "Review NEXCO Central's construction and regulation pages for your planned travel date to confirm restrictions and detour availability.",
        },
        {
          t: 'Travel with a valid ETC card and registered on-board unit',
          d: 'Use a functioning on-board unit with an in-date ETC card, and confirm gate passage by audio tone and indicator lamp at each toll gate.',
        },
        {
          t: 'Confirm usage details on the ETC Inquiry Service',
          d: 'After your trip, check the official ETC Inquiry Service for final usage history, charges, and applied discounts.',
        },
        {
          t: 'Download usage certificate (PDF) and usage details (CSV)',
          d: 'Save both the PDF certificate for accounting submissions and the CSV file for sorting, filtering, and aggregation.',
        },
        {
          t: 'Organize Tomei Expressway records by card and vehicle with JTR',
          d: 'For multi-card, multi-vehicle use, JTR auto-receives and organizes PDF and CSV files, streamlining monthly reviews.',
        },
      ],
      commonProblems: [
        {
          q: 'The fare shown in the search tool differs from the actual charge',
          a: 'Search-tool amounts are estimates. Final charges vary by actual route, time of day, and applied discounts, so typically verify post-trip on the ETC Inquiry Service.',
        },
        {
          q: "I can't tell if discounts were applied",
          a: 'Some discounts require advance registration or specific travel conditions. Check the official ETC Mileage Service and NEXCO discount pages for eligibility, then confirm in the Inquiry Service.',
        },
        {
          q: 'I only saved screenshots and have no official certificate',
          a: 'For accounting or tax purposes, you need the official PDF certificate or CSV details. Retrieve formal records from the ETC Inquiry Service or JTR.',
        },
        {
          q: 'Corporate ETC card history does not go back 15 months',
          a: 'Corporate ETC cards offer 62-day lookback; personal and ETC Personal cards offer 15 months. Retention windows vary by card type, so download records promptly.',
        },
      ],
      examples: [
        {
          who: 'Logistics dispatcher',
          use: 'Compares Tomei and Shin-Tomei route choices and reviews actual truck fares per vehicle in monthly CSV files from JTR.',
        },
        {
          who: 'Sales field driver',
          use: 'For regular Tokyo–Shizuoka visits, uses JTR PDF certificates for expense claims and CSV details for monthly summaries.',
        },
        {
          who: 'Vehicle manager',
          use: 'Reconciles expected Tomei usage against actual detail records to identify unplanned route changes during construction periods.',
        },
        {
          who: 'Foreign national resident',
          use: 'Checks official route search for fares before long trips, then receives PDF and CSV records via JTR email delivery after travel.',
        },
      ],
      faqs: [
        {
          q: 'What is the most important point about Tomei Expressway ETC fares and details?',
          a: 'Fares, discounts, and usage history are managed by official NEXCO and ETC Inquiry Services; JTR is an independent service that receives and organizes those records. typically confirm official data and use JTR to streamline record management.',
        },
        {
          q: 'Is JTR an official ETC or NEXCO service?',
          a: 'No. JTR is an independent record delivery and organization service. Fare, discount, point, and eligibility decisions are made by official NEXCO, ETC Inquiry Service, and ETC Mileage Service.',
        },
        {
          q: 'Should I save PDF or CSV, or both?',
          a: 'Save both if possible. PDFs suit human review and certificate submission; CSVs enable sorting, filtering, and monthly aggregation.',
        },
        {
          q: 'Do fares and discounts change by date or conditions?',
          a: 'Yes. Fares, discount application, and eligibility vary by road, date, time, vehicle class, route, advance registration, and official campaign rules. typically check official pages for current conditions.',
        },
        {
          q: 'How does corporate use differ from personal use?',
          a: 'Corporations should map ETC cards to drivers, vehicles, departments, and reimbursement rules, and establish repeatable workflows with shared PDF and CSV archives.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR works',
          desc: 'See how ETC usage PDF and CSV auto-delivery and organization flow',
          path: '/how-it-works',
        },
        {
          title: 'Business plans',
          desc: 'Manage Tomei Expressway records across multiple cards, vehicles, and departments',
          path: '/business',
        },
        {
          title: 'Free trial',
          desc: 'Try Tomei Expressway record delivery and see the benefits firsthand',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'NEXCO Central official site',
          url: 'https://www.c-nexco.co.jp/',
          note: 'Official fares, route search, traffic info, SA info, regulations, and ETC discounts',
        },
        {
          label: 'Drive Compass',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: 'Official fare and route search tool for Tomei Expressway and all NEXCO Central roads',
        },
        {
          label: 'NEXCO Central construction and regulation info',
          url: 'https://www.c-nexco.co.jp/construction/useful/',
          note: 'Official construction schedules, traffic restrictions, and detour info for Tomei Expressway',
        },
        {
          label: 'ETC Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for ETC usage history, certificate issuance, and PDF/CSV downloads',
        },
        {
          label: 'ETC General Information Portal (Inquiry Service guide)',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Official explanation of 15-month (personal) and 62-day (corporate) lookback and PDF/CSV provision',
        },
      ],
      longBody: "When looking up ETC tolls and itemized statements for the Tomei Expressway, many drivers first encounter confusion stemming from the fact that Japan's ETC environment is composed of multiple independent systems. The ETC card that pays the toll, the on-board unit that communicates with the toll gate, the fare-search tools provided by NEXCO Central Nippon, the ETC Meisai Service that displays post-trip usage history, and any optional services that assist with receiving and organizing itemized records each play distinct roles. The Tomei Expressway, known as route E1, is a major Tokyo–Nagoya corridor managed by NEXCO Central Nippon. Pre-trip fare estimates can be checked using official tools such as the Drive Compass, and construction notices or traffic restrictions are likewise available in advance on the NEXCO Central Nippon official site. In sections where multiple route options exist—including the Shin-Tomei—failure to specify the planned route accurately may result in a mismatch between the estimated amount and the actual charge. For post-trip record retrieval, the ETC Meisai Service plays a central role. According to official guidance, standard ETC credit cards and ETC Personal Cards permit query of 15 months of history, whereas ETC Corporate Cards offer only 62 days, requiring particular attention from corporate users. The system supports issuance of usage certificates and download in PDF or CSV format, enabling post-trip verification even when a paper certificate was not collected at the receipt lane. PDF records are easy for human review and suitable as supporting documents, while CSV records are well-suited for aggregation and sorting across multiple cards, vehicles, drivers, and departments. Accounting staff and vehicle managers are encouraged to retain both formats whenever possible, covering both individual trace confirmation and monthly consolidation. Japan Toll Receipts is positioned as an independent record-delivery and organization service, not an official system. JTR is not affiliated with NEXCO, the ETC Meisai Service, the ETC Mileage Service, or any government agency, and is neither the creator of toll data nor the authority that determines discount eligibility. Its role lies in delivering usage data generated by official systems via email, continuously archiving PDF and CSV files, and assisting with record organization across multiple cards and vehicles. Bilingual support and consistent monthly workflows are additional benefits, but final determination of toll amounts and discount application typically rests with the official systems and road operators. Logistics companies and sales representatives who frequently use the Tomei Expressway can improve budget management and expense-settlement accuracy by checking routes and tolls with official tools before travel and aggregating actual results via CSV files afterward. Toll variances caused by route changes or detours during construction periods also become easier to identify by reconciling planned routes with actual itemized records. Before using records for accounting processing or tax preparation, it is essential to verify the record source, date, vehicle class, card type, route, and final amount, and to follow internal policies and accountant requirements. Discount programs may require advance registration or apply only to specific roads, dates, or time periods, and should not be assumed to apply automatically. Common errors include confusing estimated tolls with usage records or saving only screenshots without retrieving official PDF and CSV files. For cards with short query windows, delays in record retrieval can prove critical; it is recommended to check the Meisai Service promptly after travel and, where appropriate, to use automatic delivery services such as JTR to prevent record loss.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '東名高速ETC明細の取得フロー',
            intro: '走行前の料金確認から走行後の記録取得まで、4ステップで完結します。',
            nodes: [
              {
                title: '事前料金検索',
                desc: 'NEXCO中日本ドライブコンパスで区間・車種・日時を指定し料金見積もり',
              },
              {
                title: 'ETC走行',
                desc: '東名高速をETCカードで走行、ゲート通過時に自動記録',
              },
              {
                title: 'ETC利用照会サービス',
                desc: '走行後、公式サイトで15カ月分(通常)または62日分(法人)の明細を確認',
              },
              {
                title: 'PDF・CSV保存',
                desc: '利用証明書PDF・CSVをダウンロードして経費精算・月次集計に活用',
              },
            ],
          },
          en: {
            title: 'Tomei Expressway ETC Receipt Flow',
            intro: 'Four steps from pre-trip fare lookup to post-trip record retrieval.',
            nodes: [
              {
                title: 'Pre-trip Fare Lookup',
                desc: 'Use NEXCO Central Nippon Drive Compass to estimate toll by section, vehicle, date',
              },
              {
                title: 'ETC Drive',
                desc: 'Drive Tomei with ETC card; usage is auto-recorded at each gate',
              },
              {
                title: 'ETC Usage Query Service',
                desc: 'Check 15-month (personal) or 62-day (corporate) history on official portal',
              },
              {
                title: 'Save PDF & CSV',
                desc: 'Download usage certificates and CSVs for expense claims and monthly rollups',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '東名高速の公式情報源',
            intro: '料金検索、工事情報、明細照会、割引プログラムは各公式サイトで確認できます。',
            sources: [
              {
                name: 'NEXCO中日本ドライブコンパス',
                role: '料金検索・ルート確認',
                url: 'driveplaza.com',
                scope: '東名全区間の料金見積もり、所要時間、SA・PA情報',
              },
              {
                name: 'NEXCO中日本工事・規制情報',
                role: 'リアルタイム規制',
                url: 'c-nexco.co.jp',
                scope: '通行止め、車線規制、迂回路案内',
              },
              {
                name: 'ETC利用照会サービス',
                role: '走行後の明細照会',
                url: 'etc-meisai.jp',
                scope: '利用証明書PDF・CSVダウンロード、15カ月/62日分履歴',
              },
              {
                name: 'ETCマイレージサービス',
                role: 'ポイント・割引登録',
                url: 'smile-etc.jp',
                scope: '走行ポイント還元、平日朝夕割引の登録・照会',
              },
            ],
          },
          en: {
            title: 'Official Tomei Expressway Resources',
            intro: 'Fare lookup, construction updates, usage records, and discount programs are managed by separate portals.',
            sources: [
              {
                name: 'NEXCO Central Drive Compass',
                role: 'Fare & route planner',
                url: 'driveplaza.com',
                scope: 'Toll estimates, travel time, service-area info for entire Tomei',
              },
              {
                name: 'NEXCO Central Construction Info',
                role: 'Real-time restrictions',
                url: 'c-nexco.co.jp',
                scope: 'Closures, lane regulations, detour guidance',
              },
              {
                name: 'ETC Usage Query Service',
                role: 'Post-trip records',
                url: 'etc-meisai.jp',
                scope: 'PDF/CSV download, 15-month (personal) or 62-day (corporate) history',
              },
              {
                name: 'ETC Mileage Service',
                role: 'Points & discounts',
                url: 'smile-etc.jp',
                scope: 'Mileage point redemption, weekday commuter discount registration',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF vs. CSV：明細形式の使い分け',
            intro: '公式サイトは両形式に対応。用途に応じて使い分けることで記録管理が効率化します。',
            columns: [
              'PDF',
              'CSV',
            ],
            rows: [
              {
                feature: '人が読みやすい',
                a: 'レイアウト整形済み',
                b: '表計算ソフトで開く',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '提出書類として利用',
                a: '利用証明書として提出可',
                b: '加工後に別ファイル化が必要',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '並べ替え・絞り込み',
                a: '手作業で行う',
                b: 'ソート・フィルタ機能で即座に可能',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '月次集計・分析',
                a: '手計算または転記',
                b: 'ピボットテーブル・SUMで自動集計',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '複数カードの統合',
                a: 'ファイルごとに確認',
                b: '1つのシートに結合可能',
                kindA: 'no',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'PDF vs. CSV: Choosing the Right Format',
            intro: 'Official portals offer both. Use each format strategically for efficient record management.',
            columns: [
              'PDF',
              'CSV',
            ],
            rows: [
              {
                feature: 'Human readability',
                a: 'Pre-formatted layout',
                b: 'Opens in spreadsheet',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Submission as proof',
                a: 'Usage certificate accepted as-is',
                b: 'Requires post-processing',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Sort & filter',
                a: 'Manual effort',
                b: 'Instant sort/filter in Excel',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Monthly rollup & analysis',
                a: 'Hand calculation or re-key',
                b: 'Auto-sum via pivot tables',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Multi-card consolidation',
                a: 'Check file by file',
                b: 'Merge into single sheet',
                kindA: 'no',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'DataPipeline',
        labels: {
          ja: {
            title: 'JTRによる自動配信の仕組み',
            intro: '公式ETC記録を受け取り、PDF・CSVをメール配信してアーカイブ化。手動操作を削減します。',
            stages: [
              {
                name: 'ETC走行記録の生成',
                desc: '東名高速を走行すると、公式ETC利用照会サービスに利用データが登録される',
              },
              {
                name: 'JTRが定期取得',
                desc: 'JTRは公式サービスから新しい利用データを自動的に取得',
              },
              {
                name: 'PDF・CSV自動配信',
                desc: '取得した記録をPDF・CSV形式でメールに添付して配信',
              },
              {
                name: '受信・アーカイブ',
                desc: '利用者はメールで受信し、フォルダ整理やクラウド保存で一元管理',
              },
            ],
          },
          en: {
            title: 'How JTR Automated Delivery Works',
            intro: 'JTR retrieves official ETC records and emails PDF/CSV files, reducing manual downloads.',
            stages: [
              {
                name: 'ETC Trip Record Created',
                desc: 'Your Tomei trip is logged in the official ETC Usage Query Service',
              },
              {
                name: 'JTR Periodic Fetch',
                desc: 'JTR automatically pulls new usage data from the official portal',
              },
              {
                name: 'Auto-Email PDF & CSV',
                desc: 'Records are formatted and delivered as email attachments',
              },
              {
                name: 'Receive & Archive',
                desc: 'You receive the email and store files in folders or cloud storage for unified tracking',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'kyushu-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '九州自動車道 ETC明細',
      en: 'Kyushu Expressway ETC statement',
    },
    ja: {
      title: '九州自動車道ETC明細ガイド | Japan Toll Receipts',
      metaDesc: '九州自動車道（NEXCO西日本管轄）のETC明細を確認・整理・保存する実務ガイド。ETC利用照会サービスからPDF利用証明書・CSV明細を取得する手順、IC・JCT別整理、福岡〜熊本〜鹿児島出張の経費精算、料金検索の使い方、独立サービスJTRの日次配信を併用した記録運用までまとめました。',
      h1: '九州自動車道ETC明細ガイド',
      primaryKeyword: '九州自動車道 ETC明細',
      answer: '九州自動車道のETC明細を正しく管理するには、ETC利用照会サービスで公式のETC利用記録を確認し、利用日・入口IC・出口IC・カード・車両・料金をセットで保管することが重要です。NEXCO西日本の公式ルート検索で料金や割引を照合し、PDF・CSV形式で記録を残すことで、経費精算や社内確認がスムーズに進みます。',
      whyMatters: '九州自動車道はNEXCO西日本が管理する九州の基幹ルートであり、業務・個人を問わず利用頻度が高い路線です。ETC明細を適切に管理しないと、後日の経費精算や社内確認で「いつ・誰が・なぜ」通行したのか説明できず、証憑不足や重複請求のリスクが生じます。公式記録を正しく取得・整理し、カードや車両と紐付けて保管することで、経理担当者や管理者が迅速に確認でき、不正利用の早期発見や内部統制の強化にもつながります。',
      officialBackground: '日本の通行料金記録管理では、複数の独立したシステムが連携しています。道路事業者は路線・料金・割引・車種区分・安全情報を公開し、ETC利用照会サービスはETCカードの利用明細・利用証明書・PDF/CSVダウンロードを提供します(通常のETCカードは過去15か月分)。カード発行会社は月次利用明細を提供します。JTRはこれら公式記録の受取・整理・保管・確認を支援する独立サービスであり、NEXCO・MEISAI・ETC利用照会サービス・カード会社・政府機関ではありません。公式通行料金データを作成せず、既存の公式記録を活用しやすく整えることに特化しています。地域別の記録は通常は担当事業者と紐付け、日付・IC・車種・カード・実請求額を正確に保管してください。',
      whoFor: [
        '九州圏内で頻繁に高速道路を利用する営業担当者や配送ドライバー',
        '社用車のETC明細を月次で集計・精算する経理・総務担当者',
        '複数車両・複数カードを運用する物流企業や車両管理者',
        'レンタカーや家族共用車でETC利用を分けて記録したい個人ユーザー',
      ],
      jtrSolution: {
        intro: 'JTRはETC利用記録を実務的な確認フローに変えます。手作業での検索・印刷・改名・転送を減らし、配信・整理・保管・確認支援に集中します。公式記録を作らず、既存記録を分かりやすく提示する独立サービスです。',
        bullets: [
          'PDF・CSV形式で利用明細を定期配信し、手動ダウンロードの手間を削減',
          'ETCカードや車両ごとに記録をグループ化し、部門別・目的別の仕分けを支援',
          '通常と異なる利用パターンを強調表示し、管理者の早期確認を促進',
          '過去記録をアーカイブ保管し、数か月後の照会や確認対応に備える',
          '社内ポリシーとの照合を支援するが、税務・請求書・不正判定の最終判断は行わない',
        ],
      },
      pdfCsv: 'PDF記録は閲覧・共有に便利で、CSV記録は並べ替え・絞り込み・会計ソフト取り込みに適しています。両形式を併用することで、経理担当者は迅速に集計でき、管理者は必要な明細だけを抽出して確認できます。JTRはPDF・CSV両方で出力し、用途に応じた柔軟な活用を支援します。',
      email: 'JTRはETC明細をメール経由で定期配信し、ログイン不要で最新記録を受け取れます。添付されたPDF・CSVファイルをそのまま保管フォルダへ移動すれば、月次処理や年度監査の際にすぐ参照できます。配信タイミングや宛先は管理画面で調整可能です。',
      steps: [
        {
          t: '対象ルートと記録目的の確認',
          d: '九州自動車道はNEXCO西日本が管理する主要路線です。業務利用・個人利用を問わず、公式記録と社内記録を紐付けて保管する体制を整えましょう。',
        },
        {
          t: '公式料金情報の確認',
          d: '料金額・車種区分・割引適用・ETC専用レーンに関する疑問は、まずNEXCO西日本の料金検索または問い合わせ窓口で確認してください。',
        },
        {
          t: 'ETC利用照会サービスでの利用明細取得',
          d: '走行後の利用明細・利用証明書・PDF/CSV記録はETC利用照会サービスまたはカード会社明細で取得します。通常のETCカードは過去15か月分が対象です。',
        },
        {
          t: '走行記録の体系的な記録',
          d: '利用日・入口IC・出口IC・ETCカード・車両・車種区分・担当者/部署・利用目的を一貫した形式で記録し、公式データと社内メモを分けて管理してください。',
        },
        {
          t: 'PDF+CSV記録の保管と整理',
          d: 'PDF記録は閲覧・共有に便利で、CSV記録は並べ替え・フィルタ・会計システム取り込みに適しています。両方を保管すると後の確認作業が効率化されます。',
        },
        {
          t: '不明点や不一致の早期解決',
          d: '記録が不詳細・遅延・想定外の内容だった場合は推測せず、該当する道路事業者またはカード発行会社に問い合わせ、回答を走行記録とともに保管してください。',
        },
      ],
      commonProblems: [
        {
          q: '利用明細の金額が予想と違う',
          a: '公式記録で利用日・入口IC・出口IC・車種区分を再確認し、割引適用条件や時間帯割引の有無をNEXCO西日本の料金検索で照合してください。それでも不明な場合は問い合わせ窓口へ連絡します。',
        },
        {
          q: '記憶にない利用明細が表示される',
          a: 'カード番号・車両・利用日時を確認し、家族・同僚・レンタカー利用の可能性を検討してください。心当たりがなければカード発行会社に連絡し、公式記録を保管しておきます。',
        },
        {
          q: 'PDF記録とCSV記録のどちらを保存すべきか',
          a: '両方の保存を推奨します。PDFは閲覧・共有が容易で、CSVは並べ替え・フィルタ・会計ソフト取り込みに適しています。用途に応じて使い分けることで記録管理が効率化されます。',
        },
        {
          q: '経費精算や税務処理に利用できるか',
          a: '記録は経費精算や税務レビューの補助資料として利用できますが、最終的な取り扱いは勤務先の規定・税理士の助言・公式ガイダンスで確認してください。このガイドは税務助言ではありません。',
        },
      ],
      examples: [
        {
          who: '個人ドライバー',
          use: '九州自動車道の利用明細ガイドで公式記録の確認方法を理解し、経費精算申請前に必要な情報を整理して提出します。',
        },
        {
          who: '企業経理チーム',
          use: '月次でPDF+CSV記録を取得し、社内の車両台帳・部署別配分と照合して、利用明細が正確に分類されているか確認します。',
        },
        {
          who: '複数車両を持つ家族',
          use: 'ETCカード明細を参照して個人利用と業務利用を分離し、税理士に送付する前に記録を整理します。',
        },
        {
          who: '車両管理責任者',
          use: '見慣れない走行記録を発見した際、公式ETC利用明細を確認してから運転者に状況を問い合わせ、記録とともに保管します。',
        },
      ],
      faqs: [
        {
          q: 'JTRは公式の道路事業者ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式の路線・料金・割引・安全ルールは該当する道路事業者または公式ETCサービスで確認してください。JTRはETC利用記録の受信・整理・保管・レビューを支援します。',
        },
        {
          q: 'ETC利用照会サービスで道路事業者サイトを代替できますか?',
          a: 'いいえ。ETC利用照会サービスはETCカード利用明細と利用証明書の取得に有用ですが、最新の路線・料金・割引・レーン・車種区分ルールは道路事業者サイトで確認する必要があります。',
        },
        {
          q: 'PDF記録とCSV記録のどちらを保存すべきですか?',
          a: '両方が有用です。PDF記録は閲覧・共有が容易で、CSV記録は並べ替え・フィルタ・会計システムへの取り込みに適しています。用途に応じて使い分けてください。',
        },
        {
          q: '税務処理や経費精算に利用できますか?',
          a: '記録は税務レビューや経費精算の補助資料として利用できますが、最終的な取り扱いは勤務先規定・税理士の助言・公式ガイダンスで確認してください。このガイドは税務助言ではありません。',
        },
        {
          q: '金額や路線が間違っているように見える場合はどうすればよいですか?',
          a: '公式記録を確認し、カードと車両を照合し、利用日とIC情報を比較してください。それでも解決しない場合は該当する道路事業者またはカード発行会社に問い合わせてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録の受信・整理・保管・レビュー支援がどのように機能するかを確認できます。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数部署・複数カードを扱う企業向けに、記録の一元管理とレビュー体制を構築できます。',
          path: '/business',
        },
        {
          title: '無料トライアル',
          desc: '実際の記録管理フローを試し、PDF+CSV出力や整理機能を体験できます。',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用明細の確認、利用証明書の発行、PDF/CSV形式でのダウンロードが可能。通常のETCカードは過去15か月分が対象です。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別、利用証明書発行方法、通常ETCカード記録期間、ETCコーポレートカード記録期間、無線/非無線利用、PDF/CSVダウンロードについて解説しています。',
        },
        {
          label: 'NEXCO西日本 料金・ルート検索',
          url: 'https://search.w-nexco.co.jp/',
          note: 'NEXCO西日本が管理する路線の料金・ルート検索サービスです。',
        },
        {
          label: 'ETC総合情報ポータル: お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する公式問い合わせ窓口の一覧です。',
        },
        {
          label: 'JTR プライバシーポリシー',
          url: 'https://japantollreceipts.jp/privacy',
          note: 'JTRにおける記録の取り扱い方針を確認できます。',
        },
      ],
      longBody: '九州自動車道は西日本高速道路株式会社(NEXCO西日本)が管理する主要路線であり、商業輸送や個人移動の中核を担っています。ETC利用明細の適切な管理とは、公式な利用記録を保存し、正しい車両や部署と紐付け、通行料金や割引に関する疑問が生じた際にはNEXCO西日本の料金検索や顧客窓口を確認することを意味します。日本の通行料金記録管理は複数の独立したシステムで構成されています。道路事業者は路線情報、料金、割引、車種区分、安全案内を提供し、ETC利用照会サービスはETCカードの利用履歴や利用証明書を発行します。カード会社は月次明細を提供する場合があります。JTRは独立したサービスとして、利用者がETC利用記録を受け取り、整理し、保管し、見直す作業を支援しますが、公式な通行料金データを作成する主体ではなく、いかなる公式サービスの代替でもありません。地域別の通行料金記録は、その路線を管理する公式事業者と結び付けて保管すべきです。路線名だけでは請求された通行料金の根拠を証明できないためです。利用者は正確な利用日、入口IC、出口IC、車種区分、カード、最終請求額を保持する必要があります。実用的な記録とは、どのカードが課金されたか、どの車両が通行料金を発生させたか、どの路線またはIC区間を利用したか、なぜその走行が発生したか、公式証拠がどこに保管されているかという5つの基本的な質問に答えられるものです。これらの答えがなければ、数週間後や数ヶ月後に通行料金明細を説明することは困難になります。企業、官公庁、軍関係、レンタカー、家族利用では、公式利用明細、PDF出力、CSV出力、カード明細の文脈、社内メモを一緒に保管することが最も強固な実務です。これにより、払い戻し確認、運転者からの質問、部署割り当て、不正利用の可能性を調べる際の混乱が軽減されます。記録が不詳細、遅延、予想外、または不一致の可能性がある場合は、推測ではなく関連する公式事業者またはカード発行会社に問い合わせ、その回答を走行記録と共に保管してください。JTRはPDFおよびCSV記録を利用可能な状態で保持し、設定に応じてETCカードまたは車両ごとに通行料金をグループ化し、確認が必要な記録を強調表示し、内部方針に対して利用状況を比較する必要がある管理者を支援できます。通行料金記録が異常に見える場合、JTRは不正利用の可能性を示唆したり早期確認を支援したりするものとして説明されるべきであり、決定的に不正を検出したり法的判断を下したりするものとして説明されるべきではありません。税務、請求書、払い戻し処理については、JTRは記録整理を支援できますが、利用者は最終的な取り扱いを会計士、雇用主、機関の方針、または公式ガイダンスで確認すべきです。',
    },
    en: {
      title: 'Kyushu Expressway ETC Statement Guide | Japan Toll Receipts',
      metaDesc: "How to check, organize, and store Kyushu Expressway ETC statements. Covers official inquiry services, PDF/CSV records, JTR's role, and FAQs.",
      h1: 'Kyushu Expressway ETC Statement Guide',
      primaryKeyword: 'Kyushu Expressway ETC statement',
      answer: "To properly manage Kyushu Expressway ETC statements, confirm official ETC usage records through the ETC Utilization Inquiry Service and store each trip's date, entry IC, exit IC, card, vehicle, and toll as a set. Cross-check tolls and discounts using NEXCO West's official route search, and retain records in PDF and CSV formats to streamline expense reimbursement and internal verification.",
      whyMatters: 'The Kyushu Expressway is a core NEXCO West route with high usage by both business and personal drivers across Kyushu. Without proper ETC statement management, you cannot explain who drove when and why during later expense reviews or internal audits, creating risks of missing documentation and duplicate charges. By correctly obtaining and organizing official records and linking them to specific cards and vehicles, accounting staff and managers can verify quickly, detect unauthorized use early, and strengthen internal controls.',
      officialBackground: "Japan's toll record management relies on multiple independent systems working together. Road operators publish route, toll, discount, vehicle class, and safety information. The ETC Utilization Inquiry Service provides ETC card usage details, usage certificates, and PDF/CSV downloads (typically 15 months of history for standard ETC cards). Card issuers supply monthly statements. JTR is an independent service that helps you receive, organize, store, and review these official records—it is NOT NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC inquiry service, a card company, or a government agency. JTR does not create official toll data; it specializes in making existing official records easier to use. typically link regional records to the responsible operator and store date, IC, vehicle class, card, and actual billed amount accurately.",
      whoFor: [
        'Sales staff and delivery drivers who frequently use expressways within Kyushu',
        'Accounting and admin teams who collect and reconcile monthly ETC statements for company vehicles',
        'Logistics firms and vehicle managers operating multiple vehicles and multiple cards',
        'Individual users who want to separate ETC usage for rental cars or family-shared vehicles',
      ],
      jtrSolution: {
        intro: 'JTR transforms ETC usage records into practical verification workflows. It reduces manual searching, printing, renaming, and forwarding, and focuses on delivery, organization, storage, and review support. JTR is an independent service that does not create official records but presents existing ones clearly.',
        bullets: [
          'Delivers usage statements in PDF and CSV formats on a regular schedule, cutting manual download effort',
          'Groups records by ETC card or vehicle to support sorting by department or purpose',
          'Highlights usage patterns that differ from the norm to prompt early manager review',
          'Archives past records to support inquiries and audit response months later',
          'Assists in cross-checking with internal policies but does not make final tax, invoice, or fraud determinations',
        ],
      },
      pdfCsv: 'PDF records suit viewing and sharing, while CSV records support sorting, filtering, and importing into accounting software. Using both formats together lets accounting staff aggregate quickly and managers extract only the statements they need. JTR outputs both PDF and CSV to enable flexible use tailored to each task.',
      email: 'JTR delivers ETC statements via email on a regular schedule, so you receive the latest records without logging in. Simply move the attached PDF and CSV files into your archive folder, and you can reference them immediately during monthly processing or year-end audits. Delivery timing and recipients are adjustable in the admin dashboard.',
      steps: [
        {
          t: 'Confirm target route and record purpose',
          d: 'The Kyushu Expressway is a major route operated by NEXCO West Japan. Whether for business or personal use, establish a system to link and store official records with internal documentation.',
        },
        {
          t: 'Verify official toll information',
          d: "For questions about toll amounts, vehicle classifications, discount eligibility, or ETC-only lanes, first consult NEXCO West Japan's toll search or inquiry desk.",
        },
        {
          t: 'Obtain usage statements via ETC ETC Usage Inquiry Service (MEISAI) Service',
          d: 'Retrieve post-journey usage statements, usage certificates, and PDF/CSV records through the ETC ETC Usage Inquiry Service (MEISAI) Service or your card company statements. Standard ETC cards cover the past 15 months.',
        },
        {
          t: 'Record journey data systematically',
          d: 'Log usage date, entry IC, exit IC, ETC card, vehicle, classification, person/department, and purpose in a consistent format, keeping official data and internal notes separate.',
        },
        {
          t: 'Store and organize PDF + CSV records',
          d: 'PDF records suit viewing and sharing; CSV records enable sorting, filtering, and import into accounting systems. Storing both streamlines future verification work.',
        },
        {
          t: 'Resolve unclear or mismatched items early',
          d: 'If records are incomplete, delayed, or unexpected, do not guess. Contact the relevant road operator or card issuer, and store responses alongside journey records.',
        },
      ],
      commonProblems: [
        {
          q: 'Statement amount differs from expectation',
          a: "Re-check the usage date, entry IC, exit IC, and vehicle classification in the official record, then verify discount eligibility and time-based discount conditions using NEXCO West Japan's toll search. If still unclear, contact the inquiry desk.",
        },
        {
          q: 'Statement shows unrecognized journeys',
          a: 'Verify card number, vehicle, and date/time; consider the possibility of family members, colleagues, or rental car use. If still unknown, contact your card issuer and retain the official record.',
        },
        {
          q: 'Should I save PDF or CSV records?',
          a: 'We recommend saving both. PDF is convenient for viewing and sharing; CSV suits sorting, filtering, and import into accounting software. Using both according to purpose makes record management more efficient.',
        },
        {
          q: 'Can I use these for expense claims or tax purposes?',
          a: "Records may serve as supporting material for expense claims or tax review, but final treatment must be confirmed with your employer's rules, accountant's advice, or official guidance. This guide is not tax advice.",
        },
      ],
      examples: [
        {
          who: 'Individual driver',
          use: 'Uses the Kyushu Expressway usage-statement guide to understand how to verify official records and organize necessary information before submitting an expense claim.',
        },
        {
          who: 'Corporate accounting team',
          use: 'Retrieves PDF + CSV records monthly, cross-references them with internal vehicle logs and department allocations, and confirms usage statements are correctly classified.',
        },
        {
          who: 'Family with multiple vehicles',
          use: 'Consults ETC card statements to separate personal and business journeys, then organizes records before sending them to an accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'Upon finding unfamiliar journey records, checks the official ETC usage statement before inquiring with the driver, then stores the record and response together.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official road operator?',
          a: 'No. JTR is an independent service. For official route, toll, discount, and safety rules, consult the relevant road operator or official ETC services. JTR helps you receive, organize, store, and review ETC usage records.',
        },
        {
          q: 'Can the ETC ETC Usage Inquiry Service (MEISAI) Service replace road operator sites?',
          a: 'No. The ETC ETC Usage Inquiry Service (MEISAI) Service is useful for obtaining ETC card usage statements and usage certificates, but the latest route, toll, discount, lane, and vehicle classification rules must be confirmed on road operator sites.',
        },
        {
          q: 'Should I save PDF or CSV records?',
          a: 'Both are useful. PDF records are easy to view and share; CSV records suit sorting, filtering, and import into accounting systems. Use each according to purpose.',
        },
        {
          q: 'Can I use these for tax or expense claims?',
          a: "Records may serve as supporting material for tax review or expense claims, but final treatment must be confirmed with your employer's rules, accountant's advice, or official guidance. This guide is not tax advice.",
        },
        {
          q: 'What if amounts or routes appear incorrect?',
          a: 'Check the official record, verify card and vehicle, and compare usage date and IC information. If still unresolved, contact the relevant road operator or card issuer.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: 'See how ETC usage record receiving, organizing, storage, and review support functions.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'For companies handling multiple vehicles, departments, and cards, build centralized record management and review systems.',
          path: '/business',
        },
        {
          title: 'Free Trial',
          desc: 'Try the actual record management workflow and experience PDF + CSV export and organization features.',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC ETC Usage Inquiry Service (MEISAI) Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Check ETC card usage statements, issue usage certificates, and download in PDF/CSV format. Standard ETC cards cover the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC ETC Usage Inquiry Service (MEISAI) Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, usage certificate issuance methods, standard ETC card record periods, ETC Corporate Card record periods, wireless/non-wireless usage, and PDF/CSV downloads.',
        },
        {
          label: 'NEXCO West Japan Toll & Route Search',
          url: 'https://search.w-nexco.co.jp/',
          note: 'Toll and route search service for routes operated by NEXCO West Japan.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'List of official inquiry desks for tolls, discounts, and ETC usage.',
        },
        {
          label: 'JTR Privacy Policy',
          url: 'https://japantollreceipts.jp/privacy',
          note: "Review JTR's record-handling policy.",
        },
      ],
      longBody: "The Kyushu Expressway is a principal route managed by West Nippon Expressway Company Limited (NEXCO West Japan), serving as a backbone for commercial transport and personal travel. Proper management of ETC transaction details means preserving official usage records, linking them to the correct vehicle or department, and consulting NEXCO West Japan's toll search or customer service when questions arise about charges or discounts. Toll record management in Japan is composed of multiple independent systems. Road operators provide route information, fares, discounts, vehicle classifications, and safety guidance. The ETC Usage Inquiry Service issues card transaction histories and usage certificates. Card issuers may provide monthly statements. JTR operates as a separate service that helps users receive, organise, store, and review their ETC transaction records, but it does not generate official toll data and is not a substitute for any official service. Regional toll records should be stored in association with the official operator managing that route, since route names alone cannot substantiate the basis for a charged fare. Users need to retain the accurate usage date, entry interchange, exit interchange, vehicle class, card, and final billed amount. A practical record is one that can answer five fundamental questions: which card was charged, which vehicle incurred the toll, which route or interchange segment was used, why the journey occurred, and where the official evidence is kept. Without these answers, explaining toll details weeks or months later becomes difficult. For corporate, government, public-sector, rental, and family use, the strongest practice is to store official usage statements, PDF exports, CSV exports, card statement context, and internal notes together. This reduces confusion when verifying reimbursements, responding to driver questions, assigning departmental costs, or investigating possible misuse. When records may be incomplete, delayed, unexpected, or inconsistent, contact the relevant official operator or card issuer rather than guessing, and keep their reply with the travel documentation. JTR can keep PDF and CSV records accessible, group tolls by ETC card or vehicle according to settings, highlight records requiring review, and assist administrators who need to compare usage against internal policy. When toll records appear unusual, JTR should be described as something that may suggest possible misuse or support early review, not as something that definitively flags usage for review or renders legal judgments. For tax, invoicing, and reimbursement processes, JTR can assist with record organisation, but users should confirm final handling with accountants, employers, institutional policy, or official guidance.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '九州自動車道ETC明細の確認フロー',
            intro: '利用記録を公式取得→整理→保管→確認する4ステップ',
            nodes: [
              {
                title: 'ETC利用照会サービスにログイン',
                desc: '過去15か月分の利用明細をPDF・CSV形式で取得可能',
              },
              {
                title: '利用日・IC・カード情報を照合',
                desc: '入口IC・出口IC・車両・料金を1レコードとして確認',
              },
              {
                title: 'PDF・CSV形式で記録を保管',
                desc: 'カードや車両ごとにグループ化し、部門別に仕分け',
              },
              {
                title: '月次集計・経費精算に活用',
                desc: '会計ソフトへCSV取込、管理者承認、確認対応まで対応',
              },
            ],
          },
          en: {
            title: 'Kyushu Expressway ETC Record Flow',
            intro: '4 steps: retrieve official records → organize → store → verify',
            nodes: [
              {
                title: 'Log into ETC Usage Query Service',
                desc: 'Download up to 15 months of transaction history in PDF/CSV',
              },
              {
                title: 'Cross-check dates, ICs & card info',
                desc: 'Verify entry IC, exit IC, vehicle, and toll as one record',
              },
              {
                title: 'Store records in PDF & CSV formats',
                desc: 'Group by card or vehicle; sort by department or purpose',
              },
              {
                title: 'Use for monthly summaries & expense claims',
                desc: 'Import CSV into accounting software; support manager approval & audits',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '九州自動車道ETC明細の公式情報源',
            intro: '記録取得・料金照合・カード明細の公式窓口を確認',
            sources: [
              {
                name: 'ETC利用照会サービス',
                role: 'ETC利用明細・利用証明書のPDF/CSVダウンロード提供',
                url: 'www.etc-meisai.jp',
                scope: '過去15か月分の利用記録',
              },
              {
                name: 'NEXCO西日本ドラぷら',
                role: '料金・ルート検索、割引制度、路線情報の公開',
                url: 'www.w-nexco.co.jp',
                scope: '西日本高速全路線',
              },
              {
                name: 'カード発行会社',
                role: '月次利用明細・請求額の提供、支払口座管理',
                url: '各社公式サイト',
                scope: 'カードごとの請求履歴',
              },
              {
                name: 'JTR (Japan Toll Receipts)',
                role: '公式記録の受取・整理・配信・保管支援サービス',
                url: 'japantollreceipts.jp',
                scope: 'データ作成は行わず既存記録を活用',
              },
            ],
          },
          en: {
            title: 'Official Sources for Kyushu Expressway ETC Records',
            intro: 'Where to retrieve records, check tolls, and access card statements',
            sources: [
              {
                name: 'ETC Usage Query Service',
                role: 'Provides ETC usage details & certificates in PDF/CSV',
                url: 'www.etc-meisai.jp',
                scope: 'Up to 15 months of transaction history',
              },
              {
                name: 'NEXCO West DRIVE PLAZA',
                role: 'Route & toll search, discount schemes, road information',
                url: 'www.w-nexco.co.jp',
                scope: 'All NEXCO West routes',
              },
              {
                name: 'Card Issuer',
                role: 'Monthly statements, billing amounts, payment account management',
                url: 'Issuer official site',
                scope: 'Per-card billing history',
              },
              {
                name: 'JTR (Japan Toll Receipts)',
                role: 'Receiving, organizing, distributing & archiving official records',
                url: 'japantollreceipts.jp',
                scope: 'Does not create data; leverages existing official records',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF記録 vs CSV記録の使い分け',
            intro: '用途に応じて両形式を併用すると効率的',
            columns: [
              'PDF形式',
              'CSV形式',
            ],
            rows: [
              {
                feature: '閲覧・印刷のしやすさ',
                a: 'レイアウト固定で共有に便利',
                b: 'テキストのみ、加工前提',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '並べ替え・絞り込み',
                a: 'ページ単位、検索は可能',
                b: 'Excelで自由に集計・フィルタ可能',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '会計ソフト取り込み',
                a: '手入力が必要',
                b: '直接インポート可能',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '長期保管・確認対応',
                a: 'フォーマット安定、視認性高い',
                b: 'データ量少、検索・再集計に有利',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'PDF Records vs CSV Records',
            intro: 'Use both formats to match different tasks efficiently',
            columns: [
              'PDF Format',
              'CSV Format',
            ],
            rows: [
              {
                feature: 'Ease of viewing & printing',
                a: 'Fixed layout, easy to share',
                b: 'Text only; designed for processing',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Sorting & filtering',
                a: 'Page-based; searchable',
                b: 'Freely sort, filter, and summarize in Excel',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Import into accounting software',
                a: 'Requires manual entry',
                b: 'Direct CSV import supported',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Long-term storage & audits',
                a: 'Stable format, high readability',
                b: 'Smaller file size, easy to search & re-aggregate',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '月次ETC明細確認チェックリスト',
            intro: '毎月の締め処理で必ず確認すべき6項目',
            items: [
              {
                text: '全カードの利用明細をETC利用照会サービスから取得',
                hint: '過去15か月分まで遡れるため、月初に前月分を一括ダウンロード',
              },
              {
                text: '利用日・入口IC・出口IC・料金の整合性を確認',
                hint: 'NEXCO西日本ルート検索で公式料金と照合、割引適用も確認',
              },
              {
                text: 'カード会社請求額とETC利用合計額を突合',
                hint: '他の支払いと混在する場合は内訳を分離して確認',
              },
              {
                text: '車両・部門・目的別にCSVを仕分け・集計',
                hint: 'Excelピボットテーブルや会計ソフト取り込みで効率化',
              },
              {
                text: '通常と異なる利用パターン（深夜・休日・遠方IC）を抽出',
                hint: '不正利用や誤利用の早期発見、承認フロー確認に活用',
              },
              {
                text: 'PDF・CSV両形式で月次フォルダへ保管・バックアップ',
                hint: '確認対応や数か月後の照会に備え、命名規則を統一',
              },
            ],
          },
          en: {
            title: 'Monthly ETC Record Review Checklist',
            intro: '6 essential checks for monthly closing',
            items: [
              {
                text: 'Retrieve all card records from ETC Usage Query Service',
                hint: 'Up to 15 months available; batch-download previous month at month-start',
              },
              {
                text: 'Verify usage date, entry IC, exit IC, and toll amounts',
                hint: 'Cross-check official tolls via NEXCO West route search; confirm discounts',
              },
              {
                text: 'Reconcile card issuer billing with ETC usage totals',
                hint: 'Separate ETC charges from other transactions in the statement',
              },
              {
                text: 'Sort & summarize CSV by vehicle, department, purpose',
                hint: 'Use Excel pivot tables or import into accounting software',
              },
              {
                text: 'Flag unusual patterns (late-night, weekend, distant ICs)',
                hint: 'Spot fraud or errors early; verify against approval workflows',
              },
              {
                text: 'Archive both PDF & CSV in monthly folders with backups',
                hint: 'Prepare for audits and future inquiries; use consistent naming conventions',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'tohoku-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '東北道ETC明細',
      en: 'Tōhoku Expressway ETC records',
    },
    ja: {
      title: '東北道ETC明細の確認と保管ガイド | Japan Toll Receipts',
      metaDesc: 'NEXCO東日本が管理する東北自動車道のETC利用明細の確認方法を実務目線で解説。ETC利用照会サービス（過去15か月／コーポレートカードは62日）でPDF利用証明書・CSV明細を取得する手順、IC・JCT別の整理、出張・営業車の経費精算、独立サービスJTRの日次配信併用方法まで。',
      h1: '東北道ETC明細の確認と保管ガイド',
      primaryKeyword: '東北道ETC明細',
      answer: '東北自動車道のETC利用記録を適切に管理するには、通行日・入口IC・出口IC・ETCカード・車両・請求額を保管し、ETC利用照会サービスで公式記録を確認、NEXCO東日本で料金・割引情報を確認します。JTRは受取・整理・保管・確認業務を支援する独立サービスです。',
      whyMatters: '東北道は主要なNEXCO東日本路線であり、業務・家族・レンタカー利用では通行記録の正確な保管が重要です。カード明細だけでは経路・車両・理由が不明確になり、数週間後の説明や経費精算が困難になります。公式記録と社内メモを組み合わせた記録管理により、精算・配分・問い合わせ対応がスムーズになり、不明瞭な通行の早期発見も可能です。',
      officialBackground: '東北自動車道はNEXCO東日本が管理する主要高速道路です。ETC利用者は通行日・入口IC・出口IC・カード・車両・請求額・割引を記録として保管する必要があります。公式情報源は複数に分かれており、NEXCO東日本とドライブプラザは経路・料金・割引の公式情報源、ETC利用照会サービスはETCカード利用明細と利用証明書の公式照会サービスです。カード会社は月次明細を提供します。JTRはこれら公式記録の受取・整理・保管・確認を支援する独立サービスであり、公式通行データを作成せず、公式サービスの代替ではありません。適切な記録にはカード・車両・経路・目的・公式証拠の保管場所の5要素が必要です。',
      whoFor: [
        '東北道を利用する業務ドライバー',
        '複数車両のETC利用を管理する経理担当者',
        '家族用と業務用の通行を分ける必要がある利用者',
        'レンタカーや社用車のETC記録を精算する管理者',
      ],
      jtrSolution: {
        intro: 'JTRはETC利用記録を実務的な確認業務に変えます。手作業での検索・印刷・改名・転送ではなく、配信・整理・保管・確認支援に焦点を当てた独立サービスです。',
        bullets: [
          'PDF・CSV形式でETC記録を保管し、後日の確認と比較を容易にします',
          '設定に応じてカード別・車両別に通行を分類し、部署配分を支援します',
          '確認が必要な記録を強調表示し、早期レビューを促します',
          '社内ルールとの照合が必要な管理者の業務を支援します',
          '不明瞭な通行を検出可能性として示し、管理者判断を補助します',
        ],
      },
      pdfCsv: 'JTRはPDF形式とCSV形式でETC利用記録を出力します。PDF は確認・共有に適し、CSVは並べ替え・フィルタ・会計システム取込に適しています。両方を保管することで柔軟な業務対応が可能です。',
      email: 'JTRは設定に応じてETC利用記録をメール配信します。定期配信により手作業ログインが不要になり、記録の受取漏れを減らし、チーム全体での確認業務を円滑にします。配信頻度と宛先は利用者が管理します。',
      steps: [
        {
          t: '対象となる東北道ETC利用を特定する',
          d: '東北自動車道はNEXCO東日本の主要路線です。ETC利用者は日付・入口IC・出口IC・ETCカード・車両・請求額・適用割引を記録として保存します。',
        },
        {
          t: '公式の料金事業者情報を先に確認する',
          d: 'ルート・料金額・車種区分・割引適用条件・ETC専用ゲート・道路固有のルールに関する質問は、まず公式の料金事業者サイトで確認します。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細で利用記録を取得する',
          d: '走行後のETC利用明細・利用証明書・PDF記録・CSV記録は、ETC利用照会サービスまたは適切なカード発行会社の明細で確認します。',
        },
        {
          t: '走行日・IC・カード・車両・担当者・理由を統一形式で記録する',
          d: '走行日・入口IC・出口IC・ETCカード・車両・車種区分・運転者または部署・走行理由を、社内の統一フォーマットで記録します。',
        },
        {
          t: '公式データと社内説明を分けて保管する',
          d: '公式記録は通行実績を示し、会社・家族・組織のメモは精算目的や承認理由を説明します。両者を明確に区別して保管します。',
        },
        {
          t: '不明・遅延・疑問がある記録は公式窓口に確認する',
          d: '記録が不詳細・遅延・予想外・不一致の可能性がある場合は、推測せず関連する公式事業者またはカード発行会社に連絡し、回答を記録と一緒に保管します。',
        },
      ],
      commonProblems: [
        {
          q: '東北道のETC明細が見つからない',
          a: 'ETC利用照会サービスで対象カード・期間・車両を確認してください。通常のETCカードは過去15か月分が閲覧可能です。記録が見当たらない場合はカード発行会社に問い合わせます。',
        },
        {
          q: '料金額が予想と異なる',
          a: '公式記録で走行日・入口IC・出口IC・車種区分・適用割引を確認します。深夜割引や休日割引の適用条件はNEXCO東日本の公式サイトで確認してください。',
        },
        {
          q: '複数車両の利用記録を分けて管理したい',
          a: 'JTRはETCカードまたは車両ごとに記録を整理できます。CSV出力を利用すれば車両番号や部署で絞り込み、並び替えが可能です。',
        },
        {
          q: '精算や税務処理にこの記録を使えるか',
          a: '記録は精算や税務確認の参考になりますが、最終的な取り扱いは勤務先の方針・会計士の助言・公式ガイダンスで確認してください。このガイドは税務助言ではありません。',
        },
      ],
      examples: [
        {
          who: 'ドライバー',
          use: '東北道ETC明細ガイドを使い、通行料金精算請求の前にどの公式情報源を確認すべきかを把握します。',
        },
        {
          who: '企業経理チーム',
          use: '1か月分のETC利用記録をPDF+CSV形式で出力し、社内の車両・部署割り当てと照合します。',
        },
        {
          who: '複数車両を持つ家族',
          use: 'ETCカード記録を使って個人利用と業務関連の通行を分け、会計士に情報を送る前に整理します。',
        },
        {
          who: '車両管理者',
          use: '見覚えのない走行を確認対象として扱い、運転者に説明を求める前に公式ETC利用記録を確認します。',
        },
      ],
      faqs: [
        {
          q: 'JTRはこのテーマの公式料金道路情報源ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式のルート・料金・割引・設定・安全ルールは関連する料金事業者または公式ETCサービスで確認してください。JTRはETC利用記録の受信・整理・保管・確認をサポートします。',
        },
        {
          q: 'ETC利用照会サービスは料金事業者サイトの代わりになりますか?',
          a: 'いいえ。ETC利用照会サービスはETCカード利用明細と利用証明書の確認に便利ですが、最新のルート・料金・割引・レーン・車種区分のルールは料金事業者サイトで確認する必要があります。',
        },
        {
          q: 'PDFとCSV、どちらを保存すべきですか?',
          a: '両方が便利です。PDF記録は確認・共有が簡単で、CSV記録は並び替え・絞り込み・会計または社内確認ワークフローへのインポートに適しています。',
        },
        {
          q: '金額やルートが間違っているように見える場合は?',
          a: '公式記録を確認し、カードと車両を照合し、走行日とIC情報を比較してから、問題が解決しない場合は適切な料金事業者またはカード発行会社に連絡してください。',
        },
        {
          q: '東北道ETC明細ガイドで最も注意すべきリスクは?',
          a: '記憶・スクリーンショット・不詳細な明細に頼り、公式ETC利用記録と社内業務の文脈を一緒に保管しないことが主なリスクです。',
        },
      ],
      relatedFeatures: [
        {
          title: '料金記録の整理と保管',
          desc: 'JTRはPDF+CSV形式で記録を配信し、ETCカードや車両ごとに整理して、確認が必要な記録を強調表示します。',
          path: '/how-it-works',
        },
        {
          title: 'ビジネス向け記録管理',
          desc: '企業・政府・車両管理者向けに、複数カード・複数車両の利用記録を一元管理し、部署別・プロジェクト別に整理できます。',
          path: '/business',
        },
        {
          title: '無料トライアル',
          desc: 'JTRの記録配信・整理・確認機能を無料で試し、東北道を含む全国の高速道路ETC利用記録の管理方法を体験できます。',
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用の公式照会サービス。利用証明書の発行、PDF・CSV形式での明細ダウンロードが可能。通常ETCカードは過去15か月分が閲覧可能。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別、利用証明書発行、通常ETCカード記録期間、ETCコーポレートカード記録期間、無線・非無線ETC利用、PDF・CSVダウンロードについて説明。',
        },
        {
          label: 'NEXCO東日本 ドラぷら',
          url: 'https://www.driveplaza.com/',
          note: 'NEXCO東日本の道路・交通・料金・ルート情報ポータル。',
        },
        {
          label: 'ドラぷら 料金・ルート検索',
          url: 'https://www.driveplaza.com/dp/SearchTop',
          note: 'NEXCO高速道路および関連ルートの料金・ルート検索。',
        },
        {
          label: 'ETC総合情報ポータル: お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する公式料金事業者カスタマーセンター一覧。',
        },
      ],
      longBody: '東北自動車道は、NEXCO東日本が管理する国内最長クラスの高速道路であり、首都圏と東北地方を結ぶ物流・移動の大動脈として機能しています。ETC利用者にとって、この路線の通行記録を正確に保管し、必要に応じて照会・整理できる体制を整えることは、企業の経費精算、レンタカー利用後の確認、複数車両を持つ家庭の記録管理、そして米軍関係者を含む業務利用者の会計処理において重要な実務課題となります。通行記録は単に「いくら請求されたか」を知るだけでなく、いつ・どのETCカードで・どの車両が・どの区間を利用し・どの割引が適用されたかを後日説明できる形で残す必要があります。NEXCO東日本およびドライブプラザは路線・料金・割引制度・車両区分に関する公式情報源であり、ETC利用照会サービスはETCカード単位の利用明細・利用証明書発行・PDF/CSVダウンロードを提供する公式照会窓口です。カード発行会社は月次請求書を提供しますが、インターチェンジ単位の詳細は含まれないことが多く、利用証明書や照会サービスの出力と組み合わせて初めて詳細な記録セットが構成されます。実務上よく見られる失敗例としては、スクリーンショットや記憶だけに頼る、利用日・入口IC・出口ICを記録せずカード明細の合計額だけを保存する、車両とカードの対応を曖昧にしたまま複数台を運用する、割引適用の有無を確認せず想定外の請求額に気づかない、といったケースが挙げられます。こうした不備は数週間後の経費精算や確認対応で混乱を招き、公式記録の再取得期限を過ぎてしまうリスクもあります。ETC利用照会サービスでは通常のETCカードは過去15か月分、ETCコーポレートカードは過去62か月分の記録が照会可能ですが、照会可能期間を過ぎた記録は原則として再発行されないため、定期的なダウンロードと保管が推奨されます。JTRは独立したサービスとして、公式記録の受領・整理・保管・レビュー支援を提供しますが、公式な通行料金データを作成する主体ではなく、NEXCO・MEISAI・ETC利用照会サービス・カード発行会社・政府機関のいずれでもありません。JTRはPDF+CSV形式で記録を提供し、ETCカードまたは車両ごとにグループ化し、レビューが必要な記録を強調表示することで、管理者が社内ポリシーと照合しやすくする役割を担います。記録が不自然に見える場合、JTRは可能性のある不正使用を示唆し早期レビューを支援しますが、法的判断や確定的な異常検知（要確認）を行うものではありません。税務・インボイス・経費精算の最終判断は、会計士・雇用主・所属機関の方針または公式ガイダンスで確認する必要があります。トラブルシューティングの基本は、まず公式記録を確認し、カード・車両・日付・IC情報を照合し、それでも解決しない場合は該当する料金所運営者またはカード発行会社に問い合わせることです。',
    },
    en: {
      title: 'Tōhoku Expressway ETC Record Verification & Storage Guide | JTR',
      metaDesc: "How to verify, organize, and store Tōhoku Expressway ETC usage records. Official inquiry services, PDF/CSV exports, JTR's role, FAQs, and key points.",
      h1: 'Tōhoku Expressway ETC Record Verification & Storage Guide',
      primaryKeyword: 'Tōhoku Expressway ETC records',
      answer: 'To manage Tōhoku Expressway ETC usage records properly, retain travel date, entry/exit IC, ETC card, vehicle, and billed amount; verify official records via ETC Meisai Inquiry Service; and check toll and discount information at NEXCO East. JTR is an independent service supporting receipt, organization, storage, and review workflows.',
      whyMatters: 'The Tōhoku Expressway is a major NEXCO East route; accurate record retention is critical for business, family, and rental-car usage. Card statements alone lack route, vehicle, and purpose detail, making explanation and expense settlement difficult weeks later. Combining official records with internal notes streamlines settlement, allocation, and inquiry responses, and enables early detection of unclear trips.',
      officialBackground: 'The Tōhoku Expressway is managed by NEXCO East Japan. ETC users should retain travel date, entry/exit IC, card, vehicle, billed amount, and discount as records. Official sources are distributed: NEXCO East and Drive Plaza provide route, toll, and discount information; ETC Meisai Inquiry Service offers ETC card usage statements and proof-of-use certificates; card issuers provide monthly statements. JTR is an independent service supporting receipt, organization, storage, and review of these official records—it does not generate official travel data and is not a substitute for official services. Proper records require five elements: card, vehicle, route, purpose, and location of official evidence.',
      whoFor: [
        'Business drivers using the Tōhoku Expressway',
        'Accounting staff managing multi-vehicle ETC usage',
        'Users needing to separate personal and business travel',
        'Managers settling ETC records for rental or company cars',
      ],
      jtrSolution: {
        intro: 'JTR transforms ETC usage records into practical review workflows. An independent service focused on delivery, organization, storage, and review support—not manual search, print, rename, and forward tasks.',
        bullets: [
          'Store ETC records in PDF and CSV formats for easier later verification and comparison',
          'Classify trips by card or vehicle according to settings, supporting department allocation',
          'Highlight records needing review and encourage early examination',
          'Support managers who must cross-check against internal rules',
          'Flag unclear trips as detection possibilities to assist manager judgment',
        ],
      },
      pdfCsv: 'JTR outputs ETC usage records in PDF and CSV formats. PDF suits verification and sharing; CSV suits sorting, filtering, and accounting-system import. Retaining both enables flexible operational response.',
      email: 'JTR delivers ETC usage records by email according to settings. Scheduled delivery eliminates manual login, reduces missed receipts, and smooths team-wide review workflows. Users control delivery frequency and recipients.',
      steps: [
        {
          t: 'Identify the target Tohoku Expressway ETC transaction',
          d: 'The Tohoku Expressway is a major NEXCO East Japan route. ETC users should record date, entry IC, exit IC, ETC card, vehicle, charged amount, and applied discount for each transaction.',
        },
        {
          t: 'Check official toll operator information first',
          d: 'For questions about routes, toll amounts, vehicle class, discount eligibility, ETC-only gates, or road-specific rules, consult the official toll operator site first.',
        },
        {
          t: 'Obtain transaction records via ETC Inquiry Service or card statement',
          d: 'For post-trip ETC statements, usage certificates, PDF records, or CSV records, check the ETC Inquiry Service or the appropriate card issuer statement.',
        },
        {
          t: 'Record trip date, IC, card, vehicle, person, and reason in unified format',
          d: "Record trip date, entry IC, exit IC, ETC card, vehicle, vehicle class, driver or department, and trip purpose using your organization's unified format.",
        },
        {
          t: 'Store official data and internal notes separately',
          d: 'Official records show actual road usage; company, family, or organization notes explain reimbursement purpose or approval reasons. Keep both categories clearly separated.',
        },
        {
          t: 'Contact official channels for unclear, delayed, or questionable records',
          d: 'If records are incomplete, delayed, unexpected, or potentially mismatched, do not guess—contact the relevant official operator or card issuer and store the response with the record.',
        },
      ],
      commonProblems: [
        {
          q: 'Cannot find Tohoku Expressway ETC statement',
          a: 'Check the ETC Inquiry Service for the target card, period, and vehicle. Standard ETC cards allow viewing up to 15 months of past records. If records are missing, contact your card issuer.',
        },
        {
          q: 'Toll amount differs from expectation',
          a: 'Verify trip date, entry IC, exit IC, vehicle class, and applied discount in the official record. Confirm late-night or holiday discount eligibility conditions on the NEXCO East Japan official site.',
        },
        {
          q: 'Want to manage records for multiple vehicles separately',
          a: 'JTR can organize records by ETC card or vehicle. Using CSV output, you can filter and sort by vehicle number or department assignment.',
        },
        {
          q: 'Can I use this record for reimbursement or tax processing?',
          a: "Records support reimbursement and tax review, but final handling should be confirmed with your employer's policy, accountant's advice, or official guidance. This guide is not tax advice.",
        },
      ],
      examples: [
        {
          who: 'Driver',
          use: 'Uses the Tohoku Expressway ETC statement guide to understand which official sources to check before submitting a toll reimbursement request.',
        },
        {
          who: 'Corporate accounting team',
          use: 'Exports one month of ETC transaction records in PDF + CSV format and cross-references them with internal vehicle and department assignments.',
        },
        {
          who: 'Family with multiple vehicles',
          use: 'Uses ETC card records to separate personal trips from business-related trips and organizes information before sending it to their accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'Treats unfamiliar trips as items requiring confirmation, checks official ETC transaction records before asking drivers for explanations.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR the official toll road information source for this topic?',
          a: 'No. JTR is an independent service. For official routes, tolls, discounts, settings, and safety rules, consult the relevant toll operator or official ETC service. JTR helps receive, organize, store, and review ETC transaction records.',
        },
        {
          q: 'Does the ETC Inquiry Service replace the toll operator site?',
          a: 'No. The ETC Inquiry Service is convenient for checking ETC card statements and usage certificates, but current route, toll, discount, lane, and vehicle class rules must be verified on the toll operator site.',
        },
        {
          q: 'Should I save PDF or CSV?',
          a: 'Both are useful. PDF records are easy to review and share; CSV records are ideal for sorting, filtering, and importing into accounting or internal review workflows.',
        },
        {
          q: 'What if an amount or route appears incorrect?',
          a: 'Verify the official record, match the card and vehicle, compare trip date and IC information, then contact the appropriate toll operator or card issuer if the issue persists.',
        },
        {
          q: 'What is the biggest risk with the Tohoku Expressway ETC statement guide?',
          a: 'Relying on memory, screenshots, or incomplete statements and failing to store official ETC transaction records together with internal business context is the main risk.',
        },
      ],
      relatedFeatures: [
        {
          title: 'Toll record organization and storage',
          desc: 'JTR delivers records in PDF + CSV format, organizes by ETC card or vehicle, and highlights records requiring review.',
          path: '/how-it-works',
        },
        {
          title: 'Business record management',
          desc: 'For companies, government, and vehicle managers, centralize multi-card, multi-vehicle transaction records and organize by department or project.',
          path: '/business',
        },
        {
          title: 'Free trial',
          desc: "Try JTR's record delivery, organization, and review features for free and experience managing nationwide expressway ETC transaction records including Tohoku Expressway.",
          path: '/free-trial',
        },
      ],
      references: [
        {
          label: 'ETC Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official inquiry service for ETC card usage. Issue usage certificates, download statements in PDF and CSV formats. Standard ETC cards allow viewing up to 15 months of past records.',
        },
        {
          label: 'ETC General Information Portal: ETC Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, usage certificate issuance, standard ETC card record period, ETC corporate card record period, wireless/non-wireless ETC usage, PDF and CSV download.',
        },
        {
          label: 'NEXCO East Japan DRIVE PLAZA',
          url: 'https://www.driveplaza.com/',
          note: 'NEXCO East Japan portal for road, traffic, toll, and route information.',
        },
        {
          label: 'DRIVE PLAZA Toll & Route Search',
          url: 'https://www.driveplaza.com/dp/SearchTop',
          note: 'Toll and route search for NEXCO expressways and related routes.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'Official toll operator customer center list for toll, discount, and ETC usage inquiries.',
        },
      ],
      longBody: "The Tōhoku Expressway, managed by NEXCO East, is one of Japan's longest toll routes and serves as a critical artery linking the capital region with the Tōhoku area for logistics and travel. For ETC users, maintaining accurate transit records and being able to query and organize them as needed is an important operational task for corporate expense claims, post-rental-car reconciliation, household management of multiple vehicles, and accounting processes for business users including U.S. military personnel. Transit records serve not only to confirm how much was billed, but to document—in a form that can be explained later—when, with which ETC card, which vehicle, which section, and which discount was applied. NEXCO East and its Drive Plaza portal are the authoritative sources for route, fare, discount scheme, and vehicle-class information, while the ETC Usage Inquiry Service is the official portal offering per-card usage detail, proof-of-use certificate issuance, and PDF/CSV downloads. Card issuers provide monthly statements, but these often omit interchange-level detail; only by combining them with certificates and inquiry-service output can a in-depth record set be assembled. Common operational failures include relying solely on screenshots or memory, saving only the statement total without recording usage date, entry IC, or exit IC, running multiple vehicles without clearly associating cards, and failing to verify discount application and noticing unexpected charges too late. Such gaps create confusion weeks later during expense processing or audit response and risk missing the official re-issuance window. The ETC Usage Inquiry Service retains records for 15 months for standard ETC cards and 62 months for ETC Corporate Cards, but records beyond these windows are generally not reissued, so periodic download and storage are recommended. JTR operates as an independent service offering receipt intake, organization, storage, and review support; it does not create official toll-charge data and is neither NEXCO, MEISAI, the ETC inquiry service, a card issuer, nor a government agency. JTR delivers records in PDF plus CSV format, groups them by ETC card or vehicle, and highlights entries requiring review to help administrators cross-check against internal policies. When records appear unusual, JTR may flag possible misuse and facilitate early review, but it does not render legal judgments or perform definitive fraud detection. Final determinations on tax, invoice, or expense eligibility rest with accountants, employers, institutional policy, or official guidance. The foundational troubleshooting steps are to check the official records first, cross-reference card, vehicle, date, and IC data, and if unresolved contact the relevant toll-plaza operator or card issuer.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '東北道ETC記録の流れ',
            intro: '通行から記録保管までの4段階プロセス',
            nodes: [
              {
                title: '通行',
                desc: 'ETCゲート通過時に入口IC・出口IC・時刻・車両を自動記録',
              },
              {
                title: 'カード請求',
                desc: 'カード会社が月次明細で通行料金を請求',
              },
              {
                title: '公式照会',
                desc: 'ETC利用照会サービスで経路・割引・車両詳細を確認',
              },
              {
                title: 'JTR保管',
                desc: 'PDF・CSV形式で記録を整理し、業務精算に活用',
              },
            ],
          },
          en: {
            title: 'Tohoku Expressway ETC Record Flow',
            intro: '4-stage process from transit to record retention',
            nodes: [
              {
                title: 'Transit',
                desc: 'ETC gate auto-records entry IC, exit IC, time, and vehicle',
              },
              {
                title: 'Card Billing',
                desc: 'Card issuer charges toll fees on monthly statement',
              },
              {
                title: 'Official Inquiry',
                desc: 'ETC Riyō Shōkai Service shows route, discounts, vehicle details',
              },
              {
                title: 'JTR Storage',
                desc: 'Organize records in PDF/CSV for business reimbursement',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '東北道ETC記録の公式情報源',
            intro: '目的別に使い分ける4つの公式サービス',
            sources: [
              {
                name: 'NEXCO東日本',
                role: '料金・経路・割引制度の公式管理者',
                url: 'e-nexco.co.jp',
                scope: '通行料金検索・IC情報・割引制度',
              },
              {
                name: 'ETC利用照会サービス',
                role: '通行記録の公式照会窓口',
                url: 'etc-meisai.jp',
                scope: 'カード別利用明細・利用証明書発行',
              },
              {
                name: 'カード会社',
                role: '請求額・支払記録の管理',
                url: '各社サイト',
                scope: '月次明細・支払履歴・残高',
              },
              {
                name: 'JTR',
                role: '記録の受取・整理・保管支援',
                url: 'japantollreceipts.jp',
                scope: 'PDF/CSV配信・カード/車両別分類・確認支援',
              },
            ],
          },
          en: {
            title: 'Official Sources for Tohoku ETC Records',
            intro: '4 official services for different purposes',
            sources: [
              {
                name: 'NEXCO East',
                role: 'Official toll/route/discount authority',
                url: 'e-nexco.co.jp',
                scope: 'Toll lookup, IC info, discount programs',
              },
              {
                name: 'ETC Riyō Shōkai Service',
                role: 'Official transit record inquiry',
                url: 'etc-meisai.jp',
                scope: 'Card usage details, usage certificates',
              },
              {
                name: 'Card Issuer',
                role: 'Billing & payment record manager',
                url: 'Issuer sites',
                scope: 'Monthly statements, payment history, balance',
              },
              {
                name: 'JTR',
                role: 'Record receipt/organize/storage support',
                url: 'japantollreceipts.jp',
                scope: 'PDF/CSV delivery, card/vehicle sorting, review aid',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '記録形式の比較：PDF vs CSV',
            intro: '業務用途に応じた最適な形式選択',
            columns: [
              'PDF形式',
              'CSV形式',
            ],
            rows: [
              {
                feature: '視覚的確認',
                a: 'レイアウト保持で即座に確認可能',
                b: '表計算ソフトで開く必要あり',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'メール添付共有',
                a: 'そのまま転送可能',
                b: '可能だが閲覧に手間',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '並べ替え・集計',
                a: '不可（固定レイアウト）',
                b: 'Excel/Google Sheetsで自在に操作',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '会計システム取込',
                a: '不可（手入力必要）',
                b: 'CSVインポート対応',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '長期保管',
                a: '閲覧環境に依存せず安定',
                b: 'ソフト互換性に注意',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
          en: {
            title: 'Record Format Comparison: PDF vs CSV',
            intro: 'Choose the right format for your workflow',
            columns: [
              'PDF Format',
              'CSV Format',
            ],
            rows: [
              {
                feature: 'Visual Review',
                a: 'Instant review with preserved layout',
                b: 'Requires spreadsheet app to open',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Email Attachment',
                a: 'Forward as-is',
                b: 'Possible but less convenient to view',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Sort & Summarize',
                a: 'Not possible (fixed layout)',
                b: 'Full control in Excel/Sheets',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Accounting Import',
                a: 'Manual entry required',
                b: 'CSV import supported',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Long-term Storage',
                a: 'Stable across platforms',
                b: 'Software compatibility matters',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '東北道ETC記録の完全性チェック',
            intro: '業務精算に必要な5要素を確認',
            items: [
              {
                text: 'ETCカード番号（下4桁）',
                hint: '複数カード利用時の区別に必須',
              },
              {
                text: '車両情報（ナンバー・車種）',
                hint: 'レンタカー・社用車の配分に必要',
              },
              {
                text: '通行経路（入口IC・出口IC）',
                hint: '業務目的の妥当性確認に使用',
              },
              {
                text: '通行目的メモ',
                hint: '訪問先・案件名など社内ルール項目',
              },
              {
                text: '公式記録の保管場所',
                hint: 'ETC利用照会サービス・カード明細のリンク',
              },
            ],
          },
          en: {
            title: 'Tohoku ETC Record Completeness Check',
            intro: 'Verify 5 elements for business reimbursement',
            items: [
              {
                text: 'ETC card number (last 4 digits)',
                hint: 'Essential when using multiple cards',
              },
              {
                text: 'Vehicle info (plate, class)',
                hint: 'Needed for rental/fleet allocation',
              },
              {
                text: 'Route (entry IC, exit IC)',
                hint: 'Verify business purpose validity',
              },
              {
                text: 'Trip purpose note',
                hint: 'Client name, project per internal policy',
              },
              {
                text: 'Official record location',
                hint: 'Link to ETC Riyō Shōkai, card statement',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'kan-etsu-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '関越道ETC明細',
      en: 'Kan-Etsu Expressway ETC detail',
    },
    ja: {
      title: '関越道ETC明細ガイド | Japan Toll Receipts',
      metaDesc: '関越自動車道（NEXCO東日本管轄）のETC利用明細を確認・整理・保存する方法を解説。ETC利用照会サービスからPDF利用証明書・CSV明細を取得する手順、IC・JCT別の整理、首都圏〜新潟出張の経費精算、確定申告前の年次まとめ、独立サービスJTRの日次配信で記録抜けを防ぐ運用までまとめました。',
      h1: '関越道ETC明細ガイド',
      primaryKeyword: '関越道ETC明細',
      answer: '関越自動車道のETC利用記録を適切に管理するには、料金や経路の確認にDrivePlazaを、カード別利用明細や利用証明書の取得にETC利用照会サービスを使い分けることが大切です。出張・通勤・私用を区別し、日付・IC名・カード・車両・金額を記録に残すことで、後日の説明や精算が容易になります。',
      whyMatters: '関越道はNEXCO東日本管轄の主要路線で、スキー旅行・物流・家族旅行・業務出張に広く使われます。経費精算や部門別集計、私用と業務の切り分けには正確な利用明細が欠かせません。カード明細だけでは利用目的や車両情報が不足するため、公式照会記録と社内メモを組み合わせて保管することで、数か月後の問い合わせにも対応できる体制を整えられます。',
      officialBackground: '日本の有料道路記録管理は複数の独立したシステムで構成されています。道路会社は経路・料金・割引・車種区分・安全情報を提供し、ETC利用照会サービスはカード別の利用履歴と利用証明書を発行します。カード会社は月次明細を提供する場合があります。JTRは独立組織として、利用者がETC利用記録を受信・整理・保管・確認する作業を支援しますが、公式料金データを生成する立場ではなく、公式サービスの代替でもありません。業務・官公庁・レンタカー・家族利用では、公式利用明細・PDF出力・CSV出力・カード明細・社内メモを一元管理することで、精算質問や部門配賦、確認が必要な利用の早期発見に役立ちます。',
      whoFor: [
        '東京圏・群馬・新潟方面を頻繁に往復する事業者',
        'スキー・観光・業務で関越道を利用する個人・家族',
        '複数車両・複数カードの利用記録を整理する経理担当者',
        'レンタカーや社用車の経路確認が必要な管理部門',
      ],
      jtrSolution: {
        intro: 'JTRはETC利用記録を実務的な確認フローに変える独立サービスです。手作業での検索・印刷・改名・転送に代わり、配信・整理・保管・確認支援に集中します。',
        bullets: [
          'PDF・CSV記録を継続的に受信し、カードや車両別に整理',
          '設定に応じて利用記録をグループ化し、確認が必要な行を強調表示',
          '社内ポリシーと照合が必要な管理者の作業を支援',
          '不審な記録を早期確認の対象として提示（異常検知（要確認）や法的判断は行わない）',
          '税務・インボイス・精算の記録整理を補助（最終判断は会計士・勤務先・公式指針で確認）',
        ],
      },
      pdfCsv: 'PDF記録は閲覧・共有に適し、CSV記録は並べ替え・フィルタ・会計システムへの取り込みに便利です。両方を保管することで、即座の確認と詳細分析の双方に対応できます。',
      email: 'カード別・期間別に利用明細をメール配信することで、月次確認や部門別転送が円滑になります。配信タイミングと宛先を調整し、記録の抜け漏れを防ぎます。',
      steps: [
        {
          t: '対象ルートと記録目的を確認',
          d: '関越自動車道はNEXCO東日本の路線で、スキー旅行・物流・家族旅行・出張に利用されます。通勤・出張・私用の区分が必要な場合、公式記録の取得範囲を事前に確認してください。',
        },
        {
          t: '料金・ルート情報は公式運営者サイトで確認',
          d: '料金額・車種区分・割引適用・ETC専用ゲート・路線固有ルールは、DrivePlazaなどNEXCO東日本の公式情報を最初に確認します。カード明細だけでは判断できない場合があります。',
        },
        {
          t: 'ETC利用照会サービスで利用明細を取得',
          d: '走行後のETC利用明細・利用証明書・PDF/CSV記録はETC利用照会サービスで確認します。一般ETCカードは過去15か月分が対象です。カード種別により期間が異なる場合があります。',
        },
        {
          t: '利用日・IC・カード・車両・目的を記録',
          d: '利用日・入口IC・出口IC・ETCカード番号(マスク)・車両・車種区分・運転者または部署・利用目的を一つの形式で記録します。公式記録と社内説明を分けて保存してください。',
        },
        {
          t: '公式データと内部説明を分けて保管',
          d: '公式のETC利用記録は利用事実を示し、社内メモや精算目的は承認根拠を示します。PDF・CSV・カード明細・社内メモをまとめて保管すると、後日の確認がスムーズになります。',
        },
        {
          t: '不明点は推測せず公式窓口へ問い合わせ',
          d: '記録の欠落・遅延・予想外の金額・車両不一致がある場合、推測や修正をせず、該当する道路運営者またはカード発行会社へ問い合わせ、回答を記録と一緒に保管してください。',
        },
      ],
      commonProblems: [
        {
          q: '利用明細に関越道の記録が見つからない',
          a: 'ETC利用照会サービスの対象期間(一般カードは過去15か月)を過ぎている可能性があります。カード発行会社の月次明細またはカスタマーセンターで確認してください。期間内でも反映に数日かかる場合があります。',
        },
        {
          q: '料金額が予想と異なる',
          a: '車種区分・時間帯割引・ETC割引・通行日・IC間距離が影響します。DrivePlazaの料金検索で条件を再確認し、それでも不明な場合はNEXCO東日本カスタマーセンターへ問い合わせてください。',
        },
        {
          q: 'PDF記録とCSV記録のどちらを保存すべきか',
          a: '両方の保存を推奨します。PDFは確認・共有が容易で、CSVは並べ替え・フィルタ・会計システムへの取り込みに適しています。用途に応じて使い分けると記録管理が効率的になります。',
        },
        {
          q: 'この記録を税務申告や精算に使えるか',
          a: '記録は税務や精算の裏付け資料として利用できますが、最終的な扱いは勤務先の規定・税理士の助言・公式ガイダンスに従ってください。本ガイドは税務助言ではありません。',
        },
      ],
      examples: [
        {
          who: '出張精算を行うドライバー',
          use: '関越道ETC明細ガイドで公式記録の取得先を確認し、PDF・CSV記録と社内の出張目的メモをまとめて精算部門へ提出します。',
        },
        {
          who: '経理担当者',
          use: '月次でETC利用照会サービスからPDF・CSVをダウンロードし、社内の車両・部署割り当てと突合して、精算漏れや目的不明の通行を早期に発見します。',
        },
        {
          who: '複数台車両を持つ家族',
          use: 'ETCカード明細を確認し、私用と業務用の通行を分け、業務利用分のみ税理士へ提出するための整理を行います。',
        },
        {
          who: '車両管理者',
          use: '予定外の通行を発見した際、推測せず公式のETC利用記録で日時・IC・車種を確認し、ドライバーへヒアリングする前に事実を把握します。',
        },
      ],
      faqs: [
        {
          q: 'JTRは関越道の公式情報源ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式の路線・料金・割引・安全ルールはNEXCO東日本またはETC利用照会サービスで確認してください。JTRはETC利用記録の受信・整理・保管・確認を支援します。',
        },
        {
          q: 'ETC利用照会サービスで全ての情報が揃いますか?',
          a: 'ETC利用照会サービスはカード別の利用明細と利用証明書を提供しますが、現在の料金・割引・車種区分・ETC専用レーン情報は道路運営者サイトで確認してください。',
        },
        {
          q: 'PDFとCSVはどちらを保存すべきですか?',
          a: '両方の保存を推奨します。PDFは確認・共有が容易で、CSVは並べ替え・フィルタ・会計ソフトへの取り込みに便利です。用途に応じて使い分けてください。',
        },
        {
          q: '料金やルートが間違っている場合はどうすればよいですか?',
          a: '公式記録を確認し、カード・車両・利用日・IC情報を照合してください。それでも不明な場合は、該当する道路運営者またはカード発行会社へ問い合わせ、回答を記録と共に保管してください。',
        },
        {
          q: '関越道ETC明細の主なリスクは何ですか?',
          a: '記憶・スクリーンショット・不詳細な明細に頼り、公式ETC利用記録と業務目的の説明を一緒に保管しないことです。後日の確認や精算時に説明が困難になる可能性があります。',
        },
      ],
      relatedFeatures: [
        {
          title: '無料トライアル',
          desc: 'JTRの記録整理・PDF/CSV出力・確認支援機能を無料で試せます。関越道など地域別ETC明細の管理フローを実際に体験できます。',
          path: '/free-trial',
        },
        {
          title: 'ビジネス向けプラン',
          desc: '複数車両・複数カード・部署別集計が必要な企業向けに、ETC記録の一元管理と確認フローを提供します。',
          path: '/business',
        },
        {
          title: 'セキュリティ',
          desc: 'ETC利用記録の受信・保管・アクセス管理におけるJTRのセキュリティ対策と独立性の説明をご確認いただけます。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用明細の確認・利用証明書発行・PDF/CSVダウンロードが可能な公式サービス。一般ETCカードは過去15か月分が対象です。',
        },
        {
          label: 'ETC総合情報ポータル:ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別・利用証明書発行条件・一般/法人カード記録期間・無線/非無線ETC利用・PDF/CSVダウンロード方法の説明。',
        },
        {
          label: 'NEXCO東日本 DrivePlaza',
          url: 'https://www.driveplaza.com/',
          note: 'NEXCO東日本の道路・交通・料金・ルート情報ポータル。関越道を含む路線の最新情報を確認できます。',
        },
        {
          label: 'DrivePlaza 料金・ルート検索',
          url: 'https://www.driveplaza.com/dp/SearchTop',
          note: 'NEXCO高速道路および関連路線の料金・ルート検索。IC間の料金・距離・所要時間を事前に確認できます。',
        },
        {
          label: 'ETC総合情報ポータル:お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する公式の道路運営者カスタマーセンター一覧。不明点は該当窓口へ問い合わせてください。',
        },
      ],
      longBody: '関越自動車道は東日本高速道路株式会社(NEXCO東日本)が管理する主要路線であり、東京圏と群馬県、新潟県を結ぶ幹線として、冬季のスキー旅行、物流輸送、帰省、出張など多様な目的で利用されています。ETCによる通行記録は、個人の家計管理だけでなく、企業の経費精算、車両管理、部門別配賦、レンタカー利用時の証憑整理などにおいて重要な役割を果たします。しかし、通行から数週間が経過すると、どのカードでどの車両が走行したのか、業務目的だったのか私用だったのか、記憶だけでは判別が困難になる場合があります。このため、通行日、入口インターチェンジ、出口インターチェンジ、ETCカード番号の末尾、車両番号、車種区分、通行目的、公式記録の保管場所を一貫した形式で記録し、公式データと社内説明を分けて管理することが推奨されます。関越自動車道の料金や割引制度、ETC専用出入口、車種区分の適用ルールについては、NEXCO東日本のドラプラ公式サイトで最新情報を確認する必要があります。一方、ETC利用照会サービスはカード単位の利用明細や利用証明書の発行、PDFおよびCSV形式でのダウンロードに対応しており、通常のETCカードであれば過去15か月分の記録が照会可能です。ただし、カード会社が発行する月次明細とETC利用照会サービスの記録は情報源が異なるため、両方を突合して整合性を確認することが望ましい運用です。企業や官公庁、軍関係、レンタカー事業者では、複数のカードや車両を運用するケースが多く、利用者ごと、部門ごと、目的ごとに記録を分類する必要があります。このとき、スクリーンショットや手書きメモだけに頼ると、後日の監査や問い合わせ対応で情報不足が発覚するリスクがあります。JTRは独立したサービスとして、公式のETC利用記録をPDFおよびCSV形式で受領・整理・保管し、管理者が異常な利用パターンや未承認の通行を早期に発見できるよう支援しますが、JTR自身が公式の料金データを生成することはなく、NEXCO、ETC利用照会サービス、カード発行会社、政府機関のいずれでもありません。利用記録に疑問が生じた場合は、公式の道路事業者またはカード発行会社に問い合わせを行い、その回答を記録とともに保管することが重要です。税務処理や請求書対応については、最終的な取扱いを会計士、雇用主、所属機関の規定、または公式ガイダンスで確認してください。',
    },
    en: {
      title: 'Kan-Etsu Expressway ETC Detail Guide | Japan Toll Receipts',
      metaDesc: "How to check, organize, and store Kan-Etsu Expressway ETC records. Official inquiry sources, PDF & CSV record usage, JTR's role, and FAQs explained.",
      h1: 'Kan-Etsu Expressway ETC Detail Guide',
      primaryKeyword: 'Kan-Etsu Expressway ETC detail',
      answer: 'To properly manage Kan-Etsu Expressway ETC records, use DrivePlaza to verify fares and routes, and the ETC Meisai service to obtain card-level usage statements and certificates. Distinguish business, commute, and personal trips, and record date, IC names, card, vehicle, and amount to simplify later explanations and settlements.',
      whyMatters: 'The Kan-Etsu is a major NEXCO East route widely used for ski trips, logistics, family travel, and business. Accurate detail records are essential for expense claims, departmental breakdowns, and separating personal from business use. Card statements alone lack trip purpose and vehicle data, so combining official inquiry records with internal notes ensures you can respond to inquiries even months later.',
      officialBackground: "Japan's toll record management comprises multiple independent systems. Road companies provide route, fare, discount, vehicle-class, and safety information; the ETC Meisai service issues card-level history and certificates; and card issuers may provide monthly statements. As an independent organization, JTR helps users receive, organize, store, and review ETC records but does not generate official fare data or replace official services. For business, government, rental-car, and family use, centralizing official statements, PDF exports, CSV exports, card statements, and internal notes aids settlement queries, departmental allocation, and early detection of unexpected charges.",
      whoFor: [
        'Businesses frequently traveling Tokyo–Gunma–Niigata corridors',
        'Individuals and families using Kan-Etsu for ski, tourism, or business',
        'Accounting staff organizing records across multiple vehicles and cards',
        'Administration teams requiring route verification for rental or company cars',
      ],
      jtrSolution: {
        intro: 'JTR is an independent service that turns ETC records into practical review workflows. Instead of manual searching, printing, renaming, and forwarding, it focuses on delivery, organization, storage, and review support.',
        bullets: [
          'Continuously receive PDF and CSV records, organized by card or vehicle',
          'Group usage records by configuration and highlight rows needing review',
          'Support managers who must reconcile records with internal policies',
          'Present unexpected records for early review (not fraud detection or legal judgment)',
          'Assist with tax, invoice, and settlement record organization (final decisions remain with accountants, employers, and official guidance)',
        ],
      },
      pdfCsv: 'PDF records suit viewing and sharing; CSV records enable sorting, filtering, and import into accounting systems. Storing both supports immediate confirmation and detailed analysis alike.',
      email: 'Delivering statements by card and period via email streamlines monthly reviews and departmental forwarding. Adjust delivery timing and recipients to prevent gaps in record coverage.',
      steps: [
        {
          t: 'Confirm target route and record purpose',
          d: 'Kan-Etsu Expressway is a NEXCO East route used for ski trips, logistics, family travel, and business trips. If you need to classify commuting, business, or personal use, check the scope of official records in advance.',
        },
        {
          t: 'Check fare and route info on official operator sites',
          d: 'Fare amounts, vehicle classes, discount eligibility, ETC-only gates, and route-specific rules should first be confirmed on NEXCO East official sources such as DrivePlaza. Card statements alone may not provide full details.',
        },
        {
          t: 'Obtain usage details via ETC Meisai service',
          d: 'Post-trip ETC usage details, usage certificates, and PDF/CSV records are available through the ETC Meisai inquiry service. Standard ETC cards cover the past 15 months. Period may vary by card type.',
        },
        {
          t: 'Record date, IC, card, vehicle, and purpose',
          d: 'Record usage date, entry IC, exit IC, ETC card number (masked), vehicle, vehicle class, driver or department, and purpose in one consistent format. Keep official records and internal notes separate.',
        },
        {
          t: 'Store official data and internal notes separately',
          d: 'Official ETC usage records show trip facts; internal memos and expense purposes support approval logic. Storing PDF, CSV, card statements, and internal notes together makes later review smoother.',
        },
        {
          t: 'Consult official channels for unclear points; do not guess',
          d: 'If you encounter missing records, delays, unexpected amounts, or vehicle mismatches, do not guess or modify. Contact the relevant road operator or card issuer, and keep the response with your records.',
        },
      ],
      commonProblems: [
        {
          q: 'Kan-Etsu records not found in usage details',
          a: "The ETC Meisai service retention period (15 months for standard cards) may have passed. Check your card issuer's monthly statement or customer center. Even within the period, reflection may take a few days.",
        },
        {
          q: 'Fare amount differs from expectation',
          a: 'Vehicle class, time-of-day discounts, ETC discounts, travel date, and IC-to-IC distance all affect fares. Re-check conditions using DrivePlaza fare search; if still unclear, contact NEXCO East customer center.',
        },
        {
          q: 'Should I save PDF or CSV records?',
          a: 'We recommend saving both. PDFs are easy to review and share; CSVs suit sorting, filtering, and import into accounting systems. Using each for its purpose makes record management more efficient.',
        },
        {
          q: 'Can I use these records for tax filing or expense claims?',
          a: "Records can support tax and expense documentation, but final treatment must follow your employer's rules, accountant's advice, and official guidance. This guide does not constitute tax advice.",
        },
      ],
      examples: [
        {
          who: 'Driver filing business trip expenses',
          use: 'Uses the Kan-Etsu ETC detail guide to confirm where to obtain official records, then submits PDF and CSV records along with internal trip purpose notes to the expense department.',
        },
        {
          who: 'Accounting staff',
          use: 'Downloads PDF and CSV from ETC Meisai service monthly, cross-checks against internal vehicle and department assignments, and identifies missing expenses or unexplained trips early.',
        },
        {
          who: 'Family with multiple vehicles',
          use: 'Reviews ETC card statements to separate personal and business trips, then organizes only business-use records for submission to their accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'When an unexpected trip is found, consults official ETC usage records for date, IC, and vehicle class—confirming facts before interviewing the driver, without guessing.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR the official source for Kan-Etsu Expressway information?',
          a: 'No. JTR is an independent service. For official route, fare, discount, and safety rules, consult NEXCO East or the ETC Meisai inquiry service. JTR assists with receiving, organizing, storing, and reviewing ETC usage records.',
        },
        {
          q: 'Does ETC Meisai service provide all information?',
          a: 'The ETC Meisai service offers card-specific usage details and usage certificates, but current fares, discounts, vehicle classes, and ETC-only lane information should be checked on road operator sites.',
        },
        {
          q: 'Should I save PDF or CSV?',
          a: 'We recommend saving both. PDFs are easy to review and share; CSVs are convenient for sorting, filtering, and importing into accounting software. Use each according to your needs.',
        },
        {
          q: 'What if fares or routes appear incorrect?',
          a: 'Check official records and cross-reference card, vehicle, usage date, and IC information. If still unclear, contact the relevant road operator or card issuer, and keep the response with your records.',
        },
        {
          q: 'What are the main risks with Kan-Etsu ETC details?',
          a: 'Relying on memory, screenshots, or incomplete statements—and not storing official ETC usage records together with business purpose notes—can make later review or expense processing difficult.',
        },
      ],
      relatedFeatures: [
        {
          title: 'Free Trial',
          desc: "Try JTR's record organization, PDF/CSV export, and review support features at no cost. Experience the management workflow for regional ETC details like Kan-Etsu.",
          path: '/free-trial',
        },
        {
          title: 'Business Plans',
          desc: 'For companies needing multi-vehicle, multi-card, and department-level aggregation, we provide centralized ETC record management and review workflows.',
          path: '/business',
        },
        {
          title: 'Security',
          desc: "Review JTR's security measures and independence regarding ETC usage record receipt, storage, and access control.",
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Meisai Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for checking ETC card usage details, issuing usage certificates, and downloading PDF/CSV. Standard ETC cards cover the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC Meisai Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, usage certificate issuance conditions, standard/corporate card retention periods, wireless/non-wireless ETC use, and PDF/CSV download methods.',
        },
        {
          label: 'NEXCO East DrivePlaza',
          url: 'https://www.driveplaza.com/',
          note: 'NEXCO East portal for road, traffic, fare, and route information. Check the latest information for routes including Kan-Etsu Expressway.',
        },
        {
          label: 'DrivePlaza Fare & Route Search',
          url: 'https://www.driveplaza.com/dp/SearchTop',
          note: 'Fare and route search for NEXCO expressways and related routes. Check fares, distance, and travel time between ICs in advance.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'List of official road operator customer centers for fares, discounts, and ETC usage. Contact the relevant center for unclear points.',
        },
      ],
      longBody: 'The Kan-Etsu Expressway is a major route managed by East Nippon Expressway Company Limited (NEXCO East Japan), serving as a trunk line connecting the Tokyo metropolitan area with Gunma and Niigata prefectures. It is used for diverse purposes including winter ski trips, logistics transport, homecoming visits, and business travel. ETC transaction records play an important role not only in personal household budget management but also in corporate expense reimbursement, managing multiple vehicles, departmental cost allocation, and documentation for rental car usage. However, after several weeks have passed since a journey, it can become difficult to recall which card was used with which vehicle, or whether the trip was for business or personal purposes based on memory alone. For this reason, it is advisable to record the travel date, entry interchange, exit interchange, last digits of the ETC card number, vehicle registration number, vehicle classification, travel purpose, and storage location of official records in a consistent format, keeping official data separate from internal notes. Current information regarding toll rates, discount schemes, ETC-only entry and exit points, and vehicle classification rules for the Kan-Etsu Expressway should be verified on the official NEXCO East Japan Dora-Pla website. Meanwhile, the ETC Usage Inquiry Service supports card-based usage statements, usage certificates, and downloads in PDF and CSV formats, with up to fifteen months of history available for standard ETC cards. However, because monthly statements issued by card companies and records from the ETC Usage Inquiry Service originate from different sources, it is prudent to cross-check both for consistency. Corporations, government offices, public agencies, and rental car operators often manage multiple cards and vehicles, requiring records to be categorized by user, department, and purpose. Relying solely on screenshots or handwritten notes can lead to information gaps during audits or inquiries. JTR operates as an independent service that receives, organizes, and stores official ETC usage records in PDF and CSV formats, helping administrators detect unusual usage patterns or unauthorized trips early. JTR does not generate official toll data and is not affiliated with NEXCO, the ETC Usage Inquiry Service, card issuers, or government agencies. When questions arise about usage records, it is essential to contact the official road operator or card issuer and retain their response alongside the records. For tax treatment and invoicing matters, final handling should be confirmed with an accountant, employer, institutional policies, or official guidance.',
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '関越道ETC利用記録の確認フロー',
            intro: '料金確認から証明書取得まで、目的に応じた公式サービスの使い分け',
            nodes: [
              {
                title: '料金・経路確認',
                desc: 'DrivePlazaで区間別料金や割引適用を即座に確認',
              },
              {
                title: 'カード別履歴',
                desc: 'ETC利用照会サービスで過去15か月分の利用明細を取得',
              },
              {
                title: '証明書発行',
                desc: '利用証明書をPDFで発行し、経費精算に添付',
              },
              {
                title: 'CSV出力',
                desc: '複数利用をCSVでダウンロードし、会計システムへ取り込み',
              },
              {
                title: '記録整理',
                desc: 'JTRで車両別・カード別に自動分類し、部門配賦を簡素化',
              },
            ],
          },
          en: {
            title: 'Kan-Etsu ETC Record Review Flow',
            intro: 'Selecting the right official service for fare lookup, history, and certificates',
            nodes: [
              {
                title: 'Fare & Route Check',
                desc: 'DrivePlaza provides instant segment fares and discount details',
              },
              {
                title: 'Card-Based History',
                desc: 'ETC Usage Inquiry retrieves 15 months of usage per card',
              },
              {
                title: 'Certificate Issuance',
                desc: 'Generate PDF usage certificates for expense claims',
              },
              {
                title: 'CSV Export',
                desc: 'Download multiple trips as CSV for accounting import',
              },
              {
                title: 'Record Organization',
                desc: 'JTR auto-sorts by vehicle & card for streamlined allocation',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式照会サービスと役割',
            intro: '関越道の利用記録管理に関わる主要な公式システム',
            sources: [
              {
                name: 'DrivePlaza',
                role: 'NEXCO東日本公式サイト',
                url: 'driveplaza.com',
                scope: '料金検索・SA/PA情報・割引案内',
              },
              {
                name: 'ETC利用照会サービス',
                role: 'カード別利用履歴',
                url: 'etc-meisai.jp',
                scope: '過去15か月分・利用証明書・CSV出力',
              },
              {
                name: 'カード会社明細',
                role: '月次請求明細',
                url: '各カード会社',
                scope: '請求額・引落日・利用先名称',
              },
              {
                name: 'JTR',
                role: '独立記録支援サービス',
                url: 'japantollreceipts.jp',
                scope: '受信・整理・保管・確認支援（公式データ発行元ではない）',
              },
            ],
          },
          en: {
            title: 'Official Services & Roles',
            intro: 'Key official systems for managing Kan-Etsu Expressway usage records',
            sources: [
              {
                name: 'DrivePlaza',
                role: 'NEXCO East official site',
                url: 'driveplaza.com',
                scope: 'Fare lookup, SA/PA info, discount guides',
              },
              {
                name: 'ETC Usage Inquiry',
                role: 'Card-based history',
                url: 'etc-meisai.jp',
                scope: '15-month records, certificates, CSV export',
              },
              {
                name: 'Card Issuer Statements',
                role: 'Monthly billing',
                url: 'Various issuers',
                scope: 'Charged amounts, payment dates, merchant names',
              },
              {
                name: 'JTR',
                role: 'Independent record support',
                url: 'japantollreceipts.jp',
                scope: 'Receipt, organization, storage, review (not official issuer)',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF vs CSV記録の使い分け',
            intro: '形式ごとの特性を理解し、保管・分析の両面で活用する',
            columns: [
              'PDF記録',
              'CSV記録',
            ],
            rows: [
              {
                feature: '閲覧・印刷',
                a: 'レイアウトそのまま表示可能',
                b: '表計算ソフトが必要',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'フィルタ・並替',
                a: '検索機能のみ',
                b: '日付・金額・IC名で自由に並替',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '会計システム連携',
                a: '手入力が必要',
                b: 'そのままインポート可能',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'メール添付',
                a: '軽量で共有しやすい',
                b: 'ファイルサイズが小さい',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: '長期保管',
                a: '視覚的に確認しやすい',
                b: '複数期間を一括管理可能',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'PDF vs CSV Records',
            intro: 'Understanding format strengths for storage and analysis workflows',
            columns: [
              'PDF Records',
              'CSV Records',
            ],
            rows: [
              {
                feature: 'View & Print',
                a: 'Layout preserved as-is',
                b: 'Requires spreadsheet app',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Filter & Sort',
                a: 'Search function only',
                b: 'Sort by date, amount, IC freely',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Accounting Import',
                a: 'Manual re-entry needed',
                b: 'Direct import supported',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Email Attach',
                a: 'Compact for sharing',
                b: 'Small file size',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: 'Long-Term Storage',
                a: 'Visual review easy',
                b: 'Batch manage multiple periods',
                kindA: 'yes',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '関越道記録管理チェックリスト',
            intro: '月次確認と年次整理で押さえるべきポイント',
            items: [
              {
                text: 'カード別に過去15か月分の履歴を確認済み',
                hint: 'ETC利用照会サービスでPDF・CSVを取得',
              },
              {
                text: '業務・私用・通勤を区別して記録',
                hint: '日付・IC名・車両番号をメモに残す',
              },
              {
                text: '部門別に利用金額を配賦済み',
                hint: 'CSVでフィルタし、部門コードを付与',
              },
              {
                text: '利用証明書を経費精算に添付',
                hint: '出張申請と突合して承認フローへ',
              },
              {
                text: '不審な利用がないか目視確認',
                hint: '見覚えのないIC・時間帯を早期検出',
              },
              {
                text: 'PDF・CSVを社内規定に沿って保管',
                hint: '税務調査対応のため7年間保存',
              },
            ],
          },
          en: {
            title: 'Kan-Etsu Record Management Checklist',
            intro: 'Monthly review and annual filing checkpoints',
            items: [
              {
                text: 'Verified 15-month history per card',
                hint: 'Download PDF & CSV from ETC Usage Inquiry',
              },
              {
                text: 'Separated business, personal, commute',
                hint: 'Note date, IC, vehicle for each trip',
              },
              {
                text: 'Allocated costs by department',
                hint: 'Filter CSV and assign dept codes',
              },
              {
                text: 'Attached certificates to expense claims',
                hint: 'Match with travel requests for approval',
              },
              {
                text: 'Reviewed for unfamiliar entries',
                hint: 'Flag unknown ICs or times early',
              },
              {
                text: 'Stored PDF & CSV per company policy',
                hint: 'Retain 7 years for tax audit readiness',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'chuo-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '中央道 ETC 明細',
      en: 'Chuo Expressway ETC statement',
    },
    ja: {
      title: '中央道ETC明細ガイド | Japan Toll Receipts',
      metaDesc: '中央自動車道（NEXCO中日本管轄）のETC利用明細の確認・整理・保存方法を解説。ETC利用照会サービスからPDF利用証明書・CSV明細を取得する手順、IC・JCT別の整理、関東〜中部出張の経費精算、確定申告前の年次まとめ、独立サービスJTRが日次配信で記録抜けを防ぐ活用法まで紹介します。',
      h1: '中央道ETC明細ガイド',
      primaryKeyword: '中央道 ETC 明細',
      answer: '中央自動車道のETC明細を適切に管理するには、通行日・IC・車両・カード・料金を公式記録で確認し、PDF+CSV形式で保存することが重要です。公式の通行記録と社内説明を分けて整理することで、精算・監査・問合せ対応がスムーズになります。',
      whyMatters: '中央道は東京圏と山梨・長野を結ぶ主要路線で、IC選択・車種・時間帯・割引により同じ区間でも料金が変動します。記憶や画面メモだけでは数週間後の説明が困難になるため、公式ETC利用記録・PDF+CSV出力・カード明細・社内メモを一元管理し、精算申請・部門配賦・不正利用防止に備える体制が求められます。',
      officialBackground: '中央道の料金体系はNEXCO中日本が管理し、公式料金検索・割引制度・車種区分・ETC専用ゲート情報を提供しています。通行後のETC利用記録はETC利用照会サービスで確認でき、通常のETCカードは過去15か月分の利用証明書・PDF・CSVダウンロードが可能です。法人カード・レンタカー・複数車両運用では、カード発行会社の月次明細と併せて照会する必要があります。公式記録には通行日・入口IC・出口IC・車種・料金が含まれますが、運転者・利用目的・承認状況などの内部情報は含まれないため、企業・官公庁・公共部門事機関は独自の記録様式と組み合わせて管理します。',
      whoFor: [
        '中央道を定期的に利用する通勤・業務ドライバー',
        '複数車両・複数カードを管理する企業経理担当者',
        'レンタカー・出張・家族利用で明細整理が必要な個人',
        'ETC記録の照合・監査・精算を担当する管理者',
      ],
      jtrSolution: {
        intro: 'JTRは公式ETC利用記録を受信・整理・保管し、確認作業を支援する独立サービスです。NEXCO・MEISAI・ETC利用照会サービス・カード発行会社・政府機関ではなく、公式データの作成は行いません。',
        bullets: [
          'PDF+CSV形式でETC利用記録を保管し、手動検索・印刷・リネーム作業を削減',
          'カード別・車両別に通行記録をグループ化し、部門配賦・精算申請を効率化',
          '想定外の通行・高額記録・未承認利用を早期に発見し、管理者レビューを支援',
          '公式記録と社内メモを分離保管し、監査・税務・精算時の説明負担を軽減',
          '不明な記録は公式運営者・カード発行会社への問合せを推奨し、回答を記録に紐付け',
        ],
      },
      pdfCsv: 'JTRはETC利用記録をPDF+CSV形式で提供します。PDF版は確認・共有に適し、CSV版は並替え・絞込み・会計システム連携に活用できます。Excel・XLSX形式での提供は行っていません。',
      email: 'メール配信機能を設定すると、月次・週次でETC利用記録が自動送信されます。管理者・経理担当者・ドライバー本人が定期的に記録を確認でき、精算遅延・記録紛失・問合せ対応の遅れを防止できます。',
      steps: [
        {
          t: '対象路線と利用状況の確認',
          d: '中央自動車道は東京圏・山梨・長野を結ぶNEXCO中日本管轄路線です。同じ区間でもIC・車種・時間帯・割引条件により料金が変動するため、公式ルート検索で事前確認を行います。',
        },
        {
          t: '公式料金情報の照会',
          d: 'ルート・料金額・車種区分・割引適用条件・ETC専用ゲート情報など道路固有のルールは、まずNEXCO中日本の公式サイトで確認します。カード明細だけでは判断できない情報が含まれます。',
        },
        {
          t: 'ETC利用照会サービスでの利用明細取得',
          d: '走行後のETC利用履歴・利用証明書・PDF/CSV形式の明細は、ETC利用照会サービスまたはカード発行会社の明細で取得します。通常のETCカードは過去15か月分が対象です。',
        },
        {
          t: '利用記録の整理と記録',
          d: '利用日・入口IC・出口IC・ETCカード・車両・車種区分・運転者または部署・利用目的を、統一された社内フォーマットに記録します。公式データと社内説明を分けて管理することが重要です。',
        },
        {
          t: 'PDF+CSV形式での保管',
          d: 'PDF記録は閲覧・共有に便利で、CSV記録はソート・フィルタ・会計システム取込に適しています。両方を保管することで経費精算・部署配賦・確認対応時の柔軟性が高まります。',
        },
        {
          t: '不明点・不一致発生時の対応',
          d: '記録が不詳細・遅延・想定外の場合や金額・ルートに疑問がある場合は、推測せず該当する道路事業者またはカード発行会社へ問い合わせ、回答を記録と共に保管します。',
        },
      ],
      commonProblems: [
        {
          q: '中央道の利用明細が見当たらない',
          a: 'ETC利用照会サービスで対象カード・利用期間・車載器セットアップ状況を確認します。通常ETCカードは過去15か月、ETCコーポレートカードは過去62か月が照会可能です。カード発行会社の明細も併せて確認してください。',
        },
        {
          q: '料金が予想と違う',
          a: 'NEXCO中日本の料金検索で入口IC・出口IC・車種・利用日時・適用割引を再確認します。深夜割引・休日割引・時間帯・車種判定により料金が変動します。不明な場合はNEXCO中日本お客様センターへお問い合わせください。',
        },
        {
          q: '社用車と個人利用が混在している',
          a: 'ETC利用照会サービスのPDF+CSV記録を取得し、利用日・IC・車両・運転者情報を照合して業務用と私用を区分します。JTRは記録整理と見直し支援を提供しますが、最終判断は社内規定に従ってください。',
        },
        {
          q: '経費精算に何を添付すべきか',
          a: '公式ETC利用明細・PDF記録・カード明細・社内申請フォームを揃えます。税務・経費処理の最終判断は勤務先規定・税理士助言・公式ガイダンスで確認してください。JTRは記録整理を支援しますが税務判断は行いません。',
        },
      ],
      examples: [
        {
          who: '企業経理担当者',
          use: '月次のETC利用明細をPDF+CSV形式でエクスポートし、社内の車両・部署割当と照合して経費精算処理を効率化しています。',
        },
        {
          who: '車両管理者',
          use: '見慣れない利用記録を発見した際、公式ETC利用明細を確認した上で運転者へヒアリングを行い、不正利用リスクの早期発見に役立てています。',
        },
        {
          who: '複数車両を持つ家庭',
          use: 'ETCカード明細から業務利用と私用を分離し、確定申告前に税理士へ提出する資料を整理する際に中央道利用分を正確に抽出しています。',
        },
        {
          who: 'レンタカー利用者',
          use: 'レンタル契約書・ETCカード明細・公式利用明細を一括保管し、経費精算パッケージとして提出することで差戻しを防いでいます。',
        },
      ],
      faqs: [
        {
          q: 'JTRは中央道の公式料金情報源ですか?',
          a: 'いいえ。JTRは独立したサービスであり、公式の道路事業者ではありません。ルート・料金・割引・安全ルールはNEXCO中日本など公式サイトで確認してください。JTRはETC利用記録の整理・保管・見直し支援を提供します。',
        },
        {
          q: 'ETC利用照会サービスだけで十分ですか?',
          a: '利用明細取得には有効ですが、最新の料金・割引条件・ETC専用レーン情報・車種区分ルールは道路事業者の公式サイトで確認が必要です。両方を組み合わせて利用してください。',
        },
        {
          q: 'PDFとCSVのどちらを保存すべきですか?',
          a: '両方の保存を推奨します。PDFは閲覧・共有が容易で、CSVはソート・フィルタ・会計システム取込に適しています。用途に応じて使い分けることで柔軟な記録管理が可能になります。',
        },
        {
          q: 'これらの記録は税務申告や経費精算に使えますか?',
          a: '記録整理の補助として活用できますが、最終的な税務処理・経費承認は勤務先規定・税理士助言・公式ガイダンスで確認してください。このガイドは税務アドバイスではありません。',
        },
        {
          q: '金額やルートが間違っている場合はどうすればよいですか?',
          a: '公式記録を確認し、カード・車両・利用日・IC情報を照合した上で、それでも解決しない場合は該当する道路事業者またはカード発行会社へお問い合わせください。推測での判断は避けてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録の自動受信・整理・保管の流れと、PDF+CSV形式での記録管理方法を詳しく解説します。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数カード・部署配賦・経費精算フロー対応など、企業のETC記録管理ニーズに特化した機能を提供します。',
          path: '/business',
        },
        {
          title: 'セキュリティ',
          desc: 'JTRがETC利用記録をどのように安全に保管・暗号化・アクセス制御しているかを説明します。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用照会・利用証明書発行・PDF/CSV形式での明細ダウンロードが可能な公式サービス。通常ETCカードは過去15か月分が対象。',
        },
        {
          label: 'ETC総合情報ポータル: ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別・利用証明書発行条件・通常ETCカード記録期間・ETCコーポレートカード記録期間・無線/非無線ETC利用・PDF/CSVダウンロードについて解説。',
        },
        {
          label: 'NEXCO中日本 料金・ルート検索',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: 'NEXCO中日本管轄路線の料金・ルート検索ツール「ドライブコンパス」。中央道を含む区間の事前料金確認に利用できます。',
        },
        {
          label: 'ETC総合情報ポータル: お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する質問先として、各道路事業者のお客様センター連絡先を掲載。',
        },
        {
          label: 'NEXCO中日本',
          url: 'https://www.c-nexco.co.jp/',
          note: '中央自動車道を管轄する道路事業者の公式サイト。最新の料金・工事・規制・割引情報を確認できます。',
        },
      ],
      longBody: '中央自動車道は首都圏と山梨県、長野県を結ぶNEXCO中日本管理の主要幹線であり、通勤・出張・観光・物流において日常的に利用される路線です。同じ区間を走行しても、利用するインターチェンジの組み合わせ、車両区分、時間帯割引の適用有無、ETCカードの種類によって最終的な通行料金は変動します。そのため事後の記録確認においては、単に「中央道を使った」という情報だけでは不十分であり、入口IC、出口IC、通行日時、車両番号、ETCカード、車種区分、請求額という要素を揃えて保管することが実務上求められます。企業の経理担当者や車両管理者が直面する典型的な課題は、数か月後に提出された領収書や明細の内容が曖昧で、誰がどの車両でどの目的で走行したのかを遡って確認できない状況です。特に複数の車両と複数のETCカードを運用している組織では、カード明細と車両台帳、業務日報を突合する作業に多くの時間を要します。公式なETC利用記録はETC利用照会サービスで取得でき、通常のETCカードであれば過去15か月分の利用明細と利用証明書の発行が可能です。ただしこのサービスはカード単位の利用履歴を提供するものであり、車両や部署、目的といった社内管理情報は含まれません。したがって組織内では、公式記録をPDFおよびCSV形式でダウンロードした後、社内の車両管理表や出張申請と紐付ける運用が一般的です。JTRはこうした記録の受領、整理、保管、閲覧支援を行う独立したサービスであり、NEXCO、MEISAI、ETC利用照会サービス、カード発行会社、政府機関のいずれでもありません。公式な通行料金データを生成する立場ではなく、利用者が取得した記録を効率的に管理し、後日の確認作業を支援する役割を担います。よくある誤りとして、スクリーンショットや手書きメモだけを保存し、公式記録の取得を怠るケースが挙げられます。また、カード明細の到着を待つ間に利用照会サービスの記録保持期間を超過してしまい、詳細な入退場記録が取得できなくなる事例も散見されます。予期しない金額が記録されていた場合は、まず通行日、IC名、車種区分、割引適用の有無を確認し、NEXCO中日本の料金検索ツールで想定額と照合することが推奨されます。それでも不明な点が残る場合は、推測で処理せずカード発行会社またはNEXCO中日本のお客様センターへ問い合わせを行い、回答内容を記録と共に保管してください。税務処理や経費精算への利用可否は、最終的に雇用主の規定、会計士の助言、公的指針に従って判断する必要があります。',
    },
    en: {
      title: 'Chuo Expressway ETC Statement Guide | Japan Toll Receipts',
      metaDesc: 'How to check, organize, and store Chuo Expressway ETC statements. Official inquiry services, PDF+CSV records, JTR workflows, FAQs, and security notes.',
      h1: 'Chuo Expressway ETC Statement Guide',
      primaryKeyword: 'Chuo Expressway ETC statement',
      answer: 'To manage Chuo Expressway ETC statements properly, verify travel date, IC, vehicle, card, and toll in official records, then store them in PDF+CSV format. Separating official travel records from internal notes streamlines reimbursement, audits, and inquiry responses.',
      whyMatters: 'The Chuo Expressway is a major route linking the Tokyo area with Yamanashi and Nagano. IC choice, vehicle class, time of day, and discounts cause tolls to vary even for identical segments. Memory and screenshots alone make explanations difficult weeks later, so consolidating official ETC travel records, PDF+CSV exports, card statements, and internal notes supports reimbursement applications, department allocation, and fraud prevention.',
      officialBackground: 'NEXCO Central manages Chuo Expressway toll structures and provides official fare lookup, discount schemes, vehicle classification, and ETC-only gate information. Post-travel ETC records are available through the ETC Toll Inquiry Service; standard ETC cards allow download of usage certificates, PDFs, and CSVs for the past 15 months. Corporate cards, rental vehicles, and multi-vehicle fleets require cross-checking with card-issuer monthly statements. Official records include travel date, entry IC, exit IC, vehicle class, and toll but exclude driver identity, travel purpose, or approval status, so companies, government offices, and public-sector organizations combine them with proprietary record formats.',
      whoFor: [
        'Commuters and business drivers using the Chuo Expressway regularly',
        'Corporate accounting staff managing multiple vehicles and cards',
        'Individuals organizing statements for rental cars, business trips, or family travel',
        'Managers responsible for ETC record reconciliation, audits, and reimbursement',
      ],
      jtrSolution: {
        intro: 'JTR is an independent service that receives, organizes, and stores official ETC travel records to support review tasks. JTR is not NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC Toll Inquiry Service, a card issuer, or a government agency and does not create official data.',
        bullets: [
          'Store ETC travel records in PDF+CSV format, reducing manual search, printing, and renaming work',
          'Group travel records by card and vehicle to streamline department allocation and reimbursement applications',
          'Identify unexpected trips, high-value records, and unapproved usage early to support manager review',
          'Separate official records from internal notes for easier explanations during audits, tax reviews, and reimbursement',
          'For unclear records, recommend contacting official operators or card issuers and attach responses to records',
        ],
      },
      pdfCsv: 'JTR provides ETC travel records in PDF+CSV format. PDF versions suit review and sharing; CSV versions enable sorting, filtering, and accounting-system integration. Excel or XLSX format is not offered.',
      email: 'When email delivery is configured, ETC travel records are sent automatically on a monthly or weekly schedule. Managers, accounting staff, and drivers can review records regularly, preventing reimbursement delays, lost records, and inquiry response delays.',
      steps: [
        {
          t: 'Check target route and usage',
          d: 'The Chuo Expressway connects Tokyo, Yamanashi, and Nagano under NEXCO Central management. Tolls vary by IC, vehicle class, time, and discount conditions even for identical segments—verify in advance using official route search.',
        },
        {
          t: 'Consult official toll information',
          d: "Route details, toll amounts, vehicle classifications, discount eligibility, and ETC-only gate rules are published on NEXCO Central's official site. Card statements alone do not contain all road-specific information.",
        },
        {
          t: 'Retrieve ETC usage statements via ETC Meisai Service',
          d: 'Post-trip ETC usage history, certificates, and PDF/CSV statements are available through ETC Meisai Service or your card issuer. Standard ETC cards cover the past 15 months of records.',
        },
        {
          t: 'Organize and record usage data',
          d: 'Log trip date, entry IC, exit IC, ETC card, vehicle, class, driver or department, and purpose in a standardized internal format. Keep official data and internal notes separate for clarity.',
        },
        {
          t: 'Store in PDF + CSV formats',
          d: 'PDF records are convenient for viewing and sharing; CSV records enable sorting, filtering, and accounting system import. Storing both formats improves flexibility for expense claims, departmental allocation, and audit responses.',
        },
        {
          t: 'Handle discrepancies or unclear items',
          d: 'If records are incomplete, delayed, unexpected, or tolls and routes seem questionable, do not guess—contact the relevant road operator or card issuer and retain their response alongside your records.',
        },
      ],
      commonProblems: [
        {
          q: 'Cannot find my Chuo Expressway usage statement',
          a: "Check target card, usage period, and OBU setup status in ETC Meisai Service. Standard ETC cards allow queries for the past 15 months; ETC Corporate cards for the past 62 months. Also review your card issuer's statements.",
        },
        {
          q: 'Toll differs from expectation',
          a: "Re-check entry IC, exit IC, vehicle class, trip date/time, and applied discounts using NEXCO Central's toll search. Midnight discounts, holiday discounts, time bands, and class determination affect tolls. Contact NEXCO Central Customer Center if unclear.",
        },
        {
          q: 'Company and personal trips are mixed',
          a: 'Retrieve PDF + CSV records from ETC Meisai Service and cross-reference trip dates, ICs, vehicles, and drivers to separate business from private use. JTR assists with record organization and review, but final classification follows your internal policies.',
        },
        {
          q: 'What should I attach to expense claims?',
          a: 'Gather official ETC usage statements, PDF records, card statements, and internal claim forms. Final tax and expense treatment must be confirmed by employer rules, accountant advice, and official guidance. JTR assists with record organization but does not provide tax judgments.',
        },
      ],
      examples: [
        {
          who: 'Corporate accounting staff',
          use: 'Export monthly ETC usage statements in PDF + CSV, cross-reference with internal vehicle and department assignments, and streamline expense reimbursement processing.',
        },
        {
          who: 'Vehicle manager',
          use: 'When unfamiliar usage records appear, verify official ETC statements first, then interview drivers to support early detection of misuse risks.',
        },
        {
          who: 'Multi-vehicle household',
          use: 'Separate business from personal trips using ETC card statements, and accurately extract Chuo Expressway usage when organizing documents for the accountant before tax filing.',
        },
        {
          who: 'Rental car user',
          use: 'Store rental agreements, ETC card statements, and official usage records together as a in-depth expense package to prevent claim rejections.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR the official toll information source for the Chuo Expressway?',
          a: 'No. JTR is an independent service and not an official road operator. Verify routes, tolls, discounts, and safety rules on official sites like NEXCO Central. JTR supports ETC record organization, storage, and review.',
        },
        {
          q: 'Is ETC Meisai Service alone sufficient?',
          a: "It is effective for retrieving usage statements, but current tolls, discount conditions, ETC-only lane information, and vehicle class rules must be confirmed on road operators' official sites. Use both sources together.",
        },
        {
          q: 'Should I save PDF or CSV?',
          a: 'Save both. PDF is easy to view and share; CSV is suited for sorting, filtering, and accounting system imports. Using both enables flexible record management for different purposes.',
        },
        {
          q: 'Can these records be used for tax filing or expense claims?',
          a: 'They assist with record organization, but final tax treatment and expense approval must be confirmed by employer rules, accountant advice, and official guidance. This guide is not tax advice.',
        },
        {
          q: 'What if toll amounts or routes appear incorrect?',
          a: 'Verify official records and cross-check card, vehicle, trip date, and IC information. If the issue persists, contact the relevant road operator or card issuer. Avoid making assumptions.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: 'Detailed explanation of the flow for automatic receipt, organization, and storage of ETC usage records, plus PDF + CSV record management methods.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Features tailored to corporate ETC record management needs: multi-vehicle, multi-card, departmental allocation, and expense workflow support.',
          path: '/business',
        },
        {
          title: 'Security',
          desc: 'How JTR securely stores, encrypts, and controls access to ETC usage records.',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Meisai Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for ETC card usage queries, certificate issuance, and PDF/CSV statement downloads. Standard ETC cards cover the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC Meisai Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, certificate issuance conditions, standard ETC card retention periods, ETC Corporate card periods, wireless/non-wireless ETC usage, and PDF/CSV downloads.',
        },
        {
          label: 'NEXCO Central Toll & Route Search',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: "NEXCO Central's toll and route search tool 'Drive Compass' for advance toll checks on routes including the Chuo Expressway.",
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'Lists customer center contacts for each road operator for questions about tolls, discounts, and ETC usage.',
        },
        {
          label: 'NEXCO Central',
          url: 'https://www.c-nexco.co.jp/',
          note: 'Official site of the road operator managing the Chuo Expressway. Check current tolls, construction, restrictions, and discount information.',
        },
      ],
      longBody: "The Chuo Expressway is a major NEXCO Central Japan–managed trunk route connecting the Greater Tokyo area with Yamanashi and Nagano prefectures, and is used daily for commuting, business trips, tourism, and freight. Even when traveling the same segment, final toll charges vary depending on the combination of interchanges used, vehicle classification, whether time-based discounts apply, and the type of ETC card. For post-trip record verification, simply knowing that the Chuo Expressway was used is insufficient; practical requirements call for preserving entry IC, exit IC, date and time of passage, vehicle number, ETC card, vehicle category, and billed amount as a in-depth set. A typical challenge faced by corporate accounting staff and vehicle managers is that receipts or statements submitted months later contain ambiguous content, making it impossible to trace who drove which vehicle for what purpose. Especially in organizations operating multiple vehicles and multiple ETC cards, reconciling card statements with vehicle registers and trip logs consumes considerable time. Official ETC usage records can be obtained through the ETC Riyō Shōkai Service, which for ordinary ETC cards allows retrieval of usage statements and usage certificates covering the past fifteen months. However, this service provides a card-centric transaction history and does not include internal management information such as vehicle assignment, department, or trip purpose. Consequently, standard organizational practice is to download official records in PDF and CSV formats, then link them internally with multi-vehicle records spreadsheets and travel requests. JTR is an independent service that assists in receiving, organizing, storing, and reviewing these records; it is neither NEXCO, ETC Usage Inquiry Service (MEISAI), the ETC inquiry service, a card issuer, nor a government agency. It does not generate official toll data but rather helps users manage records they have obtained and supports later verification tasks. A common mistake is to save only screenshots or handwritten notes while neglecting to retrieve official records. There are also cases where the retention period of the inquiry service expires while waiting for card statements to arrive, rendering detailed entry and exit records unavailable. When an unexpected amount appears in a record, it is advisable first to confirm the travel date, IC names, vehicle classification, and whether discounts were applied, then cross-check the expected amount using NEXCO Central Japan's toll search tool. If questions remain, do not process the entry by guesswork; instead contact the card issuer or NEXCO Central Japan customer center, and retain the response alongside the record. Whether a given record may be used for tax filing or expense reimbursement must ultimately be determined in accordance with employer policies, accountant guidance, and public directives.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '中央道ETC明細の確認フロー',
            intro: '通行から記録保管までの4ステップ',
            nodes: [
              {
                title: '通行',
                desc: '中央道IC通過、ETC車載器が通信',
              },
              {
                title: '公式記録',
                desc: 'ETC利用照会で15か月分閲覧可能',
              },
              {
                title: 'PDF+CSV保存',
                desc: 'JTRで自動受信・整理・保管',
              },
              {
                title: '精算・監査',
                desc: '部門配賦・承認・税務対応に活用',
              },
            ],
          },
          en: {
            title: 'Chuo Expressway ETC Record Flow',
            intro: '4 steps from toll passage to archival',
            nodes: [
              {
                title: 'Passage',
                desc: 'Pass Chuo IC, OBU communicates',
              },
              {
                title: 'Official Record',
                desc: 'View 15 months via ETC portal',
              },
              {
                title: 'PDF+CSV Save',
                desc: 'JTR auto-receives, sorts, stores',
              },
              {
                title: 'Settlement',
                desc: 'Use for dept. allocation & audit',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '中央道ETC明細の公式情報源',
            intro: '各記録の役割と参照先',
            sources: [
              {
                name: 'NEXCO中日本',
                role: '料金検索・割引・IC情報',
                url: 'c-nexco.co.jp',
                scope: '公式料金体系',
              },
              {
                name: 'ETC利用照会サービス',
                role: '通行記録15か月分',
                url: 'etc-meisai.jp',
                scope: 'PDF・CSV・利用証明書',
              },
              {
                name: 'カード発行会社',
                role: '月次請求明細',
                url: 'カード会社サイト',
                scope: '請求額・引落日',
              },
              {
                name: 'JTR',
                role: '記録整理・保管支援',
                url: 'japantollreceipts.jp',
                scope: '自動受信・グループ化',
              },
            ],
          },
          en: {
            title: 'Official Sources for Chuo ETC Records',
            intro: 'Role and reference for each data type',
            sources: [
              {
                name: 'NEXCO Central',
                role: 'Toll search, discounts, IC info',
                url: 'c-nexco.co.jp',
                scope: 'Official fare system',
              },
              {
                name: 'ETC Portal',
                role: '15-month trip history',
                url: 'etc-meisai.jp',
                scope: 'PDF, CSV, certificates',
              },
              {
                name: 'Card Issuer',
                role: 'Monthly billing statement',
                url: 'Card company site',
                scope: 'Charges & payment dates',
              },
              {
                name: 'JTR',
                role: 'Record organization & storage',
                url: 'japantollreceipts.jp',
                scope: 'Auto-receive & group',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '公式記録と社内管理の比較',
            intro: '両方を組み合わせて完全な記録を構築',
            columns: [
              'ETC利用照会（公式）',
              'JTR（社内）',
            ],
            rows: [
              {
                feature: '通行日・IC・料金',
                a: '含まれる',
                b: '含まれる',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: '運転者・利用目的',
                a: '含まれない',
                b: 'メモ追加可',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'PDF・CSV出力',
                a: '15か月分',
                b: '自動保管',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: '部門・車両別分類',
                a: '手動',
                b: '自動',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '監査・税務対応',
                a: '公式証明',
                b: '説明補助',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
          en: {
            title: 'Official vs. Internal Record Management',
            intro: 'Combine both for complete documentation',
            columns: [
              'ETC Portal (Official)',
              'JTR (Internal)',
            ],
            rows: [
              {
                feature: 'Date, IC, toll amount',
                a: 'Included',
                b: 'Included',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: 'Driver, trip purpose',
                a: 'Not included',
                b: 'Add memo',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'PDF & CSV export',
                a: '15 months',
                b: 'Auto-archive',
                kindA: 'yes',
                kindB: 'yes',
              },
              {
                feature: 'Dept. & vehicle grouping',
                a: 'Manual',
                b: 'Automatic',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Audit & tax support',
                a: 'Official proof',
                b: 'Explanation aid',
                kindA: 'yes',
                kindB: 'partial',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '中央道ETC明細 月次確認チェックリスト',
            intro: '精算前に確認すべき7項目',
            items: [
              {
                text: 'ETC利用照会で当月分PDF・CSVダウンロード完了',
                hint: '月初5営業日以内推奨',
              },
              {
                text: 'カード会社明細と通行記録の件数・合計額が一致',
                hint: '不一致は即問合せ',
              },
              {
                text: '全通行に運転者・利用目的・承認者メモ記入済み',
                hint: 'JTRメモ機能活用',
              },
              {
                text: '想定外の通行・高額記録・深夜通行を管理者が確認',
                hint: '異常検知機能利用',
              },
              {
                text: '部門別・車両別に集計し、予算と比較',
                hint: 'CSV並替え・絞込み',
              },
              {
                text: 'レンタカー・出張利用は領収書・報告書と紐付け',
                hint: 'PDF一元保管',
              },
              {
                text: '前月分記録をバックアップし、確認対応可能な状態で保管',
                hint: '3年以上推奨',
              },
            ],
          },
          en: {
            title: 'Chuo ETC Monthly Review Checklist',
            intro: '7 items to verify before settlement',
            items: [
              {
                text: 'Download current month PDF & CSV from ETC portal',
                hint: 'Within 5 business days',
              },
              {
                text: 'Match trip count & total with card statement',
                hint: 'Query any mismatch',
              },
              {
                text: 'Add driver, purpose, approver memo to all trips',
                hint: 'Use JTR memo feature',
              },
              {
                text: 'Manager reviews unexpected, high-cost, late-night trips',
                hint: 'Use anomaly detection',
              },
              {
                text: 'Summarize by dept. & vehicle, compare with budget',
                hint: 'CSV sort & filter',
              },
              {
                text: 'Link rental & business trips to receipts & reports',
                hint: 'Unified PDF storage',
              },
              {
                text: 'Backup prior month, keep records organized for review for 3+ years',
                hint: 'Archive recommendation',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'meishin-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '名神高速道路 ETC 明細',
      en: 'Meishin Expressway ETC statement',
    },
    ja: {
      title: '名神高速道路ETC明細の確認方法 | PDF・CSV記録管理 | JTR',
      metaDesc: '名神高速道路（E1・NEXCO中日本／西日本管轄）のETC通行記録を確認・整理・保存する実務ガイド。ETC利用照会サービスでPDF利用証明書・CSV明細を取得する手順、IC・JCT別整理、名古屋〜京都〜大阪出張の経費精算、料金検索・工事情報、独立サービスJTRの日次配信を併用した記録運用までカバー。',
      h1: '名神高速道路ETC明細の確認と記録管理',
      primaryKeyword: '名神高速道路 ETC 明細',
      answer: '名神高速道路(E1)のETC通行記録は、ETC利用照会サービスで確認・ダウンロードできます。通行前にはドライブコンパスやドライブぷらざで料金を試算し、通行後は公式サービスからPDF・CSV形式で利用証明書や明細を取得します。JTRは公式記録を自動受信・整理する独立サービスです。',
      whyMatters: '名神高速道路は名古屋・京都・大阪を結ぶ主要物流・通勤ルートであり、反復利用する法人や個人事業主にとって通行記録の整理が重要です。料金試算・割引登録・記録保管はそれぞれ別の公式サービスが担当しており、通行後の明細取得期限(通常カード15か月、法人カード62日)を逃すと証明書が入手できません。PDF・CSV両形式を保管することで、経費精算と月次集計の両方に対応できます。',
      officialBackground: '国土交通省の高速道路ナンバリングでは名神高速道路を「E1」と表記し、NEXCO中日本・NEXCO西日本が管理しています。公式資料では小牧IC~吹田ICなどの区間がE1名神として明記され、2025年には全線開通60周年を迎える基幹路線です。料金検索はNEXCO中日本ドライブコンパス、通行記録確認はETC利用照会サービス、ポイント管理はETCマイレージサービスがそれぞれ公式窓口となります。通行料金はIC間・車種・日時・経路・割引登録状況により変動するため、事前試算と事後記録の突合が推奨されます。工事・規制情報も公式サイトで最新状況を確認してください。',
      whoFor: [
        '名古屋・京都・大阪間を定期運行する物流事業者',
        '関西・中部圏を往復する営業担当者',
        '複数車両・複数カードの名神通行記録を月次管理する経理担当者',
        '記録整理用に名神利用証明書が必要な個人事業主',
      ],
      jtrSolution: {
        intro: 'Japan Toll Receiptsは公式ETC照会サービスが生成した通行記録を自動受信・整理する独立サービスです。NEXCO・ETC利用照会サービス・ETCマイレージサービスとは別組織であり、料金決定・割引判定・公式データ作成は行いません。',
        bullets: [
          'ETC利用照会サービスのPDF・CSV記録を自動受信(手作業ダウンロード不要)',
          'カード別・車両別・ドライバー別の名神通行記録を分類整理',
          '月次CSVファイルで名神・新名神など路線別集計を支援',
          '日英バイリンガル対応で外資系企業の記録管理を円滑化',
          '通行後のメール配信で記録取得期限切れを防止',
        ],
      },
      pdfCsv: 'PDF形式は人間が読みやすく経費申請に適し、CSV形式は並べ替え・抽出・月次集計に適します。法人・個人事業主・複数カード保有世帯は両形式を保管することで、「この通行の証明書は?」と「今月の名神利用総額は?」の両方に対応できます。',
      email: 'JTRは通行後にPDF・CSV記録をメール配信します。ドライバー個人・経理担当・部門管理者それぞれが必要なタイミングで記録を受け取れるため、紙レシート回収や手作業ダウンロードの手間を削減し、共有フォルダへのアーカイブ作業を簡素化します。',
      steps: [
        {
          t: '出発前にDriveCompassで名神高速のETC料金を確認',
          d: '利用するIC間、車種、日時、ルート選択肢をDrivePlazaまたはDriveCompassで事前に確認し、おおよその通行料金を把握します。',
        },
        {
          t: '公式サイトで工事・規制情報を確認',
          d: 'NEXCO中日本・西日本の公式ページで名神高速(E1)区間の工事予定や通行規制、代替ルートの案内を確認してから出発します。',
        },
        {
          t: '走行時に正しくETCを利用',
          d: 'ETCカードを車載器に正しく挿入し、ETCレーンを通過します。料金所で紙の領収書が必要な場合は一般レーンを利用してください。',
        },
        {
          t: '走行後にETC利用照会サービスで利用明細を確認',
          d: '走行後、ETC利用照会サービスにログインして実際の通行履歴、利用証明書、PDFおよびCSV形式の明細をダウンロードします。',
        },
        {
          t: 'PDFとCSV両形式の明細を保存',
          d: 'PDF形式は目視確認や提出用に、CSV形式は月次集計や部門別振り分け用に保存し、用途に応じて使い分けます。',
        },
        {
          t: 'JTRで名神高速の利用記録を整理・アーカイブ',
          d: '頻繁に名神高速を利用する場合、JTRを活用してPDF・CSV明細を自動受信し、車両・ドライバー・部門別に整理・保管します。',
        },
      ],
      commonProblems: [
        {
          q: '事前の料金検索結果と実際の請求額が異なるのはなぜですか?',
          a: '料金検索は目安です。実際の通行料金は走行ルート、時間帯割引、車種判定、登録済み割引制度の適用状況により変動するため、走行後に公式の利用明細で確認してください。',
        },
        {
          q: 'ETC利用照会サービスとETCマイレージサービスの違いは何ですか?',
          a: 'ETC利用照会サービスは利用履歴の確認と利用証明書発行が目的、ETCマイレージサービスはポイント還元が目的です。それぞれ別のサービスとして運営されています。',
        },
        {
          q: '名神高速の通行記録をいつまで遡って取得できますか?',
          a: '通常のETCクレジットカード・ETCパーソナルカードは過去15か月分、ETCコーポレートカードは過去62日分が照会可能です。カード種別により期間が異なるため早めに取得してください。',
        },
        {
          q: 'JTRは公式のNEXCOやETC利用照会サービスですか?',
          a: 'いいえ。JTRは独立した記録整理サービスです。公式システムが作成した利用データの受信・保管・整理を支援しますが、料金決定や割引適用の権限は持ちません。',
        },
      ],
      examples: [
        {
          who: '関西圏の物流会社',
          use: '名神高速を日常的に利用し、JTRのCSV明細を活用して車両別・配送先エリア別に通行料金を振り分け、月次で集計しています。',
        },
        {
          who: '名古屋・関西間を往復する営業担当者',
          use: '出張のたびに名神高速を利用し、PDF形式の利用証明書を経費精算に添付して社内申請を行っています。',
        },
        {
          who: 'ルート計画担当者',
          use: '工事期間中に名神高速と新名神高速の代替ルートを比較検討し、走行後に実際のETC記録と照合して最適ルートを評価しています。',
        },
        {
          who: '経理チーム',
          use: '毎月、複数のドライバーから紙の領収書を回収する代わりに、名神高速のETC明細を共有アーカイブで一元管理し、仕訳作業を効率化しています。',
        },
      ],
      faqs: [
        {
          q: '名神高速のETC料金・明細について最も重要なポイントは何ですか?',
          a: '名神高速の料金や割引は公式のNEXCOやETC利用照会サービスが管理しており、JTRはその後の記録整理を支援する独立サービスである点を理解することが重要です。',
        },
        {
          q: 'PDF形式とCSV形式、どちらの明細を保存すべきですか?',
          a: '業務や家計管理で記録を残す場合、両方の保存を推奨します。PDFは目視確認・提出用、CSVは集計・分析用として用途が異なります。',
        },
        {
          q: '名神高速の通行料金や割引は条件により変わりますか?',
          a: 'はい。通行区間、車種、日時、登録済み割引制度、時間帯、ルート選択により料金が変動します。最新の適用条件は公式サイトで確認してください。',
        },
        {
          q: '法人利用の場合、個人ドライバーと何が異なりますか?',
          a: '法人は複数のETCカードを車両・ドライバー・部門に紐付け、PDFとCSV明細を共有アーカイブで管理する運用体制の構築が必要です。',
        },
        {
          q: '現在の割引制度や工事情報はどこで確認できますか?',
          a: 'NEXCO中日本・NEXCO西日本の公式サイト、DrivePlaza、ETC利用照会サービス、ETCマイレージサービスなど公式情報源を参照してください。',
        },
      ],
      relatedFeatures: [
        {
          title: '仕組みを理解する',
          desc: 'JTRがどのようにETC明細を受信・整理し、PDFとCSV形式で配信するかを詳しく解説します。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数カード・複数ドライバーの名神高速利用記録を一元管理する法人向けの機能をご案内します。',
          path: '/business',
        },
        {
          title: '料金プラン',
          desc: '個人・法人それぞれの利用規模に応じたJTRの料金体系を確認できます。',
          path: '/pricing',
        },
      ],
      references: [
        {
          label: 'NEXCO中日本 高速道路ナンバリング一覧',
          url: 'https://www.c-nexco.co.jp/corporate/company/overview/area/pdf/highway_numbering_list.pdf',
          note: 'E1が名神高速道路・東名高速道路であることを示す公式資料です。',
        },
        {
          label: 'NEXCO中日本 DriveCompass',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: 'NEXCO中日本の公式料金・ルート検索ツールです。',
        },
        {
          label: '名神高速(E1)工事情報 2025年発表',
          url: 'https://www.c-nexco.co.jp/corporate/pressroom/news_release/6427.html',
          note: '小牧IC~吹田IC区間など名神高速の工事計画を公表する公式プレスリリースです。',
        },
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETC利用履歴の照会、利用証明書発行、PDFおよびCSVダウンロードを行う公式サービスです。',
        },
        {
          label: 'ETC総合情報ポータル ETC利用照会サービス案内',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '通常カードは過去15か月、コーポレートカードは過去62日分の照会が可能である旨を説明する公式ページです。',
        },
      ],
      longBody: '名神高速道路は正式には高速道路ナンバリングE1の一部として東名高速道路とともに日本の東西幹線を形成し、小牧ICから吹田IC間を結ぶ主要幹線として中部と関西を結んでいます。NEXCO中日本とNEXCO西日本の公式資料では、E1名神区間として明記され、2023年には全線開通60周年を迎えたことが両社の共同リリースで公表されています。この区間を日常的に利用する物流車両、名古屋・京都・大阪間のビジネスドライバー、関西圏通勤者にとって、通行後のETC利用明細の管理は経理処理や経費精算の基礎となります。しかし日本のETCエコシステムでは、通行料金の見積もり、割引の適用判定、利用履歴の照会、利用証明書の発行、そして記録の整理がそれぞれ異なる公式サービスや独立ツールによって分担されており、一つのシステムで完結しません。名神高速を含む幹線道路のETC明細を適切に扱うには、事前の料金確認、通行後の公式記録取得、そして継続的な記録保管という三段階の流れを正しく理解する必要があります。\n\n通行前の準備段階では、NEXCO中日本の公式ツールであるドライブコンパスやドライブプラザの料金検索を用いて、出発IC、到着IC、車種、日時、経路選択肢に応じた料金見積もりを確認します。名神区間は工事や車線規制の影響を受けることがあり、NEXCO中日本およびNEXCO西日本の工事情報リリースでは具体的な区間と期間が公表されますが、これらは時期によって変動するため旅行当日の公式情報確認が不可欠です。また割引制度は事前登録や対象期間、対象路線、時間帯、車種が細かく設定されており、すべての通行に自動適用されるわけではありません。ETCマイレージサービスのポイント還元やその他の割引キャンペーンについても、公式サイトで最新の条件を確認してから利用する必要があります。\n\n通行後の記録取得には、ETC利用照会サービスが中心的な役割を果たします。公式ポータルの説明によれば、同サービスでは利用証明書の発行、利用明細の照会、そしてPDFおよびCSV形式でのダウンロードが可能です。一般的なETCクレジットカードおよびETCパーソナルカードでは最大15か月分の照会が可能ですが、ETCコーポレートカードは62日間と短い照会期間に制限されています。この期間差は記録管理の実務において重要で、特に法人車両や営業車両を管理する担当者は照会期限を過ぎて公式記録が取得できなくなる事態を避ける必要があります。PDF記録は人間が読みやすい証明書形式であり、経費精算や社内承認の際に提出する書類として適しています。一方CSV記録は複数カード、複数車両、複数部署にまたがる通行履歴を月次で集計、分類、照合する際に有効です。どちらか一方だけでなく両形式を保管することで、後から「この通行はどの車両のものか」「月間合計はいくらか」「経路選択は計画通りだったか」といった問いに対応しやすくなります。\n\nJapan Toll Receiptsは、この通行後の記録取得と保管の段階を支援する独立した記録配信・整理サービスです。JTRはNEXCO、ETC利用照会サービス、ETCマイレージサービス、政府機関のいずれでもなく、公式システムが生成した利用データを基にした記録の受信、保存、確認の流れを効率化する外部ツールです。具体的には、利用明細のPDFおよびCSV形式での継続的な配信、メールアーカイブ、カード別・車両別・ドライバー別の整理、日英両言語対応、月次ワークフローの一貫性向上といった付加価値を提供します。JTRは公式な通行料金の決定者でも割引適用の判定者でもなく、公式記録を整理して届ける層として機能します。利用者は公式サイトで最新のルールや最終的な適用条件を確認し、JTRを用いて記録管理の手間を減らすという使い分けが推奨されます。\n\n実務上よく見られる誤りとして、料金見積もりと利用証明書を混同する、すべての割引が自動適用されると思い込む、スクリーンショットだけ保存して公式PDFやCSVを取得しない、ETCマイレージサービスのポイント残高照会とETC利用証明書を同一視する、照会期限を過ぎてから記録取得を試みる、といったケースが挙げられます。特に法人や経理部門では、どのカードをどの車両・ドライバー・部署に紐付けるか、どの形式の記録をどの期間保管するか、社内承認ルールと公式記録の関係をどう整理するかといった運用設計が重要です。名神高速道路のように繰り返し利用される幹線では、一回の通行ごとに手動ダウンロードするよりも、継続的な配信と整理の仕組みを構築することで記録の抜け漏れや後追い作業を減らすことができます。公式情報は変わり得るため、割引期間、工事区間、適用条件については常に最新の公式発表を参照し、記録はPDFとCSVの両方を保管し、JTRのような独立サービスは記録整理の負担軽減手段として位置付けるという三原則を守ることで、名神高速道路のETC明細管理は計画的かつ継続可能な業務となります。',
    },
    en: {
      title: 'Meishin Expressway ETC Statement Guide | PDF・CSV Records | JTR',
      metaDesc: 'How to check and organize Meishin Expressway (E1) ETC toll records in PDF and CSV format. Official inquiry services, fare tools, construction updates, and JTR automated record retrieval explained.',
      h1: 'Meishin Expressway ETC Statement Verification and Record Management',
      primaryKeyword: 'Meishin Expressway ETC statement',
      answer: 'Meishin Expressway (E1) ETC toll records can be checked and downloaded via the ETC Toll Inquiry Service. Before travel, estimate fares using Drive Compass or Drive Plaza; after travel, retrieve usage certificates and statements in PDF or CSV format from official services. JTR is an independent service that automates official record retrieval and organization.',
      whyMatters: 'The Meishin Expressway is a critical logistics and commuter corridor linking Nagoya, Kyoto, and Osaka. For corporations and sole proprietors with repeated usage, organizing toll records is essential. Fare estimation, discount registration, and record retrieval are handled by separate official services, and statement download deadlines (typically 15 months for personal cards, 62 days for corporate cards) mean missing the window eliminates proof of travel. Retaining both PDF and CSV formats supports expense reimbursement and monthly aggregation workflows.',
      officialBackground: 'Under the Ministry of Land, Infrastructure, Transport and Tourism\'s expressway numbering system, the Meishin Expressway is designated "E1" and managed by NEXCO Central and NEXCO West Japan. Official documents specify sections such as Komaki IC to Suita IC as E1 Meishin; 2025 marks the 60th anniversary of full-line opening for this backbone route. Fare searches are handled by NEXCO Central\'s Drive Compass, toll record confirmation by the ETC Toll Inquiry Service, and points management by the ETC Mileage Service—each an official portal. Tolls vary by IC pair, vehicle class, date/time, route, and discount enrollment, so comparing pre-trip estimates with post-trip records is recommended. Check official sites for the latest construction and regulation information.',
      whoFor: [
        'Logistics operators running regular routes between Nagoya, Kyoto, and Osaka',
        'Sales representatives traveling round-trip across Kansai and Chubu regions',
        'Accounting staff managing monthly Meishin toll records across multiple vehicles and cards',
        'Sole proprietors requiring Meishin usage certificates for expense filings',
      ],
      jtrSolution: {
        intro: 'Japan Toll Receipts is an independent service that automates retrieval and organization of toll records generated by the official ETC inquiry service. JTR is a separate entity from NEXCO, ETC Toll Inquiry Service, and ETC Mileage Service; it does not determine fares, apply discounts, or create official data.',
        bullets: [
          'Automated retrieval of PDF and CSV records from ETC Toll Inquiry Service (no manual downloads)',
          'Classification of Meishin toll records by card, vehicle, and driver',
          'Monthly CSV files support route-level aggregation for Meishin, Shin-Meishin, and other expressways',
          'Bilingual (Japanese/English) support streamlines record management for foreign-affiliated firms',
          'Post-trip email delivery prevents missed retrieval deadlines',
        ],
      },
      pdfCsv: 'PDF format is human-readable and suited to expense claims; CSV format enables sorting, filtering, and monthly totals. Corporations, sole proprietors, and multi-card households benefit from retaining both formats—answering "Where is the certificate for this trip?" and "What was this month\'s total Meishin spend?" in parallel.',
      email: 'JTR delivers PDF and CSV records via email after each trip. Individual drivers, accounting staff, and department managers receive records when needed—reducing paper receipt collection, manual download effort, and shared-folder archiving overhead.',
      steps: [
        {
          t: 'Check Meishin Expressway ETC tolls on DriveCompass before departure',
          d: 'Use DrivePlaza or DriveCompass to check toll estimates for your IC pair, vehicle class, date/time, and route options before you travel.',
        },
        {
          t: 'Review construction and traffic restrictions on official sites',
          d: 'Check NEXCO Central and West Japan official pages for Meishin Expressway (E1) construction schedules, traffic restrictions, and alternative route guidance before departure.',
        },
        {
          t: 'Use ETC correctly during your trip',
          d: 'Insert your ETC card into the onboard unit and pass through ETC lanes. If you need a paper receipt at the toll plaza, use a staffed lane instead.',
        },
        {
          t: 'Review usage details on ETC Inquiry Service after your trip',
          d: 'After traveling, log in to ETC Inquiry Service to view actual toll history, download usage statements and PDF and CSV format records.',
        },
        {
          t: 'Save records in both PDF and CSV formats',
          d: 'Keep PDF format for visual review and submission; use CSV format for monthly aggregation and department-level allocation, choosing the right format for each purpose.',
        },
        {
          t: 'Organize and archive Meishin Expressway records with JTR',
          d: 'If you use Meishin Expressway frequently, use JTR to auto-receive PDF and CSV records and organize them by vehicle, driver, and department for long-term storage.',
        },
      ],
      commonProblems: [
        {
          q: 'Why does my actual charge differ from the pre-trip toll search result?',
          a: 'Toll searches provide estimates only. Actual tolls vary by route taken, time-of-day discounts, vehicle classification, and registered discount program eligibility, so typically verify with the official usage record after traveling.',
        },
        {
          q: 'What is the difference between ETC Inquiry Service and ETC Mileage Service?',
          a: 'ETC Inquiry Service is for viewing usage history and issuing usage statements; ETC Mileage Service is for earning and redeeming points. They are operated as separate services.',
        },
        {
          q: 'How far back can I retrieve Meishin Expressway toll records?',
          a: 'Standard ETC credit and personal cards allow retrieval up to 15 months past; ETC corporate cards allow up to 62 days. Retrieval periods differ by card type, so download records promptly.',
        },
        {
          q: 'Is JTR an official NEXCO or ETC Inquiry Service?',
          a: 'No. JTR is an independent record management service. It helps you receive, store, and organize usage data created by official systems, but has no authority over toll calculation or discount application.',
        },
      ],
      examples: [
        {
          who: 'Logistics company in Kansai region',
          use: 'Uses Meishin Expressway daily and leverages JTR CSV records to allocate tolls by vehicle and delivery area, aggregating monthly reports for cost analysis.',
        },
        {
          who: 'Sales representative traveling between Nagoya and Kansai',
          use: 'Uses Meishin Expressway for every business trip, attaching PDF format usage statements to expense reimbursement forms for internal approval.',
        },
        {
          who: 'Route planning manager',
          use: 'Compares alternative routes on Meishin and Shin-Meishin Expressways during construction periods, then cross-checks actual ETC records to evaluate optimal routing.',
        },
        {
          who: 'Accounting team',
          use: 'Instead of collecting paper receipts from multiple drivers each month, manages Meishin Expressway ETC records in a shared archive to streamline bookkeeping workflows.',
        },
      ],
      faqs: [
        {
          q: 'What is the most important point about Meishin Expressway ETC tolls and records?',
          a: 'Understanding that Meishin Expressway tolls and discounts are managed by official NEXCO and ETC Inquiry Service, while JTR is an independent service that helps organize records after the fact.',
        },
        {
          q: 'Should I save PDF or CSV format records?',
          a: 'For business or household record-keeping, we recommend saving both. PDF is suited for visual review and submission; CSV is suited for aggregation and analysis—they serve different purposes.',
        },
        {
          q: 'Do Meishin Expressway tolls and discounts vary by conditions?',
          a: 'Yes. Tolls vary by travel segment, vehicle class, date/time, registered discount programs, time-of-day, and route selection. Check official sites for the latest applicable conditions.',
        },
        {
          q: 'For corporate use, what differs from individual driver use?',
          a: 'Corporate users must link multiple ETC cards to vehicles, drivers, and departments, and establish workflows to manage PDF and CSV records in a shared archive system.',
        },
        {
          q: 'Where can I check current discount programs and construction information?',
          a: 'Refer to official sources: NEXCO Central and West Japan websites, DrivePlaza, ETC Inquiry Service, and ETC Mileage Service for up-to-date information.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How It Works',
          desc: 'Learn how JTR receives and organizes ETC records and delivers them in PDF and CSV formats.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'Explore features for managing Meishin Expressway records across multiple vehicles, cards, and drivers in one centralized system.',
          path: '/business',
        },
        {
          title: 'Pricing',
          desc: 'Review JTR pricing plans for both individual and corporate users based on usage scale.',
          path: '/pricing',
        },
      ],
      references: [
        {
          label: 'NEXCO Central Japan Highway Numbering List',
          url: 'https://www.c-nexco.co.jp/corporate/company/overview/area/pdf/highway_numbering_list.pdf',
          note: 'Official document showing E1 designates Meishin and Tomei Expressways.',
        },
        {
          label: 'NEXCO Central Japan DriveCompass',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: 'Official toll and route search tool from NEXCO Central Japan.',
        },
        {
          label: 'Meishin Expressway (E1) Construction Information 2025 Announcement',
          url: 'https://www.c-nexco.co.jp/corporate/pressroom/news_release/6427.html',
          note: 'Official press release announcing construction plans for Komaki IC to Suita IC segment and other Meishin sections.',
        },
        {
          label: 'ETC Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for querying ETC usage history, issuing usage statements, and downloading PDF and CSV records.',
        },
        {
          label: 'GO ETC Portal – ETC Inquiry Service Guide',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Official page explaining that standard cards allow 15 months of history retrieval and corporate cards allow 62 days.',
        },
      ],
      longBody: "The Meishin Expressway officially forms part of Japan's east–west arterial route under expressway numbering E1, linking the Komaki and Suita interchanges and connecting the Chūbu and Kansai regions alongside the Tōmei Expressway. NEXCO Central and NEXCO West Japan identify this segment as the E1 Meishin section in their official materials, and in 2023 a joint release marked sixty years since the full route opened. For logistics vehicles, business drivers travelling between Nagoya, Kyoto and Osaka, and commuters across the Kansai area who use this corridor daily, managing ETC transaction records after each journey underpins accounting processes and expense reimbursement. Yet Japan's ETC ecosystem divides the tasks of toll estimation, discount eligibility checks, usage history queries, certificate issuance and record organisation across separate official services and independent tools; no single system covers the entire workflow. To handle ETC records for the Meishin and other trunk routes properly, users must understand a three-stage flow: pre-journey fare checks, post-journey retrieval of official records, and ongoing record storage. Before travel, drivers use official tools such as NEXCO Central's Drive Compass or Drive Plaza fare search to estimate tolls based on entry and exit interchanges, vehicle class, date, time and route options. The Meishin corridor may be affected by construction or lane restrictions; NEXCO Central and NEXCO West Japan publish specific segments and periods in their works notices, but because details shift it is important to review official announcements on the day of travel. Discount schemes require advance registration and are subject to eligibility criteria covering period, route, time of day and vehicle type; not every trip receives a discount automatically. ETC Mileage Service point rebates and other campaigns also demand that drivers check the latest conditions on official sites before use. After travel, the ETC Riyō Shōkai Service—the official usage-inquiry portal—plays the central role in record retrieval. According to the portal's documentation, users can issue certificates of use, view transaction details, and download records in both PDF and CSV formats. Standard ETC credit cards and ETC Personal Cards allow queries covering up to fifteen months, whereas ETC Corporate Cards restrict the window to sixty-two days. This difference matters in practice; staff managing corporate or commercial fleets must avoid missing the query deadline and losing access to official records. PDF files provide human-readable certificate layouts suited to expense submissions and internal approvals, while CSV data supports monthly aggregation, classification and reconciliation of trips across multiple cards, vehicles and departments. Keeping both formats makes it easier to answer questions such as which vehicle made a given trip, what the monthly total was, or whether the route matched the plan. Japan Toll Receipts is an independent record-delivery and organisation service that supports this post-journey retrieval and storage phase. JTR is neither NEXCO, the ETC inquiry service, the ETC Mileage Service nor any government body; it is an external tool that streamlines the flow of receiving, saving and reviewing records derived from official system data. Specifically, JTR offers continuous delivery of transaction records in PDF and CSV formats, email archiving, organisation by card, vehicle or driver, bilingual English and Japanese support, and improved consistency in monthly workflows. JTR does not determine toll charges or decide discount eligibility; it functions as a layer that organises and delivers official records. Users are encouraged to confirm the latest rules and final eligibility on official sites, then use JTR to reduce the administrative burden of record management. Common practical errors include confusing fare estimates with usage certificates, assuming all discounts apply automatically, saving only screenshots instead of retrieving official PDFs and CSVs, conflating ETC Mileage Service point balances with ETC usage certificates, and attempting to retrieve records after the query period has closed. Corporate and accounting teams in particular must design workflows that define which card is assigned to which vehicle, driver or department, which record format to retain and for how long, and how internal approval rules relate to official records. For a trunk route such as the Meishin that sees repeated use, building a system of continuous delivery and organisation reduces gaps and follow-up work compared with manual downloads after each trip. Because official information can change, three principles guide sound ETC record management for the Meishin Expressway: typically refer to the latest official announcements for discount periods, construction zones and eligibility conditions; store both PDF and CSV records; and position independent services such as JTR as tools to lighten the organisational load. Following these principles makes Meishin ETC record management a planned and sustainable task.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '名神高速道路ETC明細取得の流れ',
            intro: '通行前の料金試算から通行後の記録取得まで、4つの公式サービスと連携した記録管理プロセス',
            nodes: [
              {
                title: '通行前：料金試算',
                desc: 'ドライブコンパスで出発IC・到着IC・車種・日時を入力し料金を事前確認',
              },
              {
                title: '通行時：ETC無線通信',
                desc: '料金所でETCカード情報と通行データが自動記録される',
              },
              {
                title: '通行後：照会サービス',
                desc: 'ETC利用照会サービスでPDF利用証明書・CSV明細をダウンロード',
              },
              {
                title: '記録管理：JTR受信',
                desc: '公式記録を自動受信しメール配信、カード別・路線別に整理',
              },
            ],
          },
          en: {
            title: 'Meishin Expressway ETC Record Flow',
            intro: 'From pre-trip fare estimates to post-trip record retrieval via four official services',
            nodes: [
              {
                title: 'Pre-trip: Fare Estimate',
                desc: 'Use Drive Compass to input entry/exit IC, vehicle class, date/time for fare preview',
              },
              {
                title: 'During Trip: ETC Wireless',
                desc: 'Toll gate automatically records ETC card info and trip data',
              },
              {
                title: 'Post-trip: Official Query',
                desc: 'Download PDF proof and CSV details from ETC Riyō Shōkai Service',
              },
              {
                title: 'Management: JTR Inbox',
                desc: 'Auto-receive official records via email, sorted by card and route',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '名神高速道路公式サービス一覧',
            intro: '料金試算・記録照会・割引登録・工事情報はそれぞれ別の公式窓口が担当',
            sources: [
              {
                name: 'NEXCO中日本ドライブコンパス',
                role: '料金検索・ルート試算',
                url: 'dc.c-nexco.co.jp',
                scope: '小牧IC～大津IC区間',
              },
              {
                name: 'NEXCO西日本ドライブぷらざ',
                role: '料金検索・工事規制情報',
                url: 'drive.w-nexco.co.jp',
                scope: '大津IC～吹田IC区間',
              },
              {
                name: 'ETC利用照会サービス',
                role: '通行記録確認・PDF/CSV取得',
                url: 'etc-meisai.jp',
                scope: '全国高速道路',
              },
              {
                name: 'ETCマイレージサービス',
                role: 'ポイント照会・還元額登録',
                url: 'smile-etc.jp',
                scope: '割引対象路線',
              },
            ],
          },
          en: {
            title: 'Official Meishin Expressway Services',
            intro: 'Fare search, record query, discount registration, and construction info each have dedicated portals',
            sources: [
              {
                name: 'NEXCO Central Drive Compass',
                role: 'Fare search & route planning',
                url: 'dc.c-nexco.co.jp',
                scope: 'Komaki IC–Ōtsu IC',
              },
              {
                name: 'NEXCO West Drive Plaza',
                role: 'Fare search & construction alerts',
                url: 'drive.w-nexco.co.jp',
                scope: 'Ōtsu IC–Suita IC',
              },
              {
                name: 'ETC Riyō Shōkai Service',
                role: 'Trip history & PDF/CSV export',
                url: 'etc-meisai.jp',
                scope: 'Nationwide expressways',
              },
              {
                name: 'ETC Mileage Service',
                role: 'Points query & rebate registration',
                url: 'smile-etc.jp',
                scope: 'Discount-eligible routes',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: 'PDF vs CSV：名神明細の使い分け',
            intro: '両形式を保管することで経費申請と月次集計の両方に対応',
            columns: [
              'PDF利用証明書',
              'CSV明細データ',
            ],
            rows: [
              {
                feature: '個別通行の証明',
                a: '日付・IC・金額を1枚で提示可能',
                b: '1行データのみで視認性低い',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '月次集計・並べ替え',
                a: '手作業で再入力が必要',
                b: 'Excel・会計ソフトで自動集計',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '路線別・カード別抽出',
                a: '複数PDFを目視確認',
                b: 'フィルタ・ピボットで瞬時抽出',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '経費申請書への添付',
                a: 'そのまま添付可能',
                b: 'PDF再変換が必要な場合あり',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: '取得期限',
                a: '通常カード15か月・法人カード62日',
                b: '同左',
                kindA: 'neutral',
                kindB: 'neutral',
              },
            ],
          },
          en: {
            title: 'PDF vs CSV: Meishin Record Formats',
            intro: 'Retain both formats to cover expense claims and monthly aggregation',
            columns: [
              'PDF Proof',
              'CSV Detail',
            ],
            rows: [
              {
                feature: 'Single-trip proof',
                a: 'Date, IC, fare on one page',
                b: 'One-row data, less readable',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Monthly totals & sorting',
                a: 'Manual re-entry required',
                b: 'Auto-aggregate in Excel/accounting SW',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Filter by route/card',
                a: 'Visual scan of multiple PDFs',
                b: 'Instant filter/pivot extraction',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Expense report attachment',
                a: 'Attach as-is',
                b: 'May need PDF conversion',
                kindA: 'yes',
                kindB: 'partial',
              },
              {
                feature: 'Retention period',
                a: 'Personal 15mo / Corporate 62d',
                b: 'Same',
                kindA: 'neutral',
                kindB: 'neutral',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '名神ETC記録を期限内に取得するチェックリスト',
            intro: '法人カード62日・通常カード15か月の期限切れを防ぐ8つの確認項目',
            items: [
              {
                text: 'ETC利用照会サービスに全ETCカードを事前登録済み',
                hint: '通行後すぐ照会できるよう登録完了まで4時間待機',
              },
              {
                text: '法人カードは通行後62日以内に記録ダウンロード',
                hint: '個人カードは15か月だが法人カードは大幅に短い',
              },
              {
                text: 'PDF・CSV両形式を保管',
                hint: '経費申請用PDF、集計用CSVを同時取得',
              },
              {
                text: '名神・新名神を路線名で区別',
                hint: 'CSVの路線名列で「名神高速道路(E1)」を確認',
              },
              {
                text: '月次締め日前に当月分を全件ダウンロード',
                hint: '締め後の遡及取得忘れを防止',
              },
              {
                text: 'JTRメール受信設定で自動配信を有効化',
                hint: '手作業ダウンロード忘れのリスク削減',
              },
              {
                text: '通行前にドライブコンパスで料金試算',
                hint: '事後記録との突合で割引適用を確認',
              },
              {
                text: '工事・規制情報を公式サイトで最新確認',
                hint: '迂回ルートによる料金変動に注意',
              },
            ],
          },
          en: {
            title: 'Meishin ETC Record Retrieval Checklist',
            intro: '8 steps to avoid missing the 62-day (corporate) or 15-month (personal) deadline',
            items: [
              {
                text: 'All ETC cards pre-registered on Riyō Shōkai Service',
                hint: 'Wait 4 hours post-registration before querying trips',
              },
              {
                text: 'Corporate cards: download within 62 days of trip',
                hint: 'Personal cards allow 15mo, but corporate window is much shorter',
              },
              {
                text: 'Save both PDF and CSV formats',
                hint: 'PDF for claims, CSV for aggregation',
              },
              {
                text: 'Distinguish Meishin vs Shin-Meishin by route name',
                hint: "Check CSV route-name column for 'Meishin Expressway (E1)'",
              },
              {
                text: 'Download entire month before period close',
                hint: 'Prevent retroactive retrieval gaps',
              },
              {
                text: 'Enable JTR email delivery for auto-dispatch',
                hint: 'Reduce risk of manual download oversight',
              },
              {
                text: 'Pre-trip fare estimate via Drive Compass',
                hint: 'Cross-check post-trip record to verify discount application',
              },
              {
                text: 'Check latest construction/restriction info on official sites',
                hint: 'Detour routes may alter final fare',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'sanyo-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '山陽道ETC明細',
      en: "San'yō Expressway ETC statement",
    },
    ja: {
      title: '山陽道ETC明細ガイド | Japan Toll Receipts',
      metaDesc: '山陽自動車道（神戸―下関）のETC通行明細を確認・整理・保管する手順を解説。NEXCO西日本の公式情報、ETC利用照会サービスでのPDF利用証明書・CSV明細取得、月次保管のフォルダ規則、独立サービスJTRの位置づけ、安全注意事項とよくある質問まで網羅した、山陽道ドライバー・物流事業者向けの実務ガイド。',
      h1: '山陽道ETC明細ガイド',
      primaryKeyword: '山陽道ETC明細',
      answer: '山陽自動車道はNEXCO西日本の路線で、西日本の長距離移動や物流に広く利用されます。通行記録は公式照会サービスで確認し、日時・IC・車両区分・カード・請求額を記録として残すことが重要です。JTRは独立サービスとして、記録の受信・整理・保管・確認をサポートします。',
      whyMatters: '山陽道を利用する企業・ドライバー・管理者にとって、通行記録の管理は経費精算や社内承認の根拠となります。記憶や画面コピーではなく、公式のETC利用明細と社内メモを組み合わせて保管することで、後日の照合作業や問い合わせ対応がスムーズになります。利用日・入口IC・出口IC・カード・車両・理由を一貫した形式で記録し、公式データと社内説明を分けて管理することが推奨されます。',
      officialBackground: '山陽自動車道を含むNEXCO西日本管内の通行料金・経路・割引・車両区分はNEXCO西日本の公式検索サイトで確認できます。ETC利用照会サービスでは、ETCカードの利用明細・利用証明書・PDF/CSVダウンロードが可能で、通常カードは過去15か月分が照会対象です。カード発行会社の月次明細も補完的に利用されます。JTRは独立したサービスであり、NEXCO・明細照会サービス・カード会社・政府システムではありません。公式通行データを作成する立場にはなく、利用者が公式記録を受信・整理・保管・確認する作業を支援します。',
      whoFor: [
        '山陽道を定期的に利用する企業ドライバー',
        '西日本の物流・営業ルートを管理する法人',
        '複数車両・カードのETC記録を整理したい経理担当者',
        'レンタカーや家族共用車の通行履歴を分類したい個人',
      ],
      jtrSolution: {
        intro: 'JTRは公式照会サービスから取得した記録を実務的な確認フローに変換します。手作業での検索・印刷・リネーム・転送を減らし、配信・整理・保管・確認支援に集中します。',
        bullets: [
          'PDF・CSV記録を自動配信し、手作業ダウンロードの負担を軽減',
          'カード・車両ごとにグループ化し、社内配分作業を効率化',
          '確認が必要な記録を強調表示し、早期レビューを支援',
          '過去記録の検索・比較機能で内規チェックを容易化',
          '記録の保管期間を延長し、監査・精算対応を長期サポート',
        ],
      },
      pdfCsv: 'JTRはPDF形式とCSV形式で記録を出力します。PDF版は閲覧・共有に適し、CSV版は並べ替え・絞り込み・会計システムへの取り込みに便利です。利用者はダウンロード後、必要に応じて表計算ソフトで分析できます。',
      email: 'JTRは記録をメール配信することで、管理者・経理担当者・ドライバーへの定期共有を自動化します。配信タイミング・宛先・形式は利用者が設定でき、月次締め作業や部門別レポート作成の手間を削減します。',
      steps: [
        {
          t: '対象路線と記録範囲を確認',
          d: '山陽自動車道はNEXCO西日本の管轄で、西日本の長距離移動・物流・業務に使われます。記録管理では路線名・日時・車両区分・カード・割引状況を保存し、記憶に頼らないようにします。',
        },
        {
          t: '公式道路会社サイトで料金・割引・車両区分を確認',
          d: '料金額・割引適用・車両区分・ETCゲート専用レーン・通行ルールなど路線固有の情報は、まず公式の道路会社(NEXCO西日本)サイトで確認します。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細で通行記録を取得',
          d: '通行後のETC利用明細・利用証明書・PDF記録・CSV記録はETC利用照会サービスまたはカード発行会社の明細で確認します。通常のETCカードは過去15か月分が対象です。',
        },
        {
          t: '通行日・IC・カード・車両・理由を一貫した形式で記録',
          d: '通行日・入口IC・出口IC・ETCカード・車両・車両区分・運転者または部署・通行理由を一つの内部フォーマットで記録し、公式記録と社内説明を分けて保管します。',
        },
        {
          t: 'PDF記録とCSV記録を両方保存し業務フローに組み込む',
          d: 'PDF記録は確認・共有に便利で、CSV記録は並べ替え・絞り込み・会計システムへの取り込みに適しています。両方を保存し、精算や内部監査に備えます。',
        },
        {
          t: '不明・遅延・不一致の記録は公式に問い合わせ',
          d: '記録が不詳細・遅延・予想外・不一致の場合は推測せず、該当する道路会社またはカード発行会社に問い合わせ、回答を通行記録とともに保管します。',
        },
      ],
      commonProblems: [
        {
          q: '山陽道の利用記録が月末の明細に反映されていない',
          a: 'ETC利用照会サービスの記録は通行日の翌日以降に反映されますが、カード会社の月次明細は締め日・請求サイクルが異なる場合があります。公式記録で通行日・IC・金額を確認し、カード明細と突合してください。',
        },
        {
          q: '複数車両で同じETCカードを使い回して記録が混在',
          a: '同一カードを複数車両で使うと、どの車両がどの通行を行ったか区別できなくなります。車両ごとにカードを分けるか、通行直後に車両・運転者・理由をメモし、公式記録と紐付けて管理してください。',
        },
        {
          q: '割引適用の有無が事前の想定と異なる',
          a: '深夜割引・休日割引・平日朝夕割引は通行時刻・曜日・車種・対象区間によって適用条件が変わります。公式の道路会社サイトで最新の割引ルールを確認し、不明な場合は道路会社に問い合わせてください。',
        },
        {
          q: '精算書類に添付すべき記録がどれか分からない',
          a: '精算には公式のETC利用明細(PDFまたはCSV)と、社内の通行理由・承認記録を併せて提出するのが一般的です。最終的な取り扱いは勤務先・経理担当・税理士の指示に従ってください。',
        },
      ],
      examples: [
        {
          who: '業務運転者',
          use: '「山陽自動車道ETC明細ガイド」を参照し、精算申請前にどの公式情報源を確認すべきか把握します。',
        },
        {
          who: '経理担当者',
          use: '月末にETC利用記録をPDF・CSVで一括出力し、社内の車両・部署割り当てと突合して仕訳処理を行います。',
        },
        {
          who: '複数車両を持つ家族',
          use: 'ETCカード明細を確認し、業務通行と私用通行を分けてから、税理士に資料を送付します。',
        },
        {
          who: '車両管理者',
          use: '不明な通行が見つかった場合、公式のETC利用記録を確認してから運転者に経緯を問い合わせ、記録を残します。',
        },
      ],
      faqs: [
        {
          q: 'JTRは山陽道の公式情報源ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式の路線・料金・割引・安全ルールは該当する道路会社または公式ETCサービスで確認してください。JTRはETC利用記録の受信・整理・保管・確認を支援します。',
        },
        {
          q: 'ETC利用照会サービスで道路会社サイトの代わりになりますか?',
          a: 'いいえ。ETC利用照会サービスはETCカードの利用明細・利用証明書を提供しますが、最新の路線・料金・割引・車線・車両区分ルールは道路会社サイトで確認する必要があります。',
        },
        {
          q: 'PDF記録とCSV記録のどちらを保存すべきですか?',
          a: '両方が有用です。PDF記録は確認・共有が簡単で、CSV記録は並べ替え・絞り込み・会計システムへの取り込みに適しています。用途に応じて使い分けてください。',
        },
        {
          q: 'この記録を税務や精算に使えますか?',
          a: '税務・精算の参考資料として使えますが、最終的な取り扱いは勤務先の規定・税理士の助言・公式ガイダンスに従ってください。このガイドは税務助言ではありません。',
        },
        {
          q: '金額や経路が間違っている場合はどうすればよいですか?',
          a: '公式記録を確認し、カード・車両・通行日・IC情報を照合してください。問題が解決しない場合は、該当する道路会社またはカード発行会社に問い合わせてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録をどのように受信・整理・保管し、精算・監査・管理業務を支援するかを説明します。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数カード・部署別集計が必要な企業向けに、記録の一元管理と確認フローを提供します。',
          path: '/business',
        },
        {
          title: 'セキュリティとプライバシー',
          desc: '通行記録・カード情報・車両情報の保護方針と、JTRが公式データを作成しない理由を説明します。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカードの利用照会・利用証明書発行・PDF/CSV形式での明細ダウンロードを提供する公式サービス。通常のETCカードは過去15か月分が対象。',
        },
        {
          label: 'ETC総合情報ポータル:ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別・利用証明書の発行条件・通常ETCカードと法人ETCカードの記録期間・無線/非無線利用・PDF/CSVダウンロードについて説明。',
        },
        {
          label: 'NEXCO西日本 料金・経路検索',
          url: 'https://search.w-nexco.co.jp/',
          note: 'NEXCO西日本が管轄する路線の料金・経路を検索できる公式サイト。',
        },
        {
          label: 'ETC総合情報ポータル:お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する質問先として、公式の道路会社お客様センター一覧を掲載。',
        },
        {
          label: 'JTR 無料トライアル',
          url: '/free-trial',
          note: 'JTRのETC記録管理機能を試用し、PDF・CSV出力や確認フローがどのように動作するかを体験できます。',
        },
      ],
      longBody: '山陽自動車道は西日本高速道路株式会社(NEXCO西日本)が管理する幹線ルートであり、神戸から山口県下関までを結ぶ長距離移動や物流、業務出張で頻繁に利用されます。ETC明細の管理では、利用日・入口IC・出口IC・車両区分・カード番号・最終請求額を正確に保管し、後日の照会や精算に備えることが求められます。実際の業務では、通行料金が発生した時点では記憶が鮮明でも数週間後には経路や目的を忘れてしまうことが多く、公式記録と社内メモを分離して残さないと説明が困難になります。ETC利用照会サービスでは通常のETCカードなら過去15か月分の利用履歴を照会でき、利用証明書の発行やPDF・CSV形式での一括ダウンロードが可能です。一方、通行料金の詳細な計算ルールや割引条件、車種区分の判定基準はNEXCO西日本の公式サイトで確認する必要があり、ETC利用照会サービスだけでは路線固有のルールまで網羅できません。複数の車両や複数のカードを運用している企業では、月末にPDFとCSVを一括取得し、車両台帳や部署コードと突合する作業が一般的です。しかし出力された明細には利用目的や承認者名は含まれないため、社内で別途管理する必要があります。記録が不詳細なまま放置すると、経理担当者が請求内容を理解できず差戻しが発生したり、私的利用と業務利用の区別が曖昧になったりします。また、レンタカーや家族共用車では誰がいつ走行したかを車載記録だけで判断できず、カード明細と運転者メモを組み合わせて初めて正確な記録が完成します。JTRは独立したサービスであり、NEXCO・ETC利用照会サービス・カード発行会社・政府のいずれでもありません。JTRは公式通行記録を作成せず、利用者が取得した記録を受信・整理・保管・レビューする仕組みを提供します。異常な利用パターンが見つかった場合、JTRは早期レビューの契機を提示しますが、不正を確定したり法的判断を下したりする機能は持ちません。税務処理や請求書対応については、最終的な取扱いを会計士・雇用主・省庁ガイダンスで確認してください。山陽道の長距離利用では深夜割引や平日朝夕割引が適用される場合がありますが、カードの登録状態や対象区間によって割引額が変動するため、想定と異なる請求額が表示されたときは公式の料金検索ツールで再計算し、疑問が残れば運営者またはカード発行元に問い合わせることが最も確実です。',
    },
    en: {
      title: "San'yō Expressway ETC Statement Guide | Japan Toll Receipts",
      metaDesc: "How to review, organize, and archive San'yō Expressway ETC statements. Official inquiry sources, PDF & CSV records, JTR's role, FAQs, and safety notes.",
      h1: "San'yō Expressway ETC Statement Guide",
      primaryKeyword: "San'yō Expressway ETC statement",
      answer: "The San'yō Expressway is a NEXCO West Japan route widely used for long-distance travel and logistics in western Japan. Trip records can be checked via official inquiry services; it is important to retain date, IC, vehicle class, card, and billed amount as records. JTR is an independent service that supports receiving, organizing, archiving, and reviewing records.",
      whyMatters: "For companies, drivers, and administrators using the San'yō Expressway, trip record management serves as the basis for expense reimbursement and internal approval. Rather than relying on memory or screen captures, combining official ETC usage statements with internal notes ensures smooth reconciliation and inquiry responses later. Recording trip date, entry IC, exit IC, card, vehicle, and purpose in a consistent format—and keeping official data separate from internal explanations—is recommended.",
      officialBackground: "Tolls, routes, discounts, and vehicle classifications for the San'yō Expressway and other NEXCO West Japan routes can be confirmed on NEXCO West Japan's official search site. The ETC Usage Inquiry Service provides ETC card usage statements, usage certificates, and PDF/CSV downloads; standard cards can query up to 15 months of history. Monthly statements from card issuers are also used as supplementary records. JTR is an independent service—it is not NEXCO, the statement inquiry service, a card company, or a government system. JTR does not create official trip data; it supports users in receiving, organizing, archiving, and reviewing official records.",
      whoFor: [
        "Corporate drivers who regularly use the San'yō Expressway",
        'Corporations managing logistics and sales routes in western Japan',
        'Accounting staff organizing ETC records across multiple vehicles and cards',
        'Individuals sorting rental-car or family-shared vehicle trip histories',
      ],
      jtrSolution: {
        intro: 'JTR converts records obtained from official inquiry services into a practical review workflow. It reduces manual searching, printing, renaming, and forwarding, focusing instead on delivery, organization, archiving, and review support.',
        bullets: [
          'Auto-deliver PDF & CSV records to reduce manual download burden',
          'Group by card and vehicle to streamline internal allocation',
          'Highlight records needing review to support early checks',
          'Search and compare past records to simplify policy compliance checks',
          'Extend record retention periods for long-term audit and reimbursement support',
        ],
      },
      pdfCsv: 'JTR outputs records in PDF and CSV formats. PDF is suited for viewing and sharing; CSV is convenient for sorting, filtering, and importing into accounting systems. After download, users can analyze records in spreadsheet software as needed.',
      email: 'JTR automates regular sharing to administrators, accounting staff, and drivers by delivering records via email. Users configure delivery timing, recipients, and format to reduce the effort of monthly closing work and departmental reporting.',
      steps: [
        {
          t: 'Confirm target route and recording scope',
          d: 'The Sanyo Expressway is managed by NEXCO West Japan and used for long-distance travel, logistics, and business across western Japan. Record management should store route name, date-time, vehicle class, card, and discount status to avoid relying on memory.',
        },
        {
          t: 'Check tolls, discounts, and vehicle classes on official road company site',
          d: 'For route-specific information such as toll amounts, discount eligibility, vehicle classification, ETC-only lanes, and traffic rules, first consult the official road company (NEXCO West Japan) website.',
        },
        {
          t: 'Retrieve trip records via ETC inquiry service or card statement',
          d: "After travel, check ETC usage details, usage certificates, PDF records, and CSV records through the ETC inquiry service or your card issuer's statement. Standard ETC cards cover the past 15 months.",
        },
        {
          t: 'Record date, IC, card, vehicle, and reason in consistent format',
          d: 'Log trip date, entry IC, exit IC, ETC card, vehicle, vehicle class, driver or department, and travel reason in a single internal format, keeping official records and internal explanations separate.',
        },
        {
          t: 'Save both PDF and CSV records and integrate into workflow',
          d: 'PDF records are convenient for review and sharing, while CSV records suit sorting, filtering, and import into accounting systems. Store both formats and prepare for reimbursement and internal audit.',
        },
        {
          t: 'For unclear, delayed, or mismatched records, contact official sources',
          d: 'When records are incomplete, delayed, unexpected, or inconsistent, do not guess—contact the relevant road company or card issuer, and file the response with your trip records.',
        },
      ],
      commonProblems: [
        {
          q: 'Sanyo Expressway usage not reflected in end-of-month statement',
          a: 'ETC inquiry service records appear the day after travel, but card company monthly statements may have different closing dates and billing cycles. Confirm trip date, IC, and amount in the official record, then reconcile with your card statement.',
        },
        {
          q: 'Multiple vehicles share one ETC card and records are mixed',
          a: 'Using the same card across multiple vehicles makes it impossible to distinguish which vehicle made each trip. Assign separate cards per vehicle, or note vehicle, driver, and reason immediately after each trip and link to the official record.',
        },
        {
          q: 'Discount eligibility differs from expectation',
          a: 'Nighttime, weekend, and weekday commuter discounts vary by trip time, day of the week, vehicle type, and eligible sections. Check the latest discount rules on the official road company site, and contact the company if unclear.',
        },
        {
          q: 'Unsure which records to attach to reimbursement documents',
          a: 'Reimbursement typically requires official ETC usage details (PDF or CSV) together with internal trip reason and approval records. Follow the final instructions from your employer, accounting staff, or tax advisor.',
        },
      ],
      examples: [
        {
          who: 'Business driver',
          use: 'Consults the "Sanyo Expressway ETC Details Guide" to understand which official sources to check before submitting a reimbursement request.',
        },
        {
          who: 'Accounting staff',
          use: 'At month-end, exports ETC usage records in PDF and CSV, reconciles them with internal vehicle and department assignments, and processes journal entries.',
        },
        {
          who: 'Family with multiple vehicles',
          use: 'Reviews ETC card statements, separates business trips from personal use, and sends documentation to their tax advisor.',
        },
        {
          who: 'Vehicle manager',
          use: 'When an unknown trip appears, checks the official ETC usage record before inquiring with the driver about the circumstances and files a note.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official information source for the Sanyo Expressway?',
          a: 'No. JTR is an independent service. For official route, toll, discount, and safety rules, consult the relevant road company or official ETC service. JTR assists with receiving, organizing, storing, and reviewing ETC usage records.',
        },
        {
          q: 'Can the ETC inquiry service replace the road company website?',
          a: 'No. The ETC inquiry service provides ETC card usage details and certificates, but the latest route, toll, discount, lane, and vehicle-class rules must be confirmed on the road company website.',
        },
        {
          q: 'Should I save PDF or CSV records?',
          a: 'Both are useful. PDF records are easy to review and share, while CSV records are suited for sorting, filtering, and import into accounting systems. Choose according to your needs.',
        },
        {
          q: 'Can I use these records for tax or reimbursement purposes?',
          a: "They can serve as reference materials for tax and reimbursement, but final handling must follow your employer's policies, your tax advisor's guidance, and official instructions. This guide is not tax advice.",
        },
        {
          q: 'What should I do if amounts or routes appear incorrect?',
          a: 'Review the official record and cross-check card, vehicle, trip date, and IC details. If the issue persists, contact the relevant road company or card issuer.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: 'Explains how ETC usage records are received, organized, and stored to support reimbursement, audit, and management tasks.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'For companies needing multiple vehicles, multiple cards, and department-level summaries, JTR provides centralized record management and review workflows.',
          path: '/business',
        },
        {
          title: 'Security and Privacy',
          desc: 'Details the protection policy for trip records, card information, and vehicle data, and explains why JTR does not create official data.',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Usage Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service providing ETC card usage inquiry, usage certificate issuance, and statement downloads in PDF/CSV formats. Standard ETC cards cover the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC Usage Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, usage certificate issuance conditions, record periods for standard and corporate ETC cards, wireless/non-wireless usage, and PDF/CSV downloads.',
        },
        {
          label: 'NEXCO West Japan Toll & Route Search',
          url: 'https://search.w-nexco.co.jp/',
          note: 'Official site for searching tolls and routes on NEXCO West Japan–managed expressways.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'Lists official road company customer centers as contact points for questions about tolls, discounts, and ETC usage.',
        },
        {
          label: 'JTR Free Trial',
          url: '/free-trial',
          note: "Test JTR's ETC record management features and experience how PDF and CSV exports and review workflows operate.",
        },
      ],
      longBody: "The San'yo Expressway is a trunk route managed by West Nippon Expressway Company Limited (NEXCO West Japan), connecting Kobe to Shimonoseki in Yamaguchi Prefecture and frequently used for long-distance travel, logistics, and business trips. Managing ETC transaction records requires accurate retention of usage date, entry IC, exit IC, vehicle classification, card number, and final billed amount to support later inquiries and settlement. In practice, while route details may be clear at the time of travel, it is common to forget the path and purpose within weeks, making it difficult to explain charges unless official records and internal notes are kept separately. The ETC Utilization Inquiry Service allows holders of standard ETC cards to look up the past fifteen months of transaction history, issue usage certificates, and download data in bulk as PDF and CSV files. However, detailed toll calculation rules, discount conditions, and vehicle classification criteria must be confirmed on the NEXCO West Japan official site, as the ETC inquiry service alone does not cover route-specific provisions. Companies operating multiple vehicles or cards typically download PDF and CSV batches at month-end and reconcile them against fleet registers and department codes. Because exported statements do not include trip purpose or approver names, separate internal tracking is necessary. Leaving records incomplete can lead to finance staff unable to understand billing and returning submissions, or blurring the line between personal and business use. For rental or family-shared vehicles, on-board logs alone cannot determine who drove when; accurate records emerge only by combining card statements with driver notes. JTR is an independent service and is not affiliated with NEXCO, the ETC inquiry portal, card issuers, or any government body. JTR does not generate official toll records; instead, it receives, organizes, stores, and facilitates review of records users obtain themselves. When unusual usage patterns appear, JTR offers an opportunity for early review but does not confirm fraud or render legal judgments. For tax treatment and invoice handling, consult your accountant, employer, or ministerial guidance for final procedures. Long-distance San'yo Expressway journeys may qualify for late-night or weekday commuter discounts, but because discount amounts vary with card registration status and eligible segments, if a billed amount differs from expectations it is most reliable to recalculate using the official toll search tool and, if questions remain, contact the operator or card issuer directly.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '山陽道ETC記録の流れ',
            intro: '通行から記録保管までの4ステップ',
            nodes: [
              {
                title: '通行',
                desc: '山陽道料金所をETC通過',
              },
              {
                title: '公式照会',
                desc: 'ETC利用照会サービスで明細確認',
              },
              {
                title: 'JTR受信',
                desc: 'PDF・CSVを自動配信で受け取り',
              },
              {
                title: '整理・保管',
                desc: 'カード・車両別に分類し長期保存',
              },
            ],
          },
          en: {
            title: 'Sanyo Expressway ETC Record Flow',
            intro: '4 steps from travel to archive',
            nodes: [
              {
                title: 'Travel',
                desc: 'Pass Sanyo toll gates via ETC',
              },
              {
                title: 'Official Query',
                desc: 'Check details on ETC portal',
              },
              {
                title: 'JTR Receive',
                desc: 'Auto-delivery of PDF & CSV',
              },
              {
                title: 'Organize & Store',
                desc: 'Sort by card/vehicle, archive long-term',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '公式照会先マップ',
            intro: '山陽道ETC明細を確認できる公式サービス',
            sources: [
              {
                name: 'NEXCO西日本',
                role: '路線管理・料金検索',
                url: 'w-nexco.co.jp',
                scope: '山陽道含む管内全路線',
              },
              {
                name: 'ETC利用照会サービス',
                role: '明細・証明書発行',
                url: 'etc-meisai.jp',
                scope: '過去15か月・PDF/CSV',
              },
              {
                name: 'カード発行会社',
                role: '月次明細・請求額',
                url: '各社サイト',
                scope: 'カード単位の利用履歴',
              },
              {
                name: 'JTR',
                role: '記録の整理・配信・保管支援',
                url: 'japantollreceipts.jp',
                scope: '独立サービス・公式データ外',
              },
            ],
          },
          en: {
            title: 'Official Source Map',
            intro: 'Where to check Sanyo Expressway ETC records',
            sources: [
              {
                name: 'NEXCO West',
                role: 'Route & toll search',
                url: 'w-nexco.co.jp',
                scope: 'All routes incl. Sanyo',
              },
              {
                name: 'ETC Query Portal',
                role: 'Statement & certificate',
                url: 'etc-meisai.jp',
                scope: '15 months · PDF/CSV',
              },
              {
                name: 'Card Issuer',
                role: 'Monthly statement & billing',
                url: 'Issuer sites',
                scope: 'Card-level history',
              },
              {
                name: 'JTR',
                role: 'Organize, deliver, archive',
                url: 'japantollreceipts.jp',
                scope: 'Independent · non-official',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '公式明細 vs. JTR記録',
            intro: 'それぞれの役割と特徴を比較',
            columns: [
              '公式ETC明細',
              'JTR記録',
            ],
            rows: [
              {
                feature: 'データ作成者',
                a: 'NEXCO・カード会社',
                b: 'JTR（独立サービス）',
                kindA: 'yes',
                kindB: 'neutral',
              },
              {
                feature: '照会可能期間',
                a: '通常15か月',
                b: '延長保管対応',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '自動配信',
                a: 'なし（手動DL）',
                b: 'メール・定期送信',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'グループ化',
                a: 'カード単位',
                b: '車両・部門別整理',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '公式証明力',
                a: 'あり',
                b: 'なし（補助記録）',
                kindA: 'yes',
                kindB: 'no',
              },
            ],
          },
          en: {
            title: 'Official Statement vs. JTR Record',
            intro: 'Role and feature comparison',
            columns: [
              'Official ETC Statement',
              'JTR Record',
            ],
            rows: [
              {
                feature: 'Data Creator',
                a: 'NEXCO · Card issuer',
                b: 'JTR (independent)',
                kindA: 'yes',
                kindB: 'neutral',
              },
              {
                feature: 'Query Period',
                a: 'Typically 15 months',
                b: 'Extended storage',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Auto Delivery',
                a: 'No (manual DL)',
                b: 'Email · scheduled',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Grouping',
                a: 'By card',
                b: 'By vehicle/dept',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Official Proof',
                a: 'Yes',
                b: 'No (auxiliary)',
                kindA: 'yes',
                kindB: 'no',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '山陽道ETC記録の管理チェックリスト',
            intro: '確実な記録保管のための5項目',
            items: [
              {
                text: '公式照会サービスで最新明細を確認',
                hint: '過去15か月分がオンライン対象',
              },
              {
                text: '利用日・IC・カード・車両を記録',
                hint: '社内精算に必要な基本項目',
              },
              {
                text: 'PDF・CSVをバックアップ保存',
                hint: '照会期限切れに備えて早期DL',
              },
              {
                text: 'カード・車両ごとに分類整理',
                hint: '部門別・プロジェクト別配分に対応',
              },
              {
                text: 'JTR配信で定期レビュー自動化',
                hint: '月次締め作業の手間を削減',
              },
            ],
          },
          en: {
            title: 'Sanyo Expressway ETC Record Checklist',
            intro: '5 steps for reliable record keeping',
            items: [
              {
                text: 'Check latest statement on official portal',
                hint: '15-month window online',
              },
              {
                text: 'Record date, IC, card, vehicle',
                hint: 'Core items for expense claims',
              },
              {
                text: 'Backup PDF & CSV locally',
                hint: 'Download before expiry',
              },
              {
                text: 'Sort by card and vehicle',
                hint: 'Support dept/project splits',
              },
              {
                text: 'Automate review with JTR delivery',
                hint: 'Cut monthly closing effort',
              },
            ],
          },
        },
      },
    ],
  },
  {
    slug: 'hokuriku-expressway-etc-meisai-guide',
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: {
      ja: '北陸道ETC明細',
      en: 'Hokuriku Expressway ETC statement',
    },
    ja: {
      title: '北陸道ETC明細ガイド | 利用照会・記録保管の総合案内 | JTR',
      metaDesc: '北陸自動車道（NEXCO中日本／東日本管轄）のETC明細を確認・整理・保存する実務ガイド。ETC利用照会サービスでPDF利用証明書・CSV明細を取得する手順、IC・JCT別整理、新潟〜富山〜金沢〜福井ルートの経費精算、冬季積雪期の運用、独立サービスJTRの日次配信を併用した記録運用までまとめました。',
      h1: '北陸道ETC明細ガイド',
      primaryKeyword: '北陸道ETC明細',
      answer: '北陸自動車道のETC明細は、ETC利用照会サービスで利用日・IC・車両区分・金額を確認し、PDF・CSV形式で記録を保存できます。季節運転や長距離業務が重なる北陸道では、公式の利用証明書と社内の経路説明を一緒に保管することで、後日の経費精算や車両管理がスムーズになります。JTRは受信・整理・保管を支援する独立サービスです。',
      whyMatters: '北陸自動車道は季節による通行条件変更や代替ルートが多く、料金記録だけでは後日の説明が困難になりがちです。どのETCカード・車両・IC区間・日付・経路理由を記録したかを明確にすることで、企業の経費精算、レンタカー利用、家族の複数車両管理、政府・軍関係の用途区分が正確に行えます。公式記録と内部メモを分けて保管すれば、数カ月後の問い合わせや監査時にも迅速に対応できます。',
      officialBackground: '日本の有料道路記録は複数の仕組みに分かれています。道路運営会社が料金・割引・車両区分・安全情報を公開し、ETC利用照会サービスがETCカード利用明細と利用証明書を提供します(通常カードは過去15カ月、コーポレートカードは別期間)。カード発行会社は月次明細を発行します。JTRはこれらとは独立したサービスで、利用記録の受信・整理・保管・レビューを支援しますが、公式の料金データを作成したり公式サービスを代替したりするものではありません。北陸道を含む地域別記録は、通常は公式運営会社の情報源と紐付けて保管してください。実用的な記録は、どのカード・車両・IC・理由・公式証拠の保管場所、という5つの要素を答えられる状態が望ましいです。業務・政府・軍・レンタル・家族用途では、公式明細・PDF・CSV・カード明細・内部メモを一元管理することで、後日の精算や質問対応が容易になります。',
      whoFor: [
        '北陸道を定期利用する法人ドライバーと経理担当者',
        '季節や迂回で経路が変わりやすい長距離運転者',
        '複数車両・複数カードを管理する家族や事業主',
        '精算や車両管理を効率化したいレンタカー・官公庁利用者',
      ],
      jtrSolution: {
        intro: 'JTRは北陸道を含むETC利用記録の受信・整理・保管・レビューを支援します。手動検索・印刷・ファイル名変更・転送を繰り返す代わりに、JTRが記録を配信・分類・保管し、レビュー担当者が内容を理解しやすくします。',
        bullets: [
          'PDF・CSV記録を自動保管し、後日の検索・比較を容易にする',
          'ETCカード・車両ごとに利用記録をグループ化(設定済みの場合)',
          '不明な利用や確認が必要な記録を早期に強調表示',
          '経費精算・部門配賦・社内規程との照合を支援',
          '税務・請求書処理は会計士・雇用主・公式指針で最終確認を推奨',
        ],
      },
      pdfCsv: 'JTRはETC利用記録をPDF形式(閲覧・共有に便利)とCSV形式(並べ替え・フィルタ・会計システム取込に便利)で提供します。どちらも公式記録の整理を助けますが、公式データそのものを作成するわけではありません。ダウンロード後、利用者がCSVをスプレッドシートで加工することも可能です。',
      email: '記録を指定メールアドレスへ定期配信できます。経理担当・車両管理者・部門責任者が毎回ログインせずに最新記録を受け取れるため、月次精算や車両利用状況の確認が迅速化します。配信設定は各利用者が管理画面で調整できます。',
      steps: [
        {
          t: '北陸道の利用状況と記録の必要性を確認',
          d: '北陸自動車道は季節的な走行、長距離移動、業務走行が重なる路線です。公式ETC利用明細と社内の走行理由メモを両方保管することで、天候・通行止め・迂回が発生した際も説明しやすくなります。',
        },
        {
          t: '料金・車種・割引は公式道路事業者で確認',
          d: '路線名だけでは請求額を証明できません。料金・車種区分・割引適用・ETC専用ゲート・道路固有ルールは、NEXCO東日本・中日本・西日本など該当事業者の公式サイトで確認してください。',
        },
        {
          t: 'ETC利用照会サービスまたはカード明細で利用明細を取得',
          d: '走行後のETC利用明細・利用証明書・PDF記録・CSV記録は、ETC利用照会サービスまたはカード会社明細で取得します。一般ETCカードは過去15か月分が対象期間の目安です。',
        },
        {
          t: '日時・IC・カード・車両・理由を一貫した形式で記録',
          d: '通行日・入口IC・出口IC・ETCカード・車両・車種区分・運転者または部署・走行理由を、社内で統一した形式で記録してください。公式データと社内説明を分けて保管することが重要です。',
        },
        {
          t: 'PDF・CSV両方を保管し用途に応じて使い分け',
          d: 'PDF記録は閲覧・共有に便利で、CSV記録は並べ替え・絞り込み・会計システムへの取り込みに適しています。両方を保管することで、確認作業と経理処理の両方に対応できます。',
        },
        {
          t: '不明・遅延・不一致がある場合は公式に問い合わせ',
          d: '記録が不詳細・遅延・予想外・不一致の疑いがある場合は、推測せず該当する道路事業者またはカード発行会社に連絡してください。回答は走行記録と一緒に保管します。',
        },
      ],
      commonProblems: [
        {
          q: '北陸道の通行記録が見つからない',
          a: 'ETC利用照会サービスで該当カードと日付範囲を確認してください。一般ETCカードは過去15か月が目安です。それでも見つからない場合はカード発行会社または道路事業者に問い合わせてください。',
        },
        {
          q: '料金が予想より高い・低い',
          a: '入口IC・出口IC・通行日時・車種区分・割引適用を公式記録で確認してください。深夜割引・休日割引・障害者割引などの条件を道路事業者サイトで再確認し、不明な場合は事業者に問い合わせます。',
        },
        {
          q: '複数車両・複数カードで記録が混在',
          a: 'カードごと・車両ごとに記録を分けて管理してください。JTRはカードまたは車両でグループ化する機能を提供できますが、最終的な紐付けは利用者が確認する必要があります。',
        },
        {
          q: '経理提出用に何を残せばよいか',
          a: '公式ETC利用明細(PDF・CSV)、カード明細、社内の走行理由メモ、車両・運転者情報を一式で保管してください。最終的な処理方法は、勤務先・会計士・所属機関の方針で確認してください。',
        },
      ],
      examples: [
        {
          who: '出張の多い営業担当者',
          use: '「北陸自動車道ETC明細ガイド」を参照し、経費申請前にどの公式情報源を確認すべきか把握して、正確な通行記録と社内メモを準備します。',
        },
        {
          who: '経理部門',
          use: '月次でPDF・CSV記録を取得し、社内の車両・部署割り当てと照合することで、誤請求や不明利用の早期発見と経費処理の効率化を実現します。',
        },
        {
          who: '複数車両を持つ家族',
          use: 'ETCカード記録を確認して個人利用と業務利用を分け、会計士に送る前に正確な通行目的と車両情報を整理します。',
        },
        {
          who: '車両管理者',
          use: '見慣れない通行が見つかった際、公式ETC利用記録を確認してから運転者に経緯を尋ねることで、推測ではなく事実に基づいた確認作業を行います。',
        },
      ],
      faqs: [
        {
          q: 'JTRは北陸道の公式情報源ですか?',
          a: 'いいえ。JTRは独立したサービスです。公式の路線・料金・割引・設定・安全ルールは、該当する道路事業者または公式ETCサービスで確認してください。JTRはETC利用記録の受信・整理・保管・確認支援を行います。',
        },
        {
          q: 'ETC利用照会サービスで道路事業者サイトは不要?',
          a: 'いいえ。ETC利用照会サービスはETCカード利用明細と利用証明書の取得に有用ですが、最新の路線・料金・割引・レーン・車種区分ルールは道路事業者サイトでの確認が必要です。',
        },
        {
          q: 'PDFとCSVどちらを保存すべき?',
          a: '両方が有用です。PDF記録は確認・共有に適し、CSV記録は並べ替え・絞り込み・会計システムへの取り込みに適しています。用途に応じて使い分けてください。',
        },
        {
          q: '税務・経費精算に使えますか?',
          a: '税務・経費精算の確認資料として利用できますが、最終的な取り扱いは勤務先方針・会計士助言・公式指針で確認してください。このガイドは税務助言ではありません。',
        },
        {
          q: '金額や路線が間違っている場合は?',
          a: '公式記録を確認し、カード・車両・通行日・IC情報を照合してください。それでも解決しない場合は、該当する道路事業者またはカード発行会社に問い合わせてください。',
        },
      ],
      relatedFeatures: [
        {
          title: 'JTRの仕組み',
          desc: 'ETC利用記録の受信・整理・保管・確認支援の流れを理解し、北陸道を含む全国の通行記録を効率的に管理する方法を確認できます。',
          path: '/how-it-works',
        },
        {
          title: '法人向けプラン',
          desc: '複数車両・複数カード・部署別管理が必要な企業向けに、PDF・CSV記録のグループ化、確認支援、不明利用の早期発見機能を提供します。',
          path: '/business',
        },
        {
          title: 'セキュリティ',
          desc: 'ETC利用記録を安全に保管・整理するための技術的対策、アクセス管理、データ保護の仕組みを説明します。',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC利用照会サービス',
          url: 'https://www.etc-meisai.jp/',
          note: 'ETCカード利用照会・利用証明書発行・PDF/CSV明細取得の公式サービス。一般ETCカードは過去15か月が目安です。',
        },
        {
          label: 'ETC総合情報ポータル:ETC利用照会サービス',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: '対象カード種別・利用証明書発行・一般/法人カード記録期間・無線/非無線ETC利用・PDF/CSVダウンロードの説明。',
        },
        {
          label: 'NEXCO東日本 ドラぷら',
          url: 'https://www.driveplaza.com/',
          note: 'NEXCO東日本の道路・交通・料金・路線情報ポータル。',
        },
        {
          label: 'NEXCO中日本 料金・ルート検索',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: 'NEXCO中日本のドライブコンパス料金・ルート検索。',
        },
        {
          label: 'ETC総合情報ポータル:お問い合わせ',
          url: 'https://www.go-etc.jp/contact',
          note: '料金・割引・ETC利用に関する公式道路事業者カスタマーセンター一覧。',
        },
      ],
      longBody: '北陸自動車道は新潟県から石川県を経て滋賀県に至る主要幹線であり、冬季の降雪地帯通過や長距離物流、観光利用が重なる路線です。ETC利用明細の管理においては、単に料金が発生した事実を記録するだけでなく、どのETCカードがどの車両でいつ利用されたか、入口ICと出口ICはどこか、業務目的は何だったか、公式記録はどこに保管されているかを一貫して整理することが推奨されます。北陸道では季節による通行止めや迂回ルートの発生、深夜割引や通勤割引の適用条件が複雑に重なる場合があり、事後の精算や社内説明時に明細単体では文脈が不足するケースが少なくありません。日本の通行料金記録は複数の独立したシステムで構成されています。道路運営会社は路線情報、料金表、割引制度、車両区分、通行止め情報を提供し、ETC利用照会サービスは登録されたETCカードの利用履歴と利用証明書を発行します。カード発行会社は月次明細を提供しますが、これらはそれぞれ別の目的で運用されています。JTRはこれら公式記録を生成する機関ではなく、利用者が受け取ったETC明細を整理・保管・照合しやすくする独立サービスです。利用者が公式記録をPDFおよびCSV形式で保存し、社内用途に応じて車両や部門ごとに分類し、不明な明細に対しては早期に確認フローを回すことを支援します。実務上よく見られる問題は、記憶やスクリーンショットに頼った記録管理、入口ICと出口ICの記載漏れ、カード番号下4桁の混同、利用日のずれによる照合ミス、割引適用の想定違いによる金額不一致などです。こうした場合、まず利用照会サービスで公式記録を確認し、該当する道路運営会社の料金検索ツールで経路と車種区分が一致するかを照合し、それでも不明な場合は運営会社の案内窓口またはカード発行会社に問い合わせる流れが基本となります。北陸道では特に冬季のチェーン規制やSA・PA利用の中断、複数車両を持つ事業者での車載器とカードの組み合わせ違い、レンタカー利用時の精算トラブルが報告されやすく、記録と実際の行動を紐付ける内部メモの併記が有効です。JTRは利用明細を配信・整理・レビューするツールとして機能しますが、税務処理や不正判定の最終判断を行うものではなく、あくまで利用者が自身の記録を効率的に管理し、疑問点を早期に発見して公式窓口へ確認する流れを促進する位置付けです。',
    },
    en: {
      title: 'Hokuriku Expressway ETC Statement Guide | JTR',
      metaDesc: 'How to check, organize, and store Hokuriku Expressway ETC statements. Official inquiry services, PDF/CSV records, JTR support, FAQs, and secure practices.',
      h1: 'Hokuriku Expressway ETC Statement Guide',
      primaryKeyword: 'Hokuriku Expressway ETC statement',
      answer: 'Hokuriku Expressway ETC statements show date, IC, vehicle class, and amount via the ETC Inquiry Service, with records saved as PDF or CSV. On the Hokuriku Expressway—where seasonal driving and long-distance trips overlap—storing official usage certificates alongside internal route notes streamlines later expense claims and multi-vehicle records. JTR is an independent service that helps receive, organize, and store these records.',
      whyMatters: 'The Hokuriku Expressway has seasonal condition changes and multiple alternate routes, making fare records alone hard to explain later. Recording which ETC card, vehicle, IC section, date, and route reason clarifies corporate expense claims, rental-car use, multi-vehicle family management, and government or public-sector purpose codes. Separating official records from internal notes enables fast responses to inquiries and audits months afterward.',
      officialBackground: "Japan's toll-road records are split across several systems. Road operators publish fares, discounts, vehicle classes, and safety information; the ETC Inquiry Service provides ETC card usage details and usage certificates (typically fifteen months for standard cards, different periods for corporate cards). Card issuers send monthly statements. JTR is independent of these systems and assists with receiving, organizing, storing, and reviewing usage records—it does not generate official fare data or replace official services. Regional records including the Hokuriku Expressway must typically be linked to official operator sources. A practical record answers five elements: which card, which vehicle, which IC, which reason, and where the official evidence is stored. For business, government, public-sector, rental, and family use, unified management of official statements, PDFs, CSVs, card statements, and internal notes makes later claims and inquiries easier.",
      whoFor: [
        'Corporate drivers and accounting staff who regularly use the Hokuriku Expressway',
        'Long-distance drivers whose routes shift with season or detour',
        'Families and businesses managing multiple vehicles and multiple cards',
        'Rental-car and government users who want efficient claims and fleet tracking',
      ],
      jtrSolution: {
        intro: 'JTR helps receive, organize, store, and review ETC usage records including the Hokuriku Expressway. Instead of repeated manual searches, printing, renaming, and forwarding, JTR delivers, classifies, and stores records so reviewers understand them easily.',
        bullets: [
          'Automatically store PDF and CSV records for easy later search and comparison',
          'Group usage records by ETC card and vehicle (when configured)',
          'Highlight unrecognized usage or records that need review early',
          'Support expense claims, departmental allocation, and policy checks',
          'Tax and invoicing should be finalized with accountants, employers, and official guidance',
        ],
      },
      pdfCsv: 'JTR provides ETC usage records in PDF format (easy to view and share) and CSV format (easy to sort, filter, and import into accounting systems). Both help organize official records but do not generate official data themselves. After download, users may edit CSV in spreadsheets.',
      email: "Records can be delivered regularly to specified email addresses. Accounting staff, vehicle managers, and department heads receive the latest records without logging in each time, speeding monthly claims and fleet status checks. Delivery settings are adjustable in each user's dashboard.",
      steps: [
        {
          t: 'Check Hokuriku Expressway usage and record-keeping needs',
          d: 'The Hokuriku Expressway sees seasonal travel, long-distance trips, and business use. Keeping both official ETC statements and internal trip notes makes it easier to explain weather events, closures, or detours when they occur.',
        },
        {
          t: 'Confirm tolls, vehicle class, and discounts with official road operators',
          d: 'Route name alone does not prove charges. Verify tolls, vehicle classification, discount eligibility, ETC-only gates, and route-specific rules on the official sites of NEXCO East, Central, West, or the relevant operator.',
        },
        {
          t: 'Obtain statements via ETC Inquiry Service or card provider',
          d: 'Post-trip ETC statements, usage certificates, PDF records, and CSV records are available through the ETC Inquiry Service or your card issuer. Standard ETC cards typically cover the past 15 months.',
        },
        {
          t: 'Record date, IC, card, vehicle, and purpose in consistent format',
          d: 'Log travel date, entry IC, exit IC, ETC card, vehicle, vehicle class, driver or department, and trip purpose in a unified internal format. Keep official data and internal explanations separate.',
        },
        {
          t: 'Store both PDF and CSV; use each format as appropriate',
          d: 'PDF records suit viewing and sharing; CSV records support sorting, filtering, and import into accounting systems. Storing both enables review tasks and financial processing alike.',
        },
        {
          t: 'Contact official sources if records are unclear, delayed, or inconsistent',
          d: 'When records are incomplete, delayed, unexpected, or potentially incorrect, do not guess—contact the road operator or card issuer. File replies with your trip records.',
        },
      ],
      commonProblems: [
        {
          q: 'Cannot find Hokuriku Expressway trip record',
          a: 'Check the ETC Inquiry Service for the card and date range. Standard ETC cards typically cover the past 15 months. If still missing, contact your card issuer or the road operator.',
        },
        {
          q: 'Toll is higher or lower than expected',
          a: 'Verify entry IC, exit IC, travel date/time, vehicle class, and discount application in the official record. Re-check late-night, weekend, or disability discount conditions on the operator site; contact the operator if unclear.',
        },
        {
          q: 'Records mixed across multiple vehicles and cards',
          a: 'Manage records separately by card and by vehicle. JTR can offer grouping by card or vehicle, but final reconciliation remains your responsibility.',
        },
        {
          q: 'What should I keep for accounting submission?',
          a: 'Store official ETC statements (PDF and CSV), card statements, internal trip notes, and vehicle/driver details together. Confirm final handling with your employer, accountant, or institution policy.',
        },
      ],
      examples: [
        {
          who: 'Sales rep with frequent business trips',
          use: 'Refers to the Hokuriku Expressway ETC Statement Guide to identify which official sources to check before filing expenses, preparing accurate trip records and internal notes.',
        },
        {
          who: 'Accounting department',
          use: 'Retrieves PDF and CSV records monthly and cross-checks them against internal vehicle and department assignments to catch billing errors or unknown usage early and streamline expense processing.',
        },
        {
          who: 'Family with multiple vehicles',
          use: 'Reviews ETC card records to separate personal and business use, organizing accurate trip purposes and vehicle information before sending documents to the accountant.',
        },
        {
          who: 'Vehicle manager',
          use: 'When unfamiliar trips appear, checks official ETC usage records before asking drivers for context, ensuring inquiries are based on facts rather than assumptions.',
        },
      ],
      faqs: [
        {
          q: 'Is JTR an official source for Hokuriku Expressway information?',
          a: 'No. JTR is an independent service. Official route, toll, discount, setting, and safety rules must be confirmed with the relevant road operator or official ETC service. JTR supports receipt, organization, storage, and review of ETC usage records.',
        },
        {
          q: 'Does the ETC Inquiry Service replace road operator sites?',
          a: 'No. The ETC Inquiry Service is useful for obtaining ETC card statements and usage certificates, but current route, toll, discount, lane, and vehicle-class rules require checking the road operator site.',
        },
        {
          q: 'Should I save PDF or CSV?',
          a: 'Both are useful. PDF records suit review and sharing; CSV records suit sorting, filtering, and import into accounting systems. Use each format as appropriate.',
        },
        {
          q: 'Can I use this for tax or expense reimbursement?',
          a: 'You may use it as supporting documentation for tax or expense review, but final handling must be confirmed with employer policy, accountant advice, or official guidance. This guide is not tax advice.',
        },
        {
          q: 'What if amounts or routes are incorrect?',
          a: 'Check the official record and reconcile card, vehicle, travel date, and IC information. If the issue persists, contact the relevant road operator or card issuer.',
        },
      ],
      relatedFeatures: [
        {
          title: 'How JTR Works',
          desc: 'Understand the flow of receiving, organizing, storing, and reviewing ETC usage records to manage trips nationwide—including the Hokuriku Expressway—more effectively.',
          path: '/how-it-works',
        },
        {
          title: 'Business Plans',
          desc: 'For companies managing multiple vehicles, cards, and departments, JTR provides PDF and CSV grouping, review support, and early detection of unknown usage.',
          path: '/business',
        },
        {
          title: 'Security',
          desc: 'Explains technical measures, access controls, and data protection mechanisms for safe storage and organization of ETC usage records.',
          path: '/security',
        },
      ],
      references: [
        {
          label: 'ETC Usage Inquiry Service',
          url: 'https://www.etc-meisai.jp/',
          note: 'Official service for ETC card usage inquiry, usage certificate issuance, and PDF/CSV statement download. Standard ETC cards typically cover the past 15 months.',
        },
        {
          label: 'ETC General Information Portal: ETC Usage Inquiry Service',
          url: 'https://www.go-etc.jp/useful/meisai',
          note: 'Explains eligible card types, usage certificate issuance, standard/corporate card record periods, wireless/non-wireless ETC use, and PDF/CSV download.',
        },
        {
          label: 'NEXCO East DRIVEPLAZA',
          url: 'https://www.driveplaza.com/',
          note: 'NEXCO East Japan portal for road, traffic, toll, and route information.',
        },
        {
          label: 'NEXCO Central Toll & Route Search',
          url: 'https://dc.c-nexco.co.jp/dc/DriveCompass.html',
          note: 'NEXCO Central Japan Drive Compass toll and route search.',
        },
        {
          label: 'ETC General Information Portal: Contact',
          url: 'https://www.go-etc.jp/contact',
          note: 'Directory of official road operator customer centers for toll, discount, and ETC usage inquiries.',
        },
      ],
      longBody: "The Hokuriku Expressway is a major trunk route running from Niigata Prefecture through Ishikawa Prefecture to Shiga Prefecture, serving as a corridor for winter snowfall zones, long-distance logistics, and tourism traffic. When managing ETC transaction records, it is advisable to maintain more than just a log of charges incurred. A consistent structure should capture which ETC card was used with which vehicle, when the trip occurred, which entry and exit interchanges were involved, what the business purpose was, and where the official documentation is stored. Along the Hokuriku route, seasonal road closures, detour routes, and overlapping discount conditions—such as late-night and commuter reductions—can introduce complexity, and a transaction record alone often lacks the context needed for post-trip reconciliation or internal explanation. Japan's toll-charge documentation is composed of multiple independent systems. Highway operators provide route information, fare tables, discount schemes, vehicle classifications, and closure notices. The ETC online inquiry service issues usage history and proof-of-use certificates for registered ETC cards. Card-issuing companies supply monthly statements. Each serves a distinct operational purpose. JTR is not a generator of official records; it is an independent service that helps users organize, store, and cross-reference the ETC transaction data they receive. It supports users in saving official records in PDF and CSV formats, classifying them by vehicle or department according to internal needs, and initiating early review workflows for unclear entries. Common practical issues include reliance on memory or screenshots for record-keeping, missing notations of entry and exit interchanges, confusion between the last four digits of card numbers, reconciliation errors due to date shifts, and fare mismatches caused by incorrect discount assumptions. In such cases, the recommended workflow begins with checking the official record via the ETC inquiry service, verifying that the route and vehicle class align using the operator's fare-search tool, and—if ambiguity persists—contacting the operator's information desk or the card issuer. The Hokuriku Expressway is particularly prone to reports of winter chain regulations, mid-journey stops at service or parking areas, mix-ups in on-board unit and card pairings among multi-vehicle operators, and settlement issues during rental-car use. Accompanying internal notes that link records to actual travel behavior are effective. JTR functions as a tool for distributing, organizing, and reviewing transaction data. It does not render final judgments on tax treatment or fraud determination. Its role is to enable users to manage their own records efficiently, identify questions early, and facilitate prompt confirmation with official channels.",
    },
    animations: [
      {
        component: 'TollFlowDiagram',
        labels: {
          ja: {
            title: '北陸道ETC明細の確認フロー',
            intro: '利用照会から保管まで4ステップで完結',
            nodes: [
              {
                title: 'ETC利用照会サービス',
                desc: '利用日・IC・金額を確認',
              },
              {
                title: 'PDF/CSV記録',
                desc: '公式証明書をダウンロード保存',
              },
              {
                title: '経路メモ追加',
                desc: '季節迂回・代替ルート理由を記録',
              },
              {
                title: 'JTR保管・配信',
                desc: '自動整理で後日検索を効率化',
              },
            ],
          },
          en: {
            title: 'Hokuriku ETC Record Flow',
            intro: '4-step process from inquiry to storage',
            nodes: [
              {
                title: 'ETC Inquiry Service',
                desc: 'Check date, IC, amount',
              },
              {
                title: 'PDF/CSV Records',
                desc: 'Download official proof',
              },
              {
                title: 'Route Notes',
                desc: 'Record seasonal detour reasons',
              },
              {
                title: 'JTR Storage & Delivery',
                desc: 'Auto-organize for future retrieval',
              },
            ],
          },
        },
      },
      {
        component: 'OfficialSourceMap',
        labels: {
          ja: {
            title: '北陸道関連の公式情報源',
            intro: '料金・割引・記録照会は各公式サイトで確認',
            sources: [
              {
                name: 'NEXCO中日本',
                role: '料金・割引・IC情報',
                url: 'c-nexco.co.jp',
                scope: '北陸道の運営会社公式サイト',
              },
              {
                name: 'ETC利用照会サービス',
                role: '利用明細・証明書発行',
                url: 'etc-meisai.jp',
                scope: '過去15カ月の利用記録照会',
              },
              {
                name: 'ETCカード発行会社',
                role: '月次明細・請求情報',
                url: '各カード会社サイト',
                scope: 'カード別の請求内訳',
              },
              {
                name: 'JTR',
                role: '記録整理・保管支援',
                url: 'japantollreceipts.jp',
                scope: '公式記録の受信・分類・配信',
              },
            ],
          },
          en: {
            title: 'Official Hokuriku Expressway Sources',
            intro: 'Verify tolls, discounts, and records at official sites',
            sources: [
              {
                name: 'NEXCO Central',
                role: 'Toll rates & IC info',
                url: 'c-nexco.co.jp',
                scope: 'Hokuriku Expressway operator',
              },
              {
                name: 'ETC Inquiry Service',
                role: 'Usage statements & proof',
                url: 'etc-meisai.jp',
                scope: '15-month record access',
              },
              {
                name: 'ETC Card Issuer',
                role: 'Monthly billing statements',
                url: 'Card company sites',
                scope: 'Card-specific billing',
              },
              {
                name: 'JTR',
                role: 'Record organization & storage',
                url: 'japantollreceipts.jp',
                scope: 'Receive, classify, deliver official records',
              },
            ],
          },
        },
      },
      {
        component: 'ComparisonMatrix',
        labels: {
          ja: {
            title: '手動管理 vs. JTR活用の比較',
            intro: '北陸道記録の保管方法による違い',
            columns: [
              '手動管理',
              'JTR活用',
            ],
            rows: [
              {
                feature: 'PDF/CSV自動保管',
                a: '毎回ログイン・DL',
                b: '自動受信・分類',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '複数カード一元管理',
                a: 'ファイル名手動整理',
                b: 'カード・車両別グループ化',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '季節迂回ルート説明',
                a: '別途メモ管理',
                b: '記録と紐付けて保管',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: '経理・部門への配信',
                a: '手動転送',
                b: '指定アドレスへ自動配信',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: '過去記録の検索性',
                a: 'フォルダ探索',
                b: 'タグ・日付で即時検索',
                kindA: 'no',
                kindB: 'yes',
              },
            ],
          },
          en: {
            title: 'Manual vs. JTR Comparison',
            intro: 'How Hokuriku record management differs',
            columns: [
              'Manual',
              'JTR',
            ],
            rows: [
              {
                feature: 'Auto PDF/CSV storage',
                a: 'Login & DL each time',
                b: 'Auto-receive & classify',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Multi-card unified view',
                a: 'Manual file naming',
                b: 'Card & vehicle grouping',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Seasonal detour notes',
                a: 'Separate memo file',
                b: 'Linked to record',
                kindA: 'partial',
                kindB: 'yes',
              },
              {
                feature: 'Deliver to accounting',
                a: 'Manual forward',
                b: 'Auto email delivery',
                kindA: 'no',
                kindB: 'yes',
              },
              {
                feature: 'Past record search',
                a: 'Folder browsing',
                b: 'Tag & date instant search',
                kindA: 'no',
                kindB: 'yes',
              },
            ],
          },
        },
      },
      {
        component: 'ChecklistReveal',
        labels: {
          ja: {
            title: '北陸道ETC記録の保管チェックリスト',
            intro: '後日の精算・監査に備えて確認すべき5項目',
            items: [
              {
                text: 'ETCカード番号・車両を特定できるか',
                hint: '複数カード・車両がある場合は紐付けを明記',
              },
              {
                text: '利用日・IC区間・金額が公式記録と一致するか',
                hint: 'ETC利用照会サービスのPDF/CSVと照合',
              },
              {
                text: '季節・迂回・代替ルートの理由を記録したか',
                hint: '冬季通行止め・工事迂回など経路変更理由',
              },
              {
                text: '公式証明書の保管場所を記録したか',
                hint: 'PDF保存先・CSV格納フォルダ・JTRアーカイブ',
              },
              {
                text: '経理・部門担当者が記録にアクセスできるか',
                hint: '共有フォルダ・メール配信・権限設定を確認',
              },
            ],
          },
          en: {
            title: 'Hokuriku ETC Record Storage Checklist',
            intro: '5 items to verify for future reimbursement & audit',
            items: [
              {
                text: 'ETC card & vehicle identified?',
                hint: 'Label multi-card/vehicle linkage clearly',
              },
              {
                text: 'Date, IC, amount match official record?',
                hint: 'Cross-check with ETC Inquiry Service PDF/CSV',
              },
              {
                text: 'Seasonal/detour reason documented?',
                hint: 'Winter closure, construction detour, etc.',
              },
              {
                text: 'Official proof storage location noted?',
                hint: 'PDF path, CSV folder, JTR archive reference',
              },
              {
                text: 'Accounting/dept. staff can access record?',
                hint: 'Check shared folder, email delivery, permissions',
              },
            ],
          },
        },
      },
    ],
  },
];

function _nexcoRegionalEntry(r) {
  return {
    slug: r.slug,
    pathPrefix: 'ja',
    defaultLang: 'ja',
    lastUpdated: '2026-05-17',
    primaryKeyword: { ja: r.primaryKw, en: r.enPrimaryKw },
    keywords: r.keywords,
    ja: {
      title: `${r.name}のETC通行料と利用明細の確認方法【2026年版】｜${r.operatorLabel}運営の主要高速 | JTR`,
      metaDesc: `${r.operatorLabel}が運営する${r.name}（${r.region}）のETC料金体系、休日割引等の適用、利用明細PDF / CSV取得手順を解説。料金は車種・IC・距離・改定時期で変動。`,
      h1: `${r.name}のETC通行料と利用明細の確認方法【2026年版】`,
      answer: `${r.name}は${r.operatorLabel}が運営する高速道路で、${r.region}を結ぶ路線です。ETC装着車は休日割引・深夜割引・平日朝夕割引（マイレージ登録）等の対象となります。料金は車種・入口IC・出口IC・距離・改定時期で変動するため、最新の正確な料金は${r.operatorLabel}の公式サイトでご確認ください。利用明細はETC利用照会サービス（etc-meisai.jp）でPDF / CSV取得可能、JTRは毎朝のメール配信で自動アーカイブします。`,
      whyMatters: `${r.highlights}。${r.whyExtra}`,
      officialBackground: `${r.name}（最新は公式要確認）：\n• 運営：${r.operatorLabel}\n• 区間：${r.region}\n• 車種別の距離別料金\n• ETC装着車は休日割引・深夜割引等の対象\n• ETCマイレージサービス登録ドライバーは平日朝夕割引対象\n\n冬季・気象条件（該当路線）：\n• 積雪・凍結による速度規制・通行止めが発生する区間あり\n• チェーン規制・スタッドレスタイヤ着用規制の場合あり\n• 出発前に${r.operatorLabel}の道路情報サイトで規制情報の確認を推奨\n\n利用明細：他のNEXCO区間と同じくETC利用照会サービスでPDF / CSV出力可能。`,
      commonProblems: [
        { q: `${r.name}の料金が想定より高い`, a: '距離別料金のため、入口・出口ICで料金変動。公式の料金検索で経路ごとの正確な料金を事前確認推奨。' },
        { q: '冬季・気象による通行止めへの対応', a: '出発前に道路情報サイト + 気象情報の確認を推奨。冬季はタイヤ等の準備も。' },
        { q: '休日割引・深夜割引が想定通り効かない', a: '繁忙期除外日（GW・お盆・年末年始）・時間帯境界の可能性。利用明細CSVで実際の適用を確認。' },
        { q: 'レンタカーでも割引は適用されますか', a: 'ETC装着車であれば適用されます。レンタル会社のETCカード利用可否を事前確認推奨。' },
      ],
      jtrSolution: _jtrSolutionBlock(),
      whoFor: [`${r.name}沿線の通勤ドライバー`, `${r.name}を使う物流業`, '営業職（広域出張）', '観光ドライバー', '法人車両管理者'],
      steps: [
        { t: '事前に料金・所要時間を確認', d: `${r.operatorLabel}のドラぷら等で経路の料金・所要時間を確認。` },
        { t: '冬季・気象は道路情報を確認', d: '出発前に通行止め・速度規制・気象情報をチェック。' },
        { t: 'ETC装着で割引メリットを最大化', d: 'ETC装着車は休日・深夜・平日朝夕の各割引対象。マイレージサービス登録も検討。' },
        { t: 'ETC利用照会サービスで明細確認', d: '通行後、etc-meisai.jp でPDF / CSV取得。' },
        { t: 'JTR連携で自動メール配信', d: '毎朝のメールで前日の通行明細をPDF + CSV添付で受信。' },
        { t: '経費精算・観光経費の整理', d: '最終的な税務処理は税理士にご確認ください。' },
      ],
      pdfCsv: '当該路線の通行記録もETC利用照会サービスのPDF + CSVで取得可能。JTRが両方をメール同梱で配信します。',
      email: '毎朝のメール配信で当該路線の通行料を継続的に記録、月次・年次の経費整理に役立ちます。',
      examples: [
        { who: `${r.name}沿線の通勤ドライバー`, use: '通勤通行を月次でJTR集計、平日朝夕割引の還元状況確認。' },
        { who: '広域物流業', use: '車両別の通行を経費按分、運行原価可視化。' },
        { who: '営業職', use: '出張ごとの通行明細を毎朝確認、月末経費精算が効率化。' },
        { who: '観光ドライバー（繁忙期）', use: '休日割引除外日も踏まえて経費見通しを精度向上。' },
      ],
      relatedFeatures: RELATED_FEATURES_CORE,
      faqs: [
        { q: `${r.name}の料金体系はNEXCOの標準ですか?`, a: `${r.operatorLabel}の料金体系に従って算定されます。具体的な料金は公式の料金検索でご確認ください。` },
        { q: '休日割引はどのくらい安くなりますか?', a: '車種・区間により異なります。詳細は公式案内をご確認ください。' },
        { q: '深夜割引（0:00-4:00）はどう判定されますか?', a: '料金所通過時刻が判定基準。実際の適用は利用明細での確認が確実です。' },
        { q: '平日朝夕割引はどう登録しますか?', a: 'ETCマイレージサービス（smile-etc.jp）への登録が必要。詳細は「ETCマイレージサービスの登録手順」ガイドを参照。' },
        { q: 'レンタカーでもETC割引は使えますか?', a: 'ETC装着車であれば適用されます。レンタル会社にETCカード利用可否を事前確認推奨。' },
        { q: 'JTRは公式サービスですか?', a: 'いいえ、JTRは独立運営のサービスです。明細の中身に関する問合せは公式窓口へ。' },
      ],
      references: [r.operator, REF_ETC_MEISAI, REF_GOETC, REF_ETC_MILEAGE],
    },
    en: {
      title: `${r.enName} ETC Tolls and Statements Guide | JTR`,
      metaDesc: `Operated by ${r.operatorLabel.replace('NEXCO', 'NEXCO ')}, ${r.enName} ETC pricing, discounts, winter operations (where applicable), and how to get PDF/CSV statements.`,
      h1: `${r.enName} ETC Tolls & Statements`,
      answer: `${r.enName} is operated by ${r.operatorLabel.replace('NEXCO', 'NEXCO ')}, covering ${r.region}. ETC vehicles qualify for late-night, weekend, and weekday commuter (Mileage Service) discounts. Tolls vary by class/IC/distance/period. Get PDF/CSV via etc-meisai.jp; JTR delivers daily.`,
      whoFor: ['Corridor commuters', 'Logistics fleets', 'Wide-area business travelers'],
      steps: [
        { t: 'Check tolls + ETA', d: 'Dorapla or operator site.' },
        { t: 'Check winter/weather conditions', d: 'Where applicable.' },
        { t: 'Use ETC for discounts', d: 'Late-night / weekend / commuter.' },
        { t: 'Verify on the statement', d: 'PDF / CSV.' },
        { t: 'Daily JTR delivery', d: 'Inbox archive.' },
        { t: 'Use for reimbursement', d: 'Confirm tax handling with advisor.' },
      ],
      pdfCsv: 'Same format as other NEXCO sections.',
      email: 'Daily archive of corridor trips.',
      examples: [{ who: 'Corridor commuter', use: 'Monthly cost tracked.' }],
      faqs: [
        { q: 'Operator?', a: `${r.operatorLabel.replace('NEXCO', 'NEXCO ')}.` },
        { q: 'Discounts?', a: 'Standard NEXCO categories apply per rules.' },
      ],
      references: [r.operator, REF_ETC_MEISAI, REF_GOETC],
    },
  };
}

export const TIER_I_PAGES = [
  ..._REGIONS.map((r) => regionalEntry({
    slug: r.slug, primaryKeyword: r.primaryKeyword.ja, enPrimaryKeyword: r.primaryKeyword.en,
    keywords: r.keywords, ja: r.ja, enFallback: r.en, references: r.operatorRefs,
  })),
  // _NEXCO_ROUTES historically held simple route metadata that
  // _nexcoRegionalEntry would expand into full page objects via a
  // template (requires r.operatorLabel, r.name, r.region, ...).
  // After Phase C generation + patching, slugs that targeted this
  // file ended up as FULL page objects living directly inside the
  // _NEXCO_ROUTES array. We detect those by looking for an already-
  // populated `ja.title` and pass them through unchanged; anything
  // still in template-metadata shape goes through the legacy helper.
  ..._NEXCO_ROUTES.map((r) => (r && r.ja && r.ja.title) ? r : _nexcoRegionalEntry(r)),
];

export const TIER_I_SLUGS = TIER_I_PAGES.map((p) => p.slug);
